/*!
 * snice v7.10.0
 * A decorator-driven web component library with routing, controllers, daemons, and 130+ UI components. For better coding-agent results, run npx snice init-ai.
 * (c) 2024
 * Released under the MIT License.
 *
 * GENERATED FILE — DO NOT EDIT. Source: src/. Rebuild: npm run build:core
 */
// Global namespace for Snice framework
// Ensures all registries and symbols are shared across different JS files/modules
// Initialize the global Snice namespace on globalThis
// This ensures all module instances share the same registry
if (!globalThis.snice) {
    globalThis.snice = {
        controllerRegistry: new Map(),
        pendingControllerAttachments: new Map(),
        controllerIdCounter: 0
    };
}
// Multiple copies of Snice can share a global created by an older build.
globalThis.snice.pendingControllerAttachments ??= new Map();
// Export direct reference to globalThis.snice
const snice = globalThis.snice;
// Helper function to get or create a global symbol
// Symbol.for() already guarantees global uniqueness, no need for a Map cache
function getSymbol(name) {
    return Symbol.for(`snice:${name}`);
}

// Central file for all symbols used in the framework
// All symbols are stored globally to ensure consistency across modules
const IS_CONTROLLER_CLASS = getSymbol('is-controller-class');
const IS_CONTROLLER_INSTANCE = getSymbol('is-controller-instance');
const IS_DAEMON_CLASS = getSymbol('is-daemon-class');
const IS_DAEMON_INSTANCE = getSymbol('is-daemon-instance');
const IS_ELEMENT_CLASS = getSymbol('is-element-class');
const CHANNEL_HANDLERS = getSymbol('channel-handlers');
// Per-instance daemon communication state. Daemon instances are constructed by
// application code; Snice only creates and manages their private EventTarget
// when an app context explicitly provides them.
const DAEMON_STATE = getSymbol('daemon-state');
// Internal element state symbols
const READY_PROMISE = getSymbol('ready-promise');
const READY_RESOLVE = getSymbol('ready-resolve');
const READY_REJECT = getSymbol('ready-reject');
// True only while an element is executing its own @ready handlers. A
// controller attached to that same host must not await the promise whose
// settlement depends on the current handler returning.
const READY_HANDLERS_RUNNING = getSymbol('ready-handlers-running');
const RENDERED_PROMISE = getSymbol('rendered-promise');
const RENDERED_RESOLVE = getSymbol('rendered-resolve');
// Set when a render is requested but dropped because the element was
// disconnected; consumed on reconnect to replay exactly the missed render.
const PENDING_RECONNECT_RENDER = getSymbol('pending-reconnect-render');
// Per-element synchronous render-nesting depth, used to cap runaway re-entry
// without a shared global counter that one element could exhaust for others.
const RENDER_DEPTH = getSymbol('render-depth');
const RENDER_ROOT = getSymbol('render-root');
const ELEMENT_OPTIONS = getSymbol('element-options');
const CONTROLLER = getSymbol('controller');
const INITIALIZED = getSymbol('initialized');
// Controller symbols
const CONTROLLER_KEY = getSymbol('controller-key');
const CONTROLLER_NAME_KEY = getSymbol('controller-name');
const CONTROLLER_ID = getSymbol('controller-id');
const CONTROLLER_OPERATIONS = getSymbol('controller-operations');
const NATIVE_CONTROLLER = getSymbol('native-controller');
// Decorator name stored on the class so direct class bindings can expose the
// same useful DOM label without resolving the controller through the registry.
const CONTROLLER_REGISTRY_NAME = getSymbol('controller-registry-name');
// Set while a controller CLASS (not a registry name) is attached — the
// attribute/MutationObserver channel must not attach over or detach it.
const DIRECT_CONTROLLER = getSymbol('direct-controller');
// Guards framework-authored diagnostic controller attribute writes from the
// synchronous custom-element attributeChangedCallback path.
const CONTROLLER_ATTRIBUTE_SYNC = getSymbol('controller-attribute-sync');
// A controller class bound by a template before the custom element was
// defined/upgraded. Picked up by connectedCallback on first initialization.
const PENDING_CONTROLLER_BINDING = getSymbol('pending-controller-binding');
// Cleanup symbol - holds an object with all cleanup arrays
const CLEANUP = getSymbol('cleanup');
// Property symbols
const PROPERTIES = getSymbol('properties');
const PROPERTY_VALUES = getSymbol('property-values');
const PROPERTY_DEFAULTS = getSymbol('property-defaults');
const PROPERTY_WRAPPERS = getSymbol('property-wrappers');
const PRE_INIT_PROPERTY_VALUES = getSymbol('pre-init-property-values');
const PRE_UPGRADE_PROPERTY_BINDINGS = getSymbol('pre-upgrade-property-bindings');
const PROPERTIES_INITIALIZED = getSymbol('properties-initialized');
const PROPERTY_WATCHERS = getSymbol('property-watchers');
const EXPLICITLY_SET_PROPERTIES = getSymbol('explicitly-set-properties');
// Router context symbol
const ROUTER_CONTEXT = getSymbol('router-context');
const PAGE_TRANSITION = getSymbol('page-transition');
const CREATED_AT = getSymbol('created-at');
// Lifecycle symbols
const READY_HANDLERS = getSymbol('ready-handlers');
const DISPOSE_HANDLERS = getSymbol('dispose-handlers');
const RECONNECT_HANDLERS = getSymbol('reconnect-handlers');
const MOVED_HANDLERS = getSymbol('moved-handlers');
const ADOPTED_HANDLERS = getSymbol('adopted-handlers');
// Observer symbols
const OBSERVERS = getSymbol('observers');
// Property setter flag (prevents attributeChangedCallback re-triggering watchers)
const SETTING_FROM_PROPERTY = getSymbol('setting-from-property');
// Lifecycle callback timers
const MOVED_TIMERS = getSymbol('moved-timers');
const ADOPTED_TIMERS = getSymbol('adopted-timers');
// Dispatch timing symbols
const DISPATCH_TIMERS = getSymbol('dispatch-timers');
// Render symbols (v3.0.0)
const RENDER_METHOD = getSymbol('render-method');
const RENDER_OPTIONS = getSymbol('render-options');
const RENDER_INSTANCE = getSymbol('render-instance');
const RENDER_TIMERS = getSymbol('render-timers');
const RENDER_CALLBACKS = getSymbol('render-callbacks');
const STYLES_METHOD = getSymbol('styles-method');
const STYLES_APPLIED = getSymbol('styles-applied');
const PARENT_STYLES_METHODS = getSymbol('parent-styles-methods');
const SNICE_ELEMENT_BASE = getSymbol('snice-element-base');
// Navigation context symbols
const CONTEXT_HANDLER = getSymbol('context-handler');
const NAVIGATION_CONTEXT_INSTANCE = getSymbol('navigation-context-instance');
const REGISTERED_ELEMENTS = getSymbol('registered-elements');
const IS_UPDATING = getSymbol('is-updating');
const CONTEXT_REGISTER = getSymbol('context-register');
const CONTEXT_UNREGISTER = getSymbol('context-unregister');
const CONTEXT_NOTIFY_ELEMENT = getSymbol('context-notify-element');
const CONTEXT_TIMER = getSymbol('context-timer');
const CONTEXT_CALLED = getSymbol('context-called');
const CONTEXT_UPDATE = getSymbol('context-update');
// Registration de-dup sets on the constructor — replace previous `__*Methods`
// expando strings that violated the no-double-underscore rule.
const CONTEXT_METHODS = getSymbol('context-methods');
const WATCH_METHODS = getSymbol('watch-methods');
const OBSERVE_METHODS = getSymbol('observe-methods');
const ON_METHODS = getSymbol('on-methods');
const RESPOND_METHODS = getSymbol('respond-methods');
const READY_METHODS = getSymbol('ready-methods');
const DISPOSE_METHODS = getSymbol('dispose-methods');
const RECONNECT_METHODS = getSymbol('reconnect-methods');
const MOVED_METHODS = getSymbol('moved-methods');
const ADOPTED_METHODS = getSymbol('adopted-methods');
// Controller attach-abort — set to an AbortController while attachController
// is awaiting element.ready, so detachController can short-circuit the wait.
const CONTROLLER_ABORT = getSymbol('controller-abort');
// Set on a controller instance once it has gotten past element.ready and is
// being attached, so detachController knows not to run detach() on a controller
// whose attach() was aborted before it ever ran.
const CONTROLLER_ATTACHED = getSymbol('controller-attached');
const PROPERTY_DEFINERS = getSymbol('property-definers');

/**
 * SimpleArray type for arrays that can be safely reflected to attributes
 * Supports arrays of: string, number, boolean
 * Uses full-width comma (，) as separator to avoid conflicts
 * Strings cannot contain the full-width comma character
 */
class SimpleArray {
    static { this.SEPARATOR = '，'; } // U+FF0C Full-width comma
    /**
     * Serialize array to string for attribute storage
     */
    static serialize(arr) {
        if (!Array.isArray(arr))
            return '';
        return arr.map(item => {
            if (typeof item === 'string') {
                // Validate string doesn't contain our separator
                if (item.includes(SimpleArray.SEPARATOR)) {
                    throw new Error(`SimpleArray strings cannot contain the character "${SimpleArray.SEPARATOR}" (U+FF0C)`);
                }
                return item;
            }
            else if (typeof item === 'number' || typeof item === 'boolean') {
                return String(item);
            }
            else {
                throw new Error(`SimpleArray only supports string, number, and boolean types. Got: ${typeof item}`);
            }
        }).join(SimpleArray.SEPARATOR);
    }
    /**
     * Parse string from attribute back to array
     */
    static parse(str) {
        if (str === null || str === undefined)
            return [];
        // Empty string should not be parsed as containing an empty string
        // since empty arrays don't get reflected (handled by the reflection logic)
        if (str === '')
            return [];
        return str.split(SimpleArray.SEPARATOR).map(item => {
            // Try to parse as number
            if (/^-?\d+\.?\d*$/.test(item)) {
                const num = Number(item);
                if (!isNaN(num))
                    return num;
            }
            // Parse as boolean
            if (item === 'true')
                return true;
            if (item === 'false')
                return false;
            // Default to string
            return item;
        });
    }
}

/**
 * Generic transition system for animating between elements
 */
var TransitionMode;
(function (TransitionMode) {
    /** out transition completes before in transition starts */
    TransitionMode["SEQUENTIAL"] = "sequential";
    /** both transitions happen at the same time */
    TransitionMode["SIMULTANEOUS"] = "simultaneous";
})(TransitionMode || (TransitionMode = {}));

var _a, _b, _c;
// Symbol for storing the Set of elements
// Use Symbol.for() to ensure symbols are shared across multiple Snice instances
const REGISTERED_ELEMENTS_SET = Symbol.for('snice:registered-elements-set');
// Counter for generating unique context IDs
let contextIdCounter = 0;
/**
 * Represents the bundled router state that can notify registered elements of changes
 */
class Context {
    constructor(context = {}, placards = [], currentRoute = '', routeParams = {}, fetcher) {
        // Stores the SET of wrapped-method names per subscriber so that classes with
        // multiple @context() handlers register every method, not just the last one.
        this[_a] = new WeakMap();
        this[_b] = new Set();
        this[_c] = false;
        this.id = contextIdCounter++;
        this.application = context;
        this.navigation = {
            placards,
            route: currentRoute,
            params: routeParams
        };
        // Initialize fetch with middleware support or fallback to native fetch
        if (fetcher && typeof fetcher.create === 'function') {
            this.fetch = fetcher.create(this);
        }
        else if (typeof fetch === 'function') {
            this.fetch = fetch.bind(this);
        }
        else {
            throw new Error('No fetch implementation available');
        }
    }
    /**
     * Register an element or controller to receive context updates
     * @internal Used by @context decorator
     */
    [(_a = REGISTERED_ELEMENTS, _b = REGISTERED_ELEMENTS_SET, _c = IS_UPDATING, CONTEXT_REGISTER)](element, methodName) {
        const map = this[REGISTERED_ELEMENTS];
        let names = map.get(element);
        if (!names) {
            names = new Set();
            map.set(element, names);
        }
        names.add(methodName);
        this[REGISTERED_ELEMENTS_SET].add(element);
    }
    /**
     * Unregister a subscriber from context updates. With a methodName,
     * removes only that one handler (so a `once` handler tearing itself down
     * doesn't kill the element's other @context handlers); without one, removes
     * the subscriber entirely (used on disconnect/detach).
     * @internal Used by @context decorator cleanup
     */
    [CONTEXT_UNREGISTER](element, methodName) {
        const map = this[REGISTERED_ELEMENTS];
        if (methodName !== undefined) {
            const names = map.get(element);
            if (!names)
                return;
            names.delete(methodName);
            if (names.size > 0)
                return; // other handlers remain — keep the element
        }
        map.delete(element);
        this[REGISTERED_ELEMENTS_SET].delete(element);
    }
    /**
     * Update the context and notify all registered elements
     * Prevents infinite loops by tracking update state
     * @internal Used by Router during navigation
     */
    [CONTEXT_UPDATE](context, placards, currentRoute, routeParams) {
        this.application = context;
        this.navigation.placards = placards;
        this.navigation.route = currentRoute;
        this.navigation.params = routeParams;
        this.notify();
    }
    /**
     * Signal all @context() subscribers to re-read context
     * Does not modify any state — just notifies listeners with current values
     */
    update() {
        this.notify();
    }
    notify() {
        // Prevent infinite loops
        if (this[IS_UPDATING]) {
            return;
        }
        this[IS_UPDATING] = true;
        // Notify all registered subscribers by calling EVERY registered method
        // name on each. Classes with multiple @context() methods get all fired.
        const elementsSet = this[REGISTERED_ELEMENTS_SET];
        const elementsMap = this[REGISTERED_ELEMENTS];
        for (const element of elementsSet) {
            const names = elementsMap.get(element);
            if (!names)
                continue;
            for (const methodName of names) {
                if (typeof element[methodName] === 'function') {
                    try {
                        element[methodName](this);
                    }
                    catch (error) {
                        console.error(`Error calling @context method ${methodName}:`, error);
                    }
                }
            }
        }
        this[IS_UPDATING] = false;
    }
    /**
     * Notify a specific subscriber of the current context state
     * @internal Used by @context decorator
     */
    [CONTEXT_NOTIFY_ELEMENT](element) {
        const names = this[REGISTERED_ELEMENTS].get(element);
        if (!names)
            return;
        for (const methodName of names) {
            if (typeof element[methodName] === 'function') {
                try {
                    element[methodName](this);
                }
                catch (error) {
                    console.error(`Error calling @context method ${methodName}:`, error);
                }
            }
        }
    }
}

/**
 * Detects the type constructor from an initial value
 * @param initialValue - The default value assigned to a property field (e.g., `name = "default"` -> "default")
 * @returns The constructor function (String, Number, Boolean, etc.) or undefined if type can't be determined
 */
function detectType(initialValue) {
    if (initialValue === null || initialValue === undefined) {
        return undefined;
    }
    if (typeof initialValue === 'string') {
        return String;
    }
    if (typeof initialValue === 'number') {
        return Number;
    }
    if (typeof initialValue === 'boolean') {
        return Boolean;
    }
    if (initialValue instanceof Date) {
        return Date;
    }
    if (typeof initialValue === 'bigint') {
        return BigInt;
    }
    if (Array.isArray(initialValue)) {
        return Array;
    }
    return undefined;
}
/**
 * Parses an attribute value based on the property type
 * @param attributeValue - The raw string value from the HTML attribute (e.g., "123", "true", "hello")
 * @param propertyOptions - The options from @property decorator (contains explicit type, attribute name, etc.)
 * @param currentValue - The current stored value of the property (used for type inference when no explicit type)
 * @param initialValue - The default value assigned to the property field (used for type detection as fallback)
 * @returns The parsed value in the correct JavaScript type
 */
function parseAttributeValue(attributeValue, propertyOptions, currentValue, initialValue) {
    // Use explicit type or detect from initial value
    const typeToUse = propertyOptions.type || detectType(initialValue);
    if (propertyOptions.converter?.fromAttribute) {
        return propertyOptions.converter.fromAttribute(attributeValue, typeToUse);
    }
    switch (typeToUse) {
        case Boolean:
            // Boolean semantics per user specification:
            // - No attribute = false
            // - String "false" = false
            // - String "true" = true
            // - Empty string = true (attribute exists like <element bool-attr>)
            // - Any other value = true
            if (attributeValue === null)
                return false; // No attribute
            if (attributeValue === 'false')
                return false; // String "false"
            // All other cases (including empty string) = true (attribute exists)
            return true;
        case Number:
            return attributeValue !== null ? Number(attributeValue) : null;
        case String:
            return attributeValue;
        case Date:
            return attributeValue ? new Date(attributeValue) : null;
        case BigInt:
            if (attributeValue && attributeValue.endsWith('n')) {
                return BigInt(attributeValue.slice(0, -1));
            }
            else {
                return attributeValue ? BigInt(attributeValue) : null;
            }
        case SimpleArray:
            return SimpleArray.parse(attributeValue);
        case Array:
            try {
                return attributeValue ? JSON.parse(attributeValue) : [];
            }
            catch {
                return [];
            }
        case Object:
            try {
                return attributeValue ? JSON.parse(attributeValue) : {};
            }
            catch {
                return {};
            }
        default:
            // If no type specified and can't detect, try to infer from current value type
            if (typeof currentValue === 'number' && attributeValue !== null) {
                return Number(attributeValue);
            }
            else {
                return attributeValue;
            }
    }
}
/**
 * Converts a property value to its attribute string representation
 * @param value - The property value to convert
 * @param propertyOptions - The options from @property decorator
 * @param initialValue - The default value assigned to the property field (used for type detection)
 * @returns The string representation for the HTML attribute, or null if value should remove attribute
 */
/**
 * Default property dirty-check: has the value changed? Like `!==`, but treats
 * NaN → NaN as unchanged (NaN !== NaN would otherwise re-fire forever).
 */
function notEqual(value, old) {
    // Changed unless strictly equal, or both are NaN (each self-unequal).
    return value !== old && (value === value || old === old);
}
/**
 * Extract attribute name from property options
 */
function getAttrName(opts, propName) {
    // Lowercase always: the DOM stores attribute names lowercase, so an explicit
    // camelCase `attribute:` would otherwise never match observedAttributes and
    // attributeChangedCallback would never fire. get/setAttribute are already
    // case-insensitive, so this is safe for every caller.
    if (typeof opts.attribute === 'string')
        return opts.attribute.toLowerCase();
    if (opts.attribute === true || opts.attributeNaming === 'kebab') {
        return propName.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
    }
    return propName.toLowerCase();
}
/**
 * Escape HTML text-node content. Use for text that will be interpolated
 * into an HTML string (not an attribute value).
 */
function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
/**
 * Escape HTML attribute value. Use for anything interpolated inside a
 * quoted attribute (e.g. `href="${...}"`). Escapes both quote styles so
 * the value cannot close the attribute or the surrounding tag.
 */
function escapeAttr(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Validate a URL's scheme against a safe-list. Relative references are
 * accepted after parsing, while absolute and network-path references must
 * resolve to an allowed protocol. Raw ASCII control characters are rejected
 * before parsing so they cannot be stripped out to disguise a scheme.
 *
 * Rejects `javascript:`, `data:`, `vbscript:`, and any scheme not in the
 * allowed set by default. Pass to `window.open`, `iframe.src`, `<a href>`,
 * etc. when the URL originates from user input.
 *
 * @example
 *   if (!isSafeUrl(url)) return;
 *   window.open(url, '_blank');
 */
function isSafeUrl(url, opts = {}) {
    let raw;
    try {
        raw = String(url ?? '');
    }
    catch {
        return false;
    }
    if (!raw || /[\u0000-\u001f\u007f]/.test(raw))
        return false;
    const s = raw.trim();
    if (!s)
        return false;
    const allowed = opts.allowed ?? ['http:', 'https:', 'mailto:', 'tel:'];
    try {
        const parsed = new URL(s, 'http://__snice-relative.invalid/');
        const hasExplicitScheme = /^[a-z][a-z\d+.-]*:/i.test(s);
        const isNetworkPathReference = /^[\\/]{2}/.test(s);
        // Paths, hashes, and query-only references inherit the embedding page's
        // protocol and are safe independently of the absolute-protocol allowlist.
        if (!hasExplicitScheme && !isNetworkPathReference)
            return true;
        return allowed.some(protocol => typeof protocol === 'string'
            && protocol.toLowerCase() === parsed.protocol);
    }
    catch {
        return false;
    }
}
/**
 * Parse a duration string into a unit-aware Duration.
 * Recognized suffixes: `ms` (milliseconds), `s` (seconds), `m` (minutes),
 * `h` (hours). Plain numbers with no suffix are treated as milliseconds.
 * Unparseable input yields a zero-duration.
 *
 * @example
 *   parseDuration('200ms').milliseconds() // 200
 *   parseDuration('0.4s').milliseconds()  // 400
 *   parseDuration('0.4s').seconds()       // 0.4
 */
function parseDuration(value) {
    const v = String(value).trim();
    const num = parseFloat(v);
    let ms;
    if (isNaN(num)) {
        ms = 0;
    }
    else if (/ms$/i.test(v)) {
        ms = num;
    }
    else if (/s$/i.test(v)) {
        ms = num * 1000;
    }
    else if (/m$/i.test(v)) {
        ms = num * 60000;
    }
    else if (/h$/i.test(v)) {
        ms = num * 3600000;
    }
    else {
        ms = num;
    }
    return {
        milliseconds: () => ms,
        seconds: () => ms / 1000,
        minutes: () => ms / 60000,
    };
}
/**
 * Lazy-init a Set on a symbol key
 */
function ensureSet(obj, sym) {
    if (!obj[sym])
        obj[sym] = new Set();
    return obj[sym];
}
/**
 * Lazy-init an object on a symbol key
 */
function ensureObj(obj, sym) {
    if (!obj[sym])
        obj[sym] = {};
    return obj[sym];
}
/**
 * Invoke property watchers for a given property change
 */
function invokeWatchers(instance, constructor, propName, oldValue, newValue) {
    const watchers = constructor[PROPERTY_WATCHERS];
    if (!watchers)
        return;
    // Look up watchers by the JS property name AND the explicit `attribute:`
    // name (if declared). Lets `@watch('show-dropdown')` match a property
    // declared as `@property({ attribute: 'show-dropdown' }) showDropdown`.
    const keys = [propName];
    const propOpts = constructor[PROPERTIES]?.get(propName);
    const attrName = propOpts && typeof propOpts.attribute === 'string' ? propOpts.attribute : null;
    if (attrName && attrName !== propName)
        keys.push(attrName);
    for (const key of keys) {
        if (watchers.has(key)) {
            for (const w of watchers.get(key)) {
                try {
                    w.method.call(instance, oldValue, newValue, propName);
                }
                catch (e) {
                    console.error(`Error in @watch('${key}') method ${w.methodName}:`, e);
                }
            }
        }
    }
    if (watchers.has('*')) {
        for (const w of watchers.get('*')) {
            try {
                w.method.call(instance, oldValue, newValue, propName);
            }
            catch (e) {
                console.error(`Error in @watch('*') method ${w.methodName}:`, e);
            }
        }
    }
}
/**
 * Fire the one-time initialization call for `@watch(..., { immediate: true })`
 * handlers. Runs once per declared property after the element is initialized,
 * passing the effective initial value with `oldValue` undefined. Non-immediate
 * watchers are untouched — they only react to later changes.
 */
function invokeImmediateWatchers(instance, constructor) {
    const watchers = constructor[PROPERTY_WATCHERS];
    const properties = constructor[PROPERTIES];
    if (!watchers || !properties)
        return;
    for (const [propName, propOptions] of properties) {
        // A watcher may be registered under the JS name, the explicit `attribute:`
        // name, or the `*` wildcard; fire each immediate one exactly once, keyed to
        // this property's current value.
        const keys = [propName];
        const attrName = propOptions && typeof propOptions.attribute === 'string' ? propOptions.attribute : null;
        if (attrName && attrName !== propName)
            keys.push(attrName);
        keys.push('*');
        for (const key of keys) {
            if (!watchers.has(key))
                continue;
            for (const w of watchers.get(key)) {
                if (!w.immediate)
                    continue;
                try {
                    w.method.call(instance, undefined, instance[propName], propName);
                }
                catch (e) {
                    console.error(`Error in @watch('${key}') method ${w.methodName}:`, e);
                }
            }
        }
    }
}
/**
 * Create a debounced version of a function
 */
function createDebounced(fn, delay) {
    let timer = null;
    const debounced = function (...args) {
        if (timer !== null)
            clearTimeout(timer);
        timer = setTimeout(() => { fn.apply(this, args); timer = null; }, delay);
    };
    debounced.cancel = () => {
        if (timer !== null)
            clearTimeout(timer);
        timer = null;
    };
    return debounced;
}
/**
 * Create a throttled version of a function (leading + trailing edge)
 *
 * The trailing edge is a real `setTimeout`, so the returned function owns a
 * timer that outlives its caller unless somebody stops it. `cancel()` drops
 * that pending trailing call — teardown paths must call it, or the wrapped
 * method runs against an already-disposed instance. Mirrors `createDebounced`.
 */
function createThrottled(fn, delay) {
    let lastCall = 0;
    let timer = null;
    const throttled = function (...args) {
        const now = Date.now();
        const remaining = delay - (now - lastCall);
        if (remaining <= 0) {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            lastCall = now;
            return fn.apply(this, args);
        }
        if (!timer) {
            timer = setTimeout(() => { lastCall = Date.now(); timer = null; fn.apply(this, args); }, remaining);
        }
    };
    throttled.cancel = () => {
        if (timer !== null)
            clearTimeout(timer);
        timer = null;
    };
    return throttled;
}
/** Resolve and validate an @on/@dispatch debounce or throttle interval. */
function resolveEventTiming(instance, timing, decorator, option) {
    if (timing === undefined)
        return undefined;
    const value = typeof timing === 'function'
        ? timing.call(instance)
        : timing;
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new TypeError(`${decorator} ${option} must resolve to a finite, non-negative number of milliseconds.`);
    }
    return value;
}
/**
 * Resolve a scope option to a concrete EventTarget.
 *
 * - undefined → host element (default behavior)
 * - 'global' → `document`
 * - string → `host.closest(selector)` (nearest matching ancestor)
 * - EventTarget instance → that target directly
 * - resolver function → called with host as `this`, returns target or null
 *
 * Returns null when the scope cannot be resolved (e.g. selector matches no
 * ancestor, resolver returns null). Caller decides how to handle null —
 * typically warn in dev and skip listener attachment.
 */
function resolveScope(host, scope) {
    if (scope === undefined || scope === null)
        return host;
    if (scope === 'global')
        return typeof document !== 'undefined' ? document : null;
    if (typeof scope === 'function') {
        try {
            return scope.call(host) || null;
        }
        catch {
            return null;
        }
    }
    if (typeof scope === 'string') {
        return host.closest?.(scope) || null;
    }
    // Direct EventTarget instance.
    if (scope && typeof scope.addEventListener === 'function') {
        return scope;
    }
    return null;
}
function valueToAttribute(value, propertyOptions, initialValue) {
    // Handle null/undefined/false values and empty arrays
    if (value === null || value === undefined || value === false ||
        (propertyOptions?.type === SimpleArray && Array.isArray(value) && value.length === 0)) {
        return null;
    }
    // Use custom converter if provided
    if (propertyOptions.converter?.toAttribute) {
        return propertyOptions.converter.toAttribute(value, propertyOptions.type);
    }
    // Use explicit type or detect from initial value
    const typeToUse = propertyOptions.type || detectType(initialValue);
    switch (typeToUse) {
        case Boolean:
            // For boolean attributes, return "true" when true
            // (false is already handled above - returns null to remove attribute)
            return 'true';
        case Date:
            return value instanceof Date ? value.toISOString() : String(value);
        case BigInt:
            return typeof value === 'bigint' ? value.toString() + 'n' : String(value);
        case SimpleArray:
            return Array.isArray(value) ? SimpleArray.serialize(value) : String(value);
        case Array:
            return Array.isArray(value) ? JSON.stringify(value) : String(value);
        case Object:
            return typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);
        default:
            return String(value);
    }
}
/**
 * Warn (once per class) about @watch targets that match no @property —
 * such watchers can never fire, and the typo is otherwise silent.
 * Valid targets: property names, their attribute forms, and the '*' wildcard.
 */
const validatedWatchClasses = new WeakSet();
function validateWatchedProperties(instance, constructor) {
    if (validatedWatchClasses.has(constructor))
        return;
    validatedWatchClasses.add(constructor);
    const watchers = constructor[PROPERTY_WATCHERS];
    if (!watchers || watchers.size === 0)
        return;
    const properties = constructor[PROPERTIES];
    // invokeWatchers matches exactly: the property name, or an EXPLICIT
    // `attribute:` string. A derived/lowercased attribute name is NOT watchable,
    // so it must not be treated as valid here.
    const valid = new Set(['*']);
    if (properties) {
        for (const [name, opts] of properties) {
            valid.add(name);
            if (typeof opts?.attribute === 'string')
                valid.add(opts.attribute);
        }
    }
    for (const key of watchers.keys()) {
        if (valid.has(key))
            continue;
        const tag = instance.tagName ? instance.tagName.toLowerCase() : 'element';
        const known = properties ? [...properties.keys()].join(', ') : '(none)';
        console.warn(`@watch('${key}') on <${tag}> matches no @property — it will never fire. ` +
            `Known properties: ${known}.`);
    }
}

// Global cache for MediaQueryList objects
const mediaQueryCache = new Map();
/**
 * Decorator for observing external changes like viewport intersection, resize, media queries, and DOM mutations
 *
 * @param target - The observation target (e.g., 'intersection', 'resize', 'media:(min-width: 768px)', 'mutation:childList') or array of targets
 * @param selectorOrOptions - CSS selector for element to observe OR options object
 * @param options - Options object (when second parameter is a selector)
 */
function observe(observeTarget, selectorOrOptions, options) {
    // Handle overloaded parameters
    let selector;
    let opts;
    if (typeof selectorOrOptions === 'string') {
        selector = selectorOrOptions;
        opts = options;
    }
    else {
        selector = undefined;
        opts = selectorOrOptions;
    }
    return function (target, context) {
        const propertyKey = context.name;
        context.addInitializer(function () {
            const constructor = this.constructor;
            // hasOwnProperty guards so subclasses don't mutate parent state via
            // the prototype chain. Without this, instantiating a child pushes into
            // the parent's OBSERVERS array and fires child observers on fresh
            // parent instances.
            if (!Object.prototype.hasOwnProperty.call(constructor, OBSERVE_METHODS)) {
                constructor[OBSERVE_METHODS] = new Set();
            }
            if (constructor[OBSERVE_METHODS].has(target))
                return;
            constructor[OBSERVE_METHODS].add(target);
            if (!Object.prototype.hasOwnProperty.call(constructor, OBSERVERS)) {
                const inherited = constructor[OBSERVERS];
                constructor[OBSERVERS] = inherited ? [...inherited] : [];
            }
            const observeTargets = Array.isArray(observeTarget) ? observeTarget : [observeTarget];
            for (const targetString of observeTargets) {
                const [type, ...modifiers] = targetString.split(':');
                const targetStr = modifiers.join(':');
                constructor[OBSERVERS].push({
                    type,
                    target: targetStr,
                    selector,
                    methodName: propertyKey,
                    method: target,
                    options: opts
                });
            }
        });
    };
}
// Helper to setup observers for elements
function setupObservers(instance, element) {
    const observers = instance.constructor[OBSERVERS];
    if (!observers || !Array.isArray(observers) || observers.length === 0) {
        return;
    }
    // Initialize cleanup object if needed
    if (!instance[CLEANUP]) {
        instance[CLEANUP] = { events: [], channels: [], observers: [] };
    }
    else if (!instance[CLEANUP].observers) {
        instance[CLEANUP].observers = [];
    }
    for (const observer of observers) {
        const method = observer.method.bind(instance);
        // Apply throttling if specified. The throttle's trailing edge is a timer
        // that survives disconnect on its own, so its cancel joins the same
        // cleanup list the observers use — disconnecting the observer stops new
        // notifications, cancelling the throttle stops the one already queued.
        let callback = method;
        if (observer.options?.throttle) {
            const throttled = createThrottled(method, observer.options.throttle);
            instance[CLEANUP].observers.push(() => throttled.cancel());
            callback = throttled;
        }
        switch (observer.type) {
            case 'intersection':
                setupIntersectionObserver(instance, element, observer, callback);
                break;
            case 'resize':
                setupResizeObserver(instance, element, observer, callback);
                break;
            case 'media':
                setupMediaQueryObserver(instance, element, observer, callback);
                break;
            case 'mutation':
                setupMutationObserver(instance, element, observer, callback);
                break;
            default:
                console.warn(`Unknown observer type: ${observer.type}`);
        }
    }
}
function setupIntersectionObserver(instance, element, observer, callback) {
    const options = {
        threshold: observer.options?.threshold ?? 0,
        rootMargin: observer.options?.rootMargin ?? '0px',
        root: observer.options?.root ?? null
    };
    // Wrap callback to handle return value
    const wrappedCallback = (entries) => {
        for (const entry of entries) {
            try {
                const result = callback(entry);
                if (result === false && io) {
                    io.unobserve(entry.target);
                }
            }
            catch (error) {
                console.error(`Error in intersection observer ${observer.methodName}:`, error);
            }
        }
    };
    if (typeof IntersectionObserver === 'undefined') {
        console.warn('IntersectionObserver is not available in this environment');
        return;
    }
    const io = new IntersectionObserver(wrappedCallback, options);
    // Find target elements
    const targets = observer.selector
        ? Array.from(element.shadowRoot?.querySelectorAll(observer.selector) || [])
        : [element];
    // Start observing
    targets.forEach(target => io.observe(target));
    // Store cleanup
    instance[CLEANUP].observers.push(() => {
        targets.forEach(target => io.unobserve(target));
        // Only disconnect if no more targets
        if (io.takeRecords().length === 0) {
            io.disconnect();
        }
    });
}
function setupResizeObserver(instance, element, observer, callback) {
    const box = observer.options?.box || 'content-box';
    // Wrap callback with error handling
    const wrappedCallback = (entries) => {
        for (const entry of entries) {
            try {
                callback(entry);
            }
            catch (error) {
                console.error(`Error in resize observer ${observer.methodName}:`, error);
            }
        }
    };
    // Check if ResizeObserver is available
    if (typeof ResizeObserver === 'undefined') {
        console.warn('ResizeObserver is not available in this environment');
        return;
    }
    // Create ResizeObserver
    const ro = new ResizeObserver(wrappedCallback);
    // Find target elements
    const targets = observer.selector
        ? Array.from(element.shadowRoot?.querySelectorAll(observer.selector) || [])
        : [element];
    // Start observing with options
    targets.forEach(target => {
        ro.observe(target, { box });
    });
    // Store cleanup
    instance[CLEANUP].observers.push(() => {
        ro.disconnect();
    });
}
function setupMediaQueryObserver(instance, _element, observer, callback) {
    // Extract media query from target (e.g., "media:(min-width: 768px)" -> "(min-width: 768px)")
    const mediaQuery = observer.target;
    if (!mediaQuery) {
        console.warn('Media query observer requires a query string');
        return;
    }
    // Get or create MediaQueryList (cached globally)
    let mql = mediaQueryCache.get(mediaQuery);
    if (!mql) {
        try {
            mql = window.matchMedia(mediaQuery);
            mediaQueryCache.set(mediaQuery, mql);
        }
        catch (error) {
            console.error(`Invalid media query: ${mediaQuery}`, error);
            return;
        }
    }
    // Wrap callback with error handling
    const wrappedCallback = (event) => {
        try {
            callback('matches' in event ? event.matches : event.matches);
        }
        catch (error) {
            console.error(`Error in media query observer ${observer.methodName}:`, error);
        }
    };
    // Call immediately with current state
    wrappedCallback(mql);
    // Listen for changes
    const changeHandler = (e) => wrappedCallback(e);
    // Modern browsers use addEventListener
    if (mql.addEventListener) {
        mql.addEventListener('change', changeHandler);
        instance[CLEANUP].observers.push(() => {
            mql.removeEventListener('change', changeHandler);
        });
    }
    else {
        // Fallback for older browsers
        mql.addListener(changeHandler);
        instance[CLEANUP].observers.push(() => {
            mql.removeListener(changeHandler);
        });
    }
}
function setupMutationObserver(instance, element, observer, callback) {
    // Parse mutation type and attribute from target
    // e.g., "childList", "attributes:class", "attributes:data-state"
    const parts = observer.target.split(':');
    const mutationType = parts[0] || 'childList';
    const attributeName = parts[1];
    // Build MutationObserver options
    const options = {};
    switch (mutationType) {
        case 'childList':
            options.childList = true;
            break;
        case 'attributes':
            options.attributes = true;
            if (attributeName) {
                options.attributeFilter = [attributeName];
            }
            break;
        default: {
            // A leading . # or [ means the author put a CSS selector where the
            // mutation type goes — point at the correct two-argument form.
            if (/^[.#[]/.test(mutationType)) {
                console.warn(`@observe: "mutation:${mutationType}" — "${mutationType}" looks like a selector. ` +
                    `Selectors are the second argument — did you mean ` +
                    `@observe('mutation:childList', '${mutationType}')?`);
            }
            else {
                console.warn(`Unknown mutation type: ${mutationType} (expected childList or attributes)`);
            }
            return;
        }
    }
    // Apply subtree with safety limits
    if (observer.options?.subtree) {
        options.subtree = true;
        // Could implement maxDepth checking in the callback
        if (observer.options.maxDepth) {
            console.warn('maxDepth is set but requires custom implementation');
        }
    }
    // Wrap callback with error handling and optional depth filtering
    const needsDepthFilter = !!(observer.options?.maxDepth && observer.options.subtree);
    const wrappedCallback = (mutations) => {
        try {
            if (!needsDepthFilter) {
                callback(mutations);
                return;
            }
            const maxDepth = observer.options.maxDepth;
            const root = element.shadowRoot || element;
            const filtered = mutations.filter(mutation => {
                let depth = 0;
                let current = mutation.target;
                while (current && current !== root && depth < maxDepth) {
                    current = current.parentNode;
                    depth++;
                }
                return depth < maxDepth;
            });
            if (filtered.length > 0)
                callback(filtered);
        }
        catch (error) {
            console.error(`Error in mutation observer ${observer.methodName}:`, error);
        }
    };
    // Create MutationObserver
    const mo = new MutationObserver(wrappedCallback);
    // Find target elements
    const isController = instance[IS_CONTROLLER_INSTANCE] === true;
    // For mutation observers without selector:
    // - Controllers: observe the shadow DOM of the element they're attached to (if it exists)
    // - Elements: observe the element itself (light DOM) to avoid infinite loops
    const targets = observer.selector
        ? Array.from(element.shadowRoot?.querySelectorAll(observer.selector) || [])
        : isController && element.shadowRoot
            ? [element.shadowRoot]
            : [element];
    // Start observing
    targets.forEach(target => {
        mo.observe(target, options);
    });
    // Store cleanup
    instance[CLEANUP].observers.push(() => {
        mo.disconnect();
    });
}
// Helper to cleanup observers
function cleanupObservers(instance) {
    if (instance[CLEANUP]?.observers) {
        for (const cleanup of instance[CLEANUP].observers) {
            cleanup();
        }
        instance[CLEANUP].observers = [];
    }
}

const CONTEXT_REQUEST_EVENT = '@context/request';
const providers = new WeakMap();
/** @internal */
function hasContextProvider(root) {
    return providers.has(root);
}
function contextTarget(source) {
    if (!source || (typeof source !== 'object' && typeof source !== 'function')) {
        return null;
    }
    const value = source;
    if (value[IS_CONTROLLER_INSTANCE] === true) {
        return value.element && typeof value.element.dispatchEvent === 'function'
            ? value.element
            : null;
    }
    return typeof value.dispatchEvent === 'function' ? value : null;
}
/**
 * Installs the low-level application-context provider on a concrete event
 * boundary. Public callers use provideContext(), which also activates the
 * daemon instances declared by the context.
 */
function installContextProvider(root, context, fetch, navigation) {
    if (!root || typeof root.addEventListener !== 'function' || typeof root.removeEventListener !== 'function') {
        throw new TypeError('provideContext() requires an EventTarget root.');
    }
    if (!context || typeof context !== 'object') {
        throw new TypeError('provideContext() requires an application context object.');
    }
    if (providers.has(root)) {
        throw new Error('This root already provides an application context. Release it before providing another.');
    }
    const listener = (event) => {
        const request = event;
        if (!request.detail)
            return;
        if (request.detail.kind === 'application') {
            if (request.detail.context !== undefined)
                return;
            request.detail.context = context;
        }
        else if (request.detail.kind === 'fetch') {
            if (!fetch || request.detail.fetch !== undefined)
                return;
            request.detail.fetch = fetch;
        }
        else if (request.detail.kind === 'navigation') {
            if (!navigation || request.detail.navigation !== undefined)
                return;
            request.detail.navigation = navigation;
        }
        else {
            return;
        }
        request.preventDefault();
        request.stopPropagation();
    };
    root.addEventListener(CONTEXT_REQUEST_EVENT, listener);
    providers.set(root, { listener });
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        const current = providers.get(root);
        if (current?.listener === listener) {
            root.removeEventListener(CONTEXT_REQUEST_EVENT, listener);
            providers.delete(root);
        }
    };
}
/**
 * Resolves the raw application context visible to an element or controller.
 * A single composed, bubbling request is answered by the nearest
 * provideContext() root. Router-injected context remains a fallback for a
 * detached page or controller that cannot currently reach its provider.
 */
function getContext(source) {
    if (!source || (typeof source !== 'object' && typeof source !== 'function')) {
        return undefined;
    }
    const target = contextTarget(source);
    if (target) {
        const detail = { kind: 'application' };
        target.dispatchEvent(new CustomEvent(CONTEXT_REQUEST_EVENT, {
            bubbles: true,
            composed: true,
            cancelable: true,
            detail,
        }));
        if (detail.context !== undefined)
            return detail.context;
    }
    const value = source;
    if (value[ROUTER_CONTEXT] !== undefined) {
        return value[ROUTER_CONTEXT];
    }
    if (value[IS_CONTROLLER_INSTANCE] === true && value.element?.[ROUTER_CONTEXT] !== undefined) {
        return value.element[ROUTER_CONTEXT];
    }
    return undefined;
}
/**
 * Resolve the fetch function supplied by the nearest context provider.
 * Router providers expose their ContextAwareFetcher-bound function here;
 * explicit providers may supply the same dependency for non-router apps and
 * tests without adding a reserved property to AppContext.
 */
function getContextFetch(source) {
    const target = contextTarget(source);
    if (!target)
        return undefined;
    const detail = { kind: 'fetch' };
    target.dispatchEvent(new CustomEvent(CONTEXT_REQUEST_EVENT, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail,
    }));
    return detail.fetch;
}
/** @internal Resolve the router Context visible to an element or controller. */
function getNavigationContext(source) {
    const target = contextTarget(source);
    if (!target)
        return undefined;
    const detail = { kind: 'navigation' };
    target.dispatchEvent(new CustomEvent(CONTEXT_REQUEST_EVENT, {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail,
    }));
    return detail.navigation;
}

function isDaemonInstance(value) {
    return !!value
        && (typeof value === 'object' || typeof value === 'function')
        && value[IS_DAEMON_CLASS] === true;
}
function daemonState(instance) {
    if (!isDaemonInstance(instance)) {
        throw new TypeError('Application contexts may provide only instances of @daemon classes.');
    }
    let state = instance[DAEMON_STATE];
    if (!state) {
        state = {
            target: new EventTarget(),
            bindings: 0,
            active: false,
        };
        try {
            Object.defineProperty(instance, DAEMON_STATE, {
                value: state,
                configurable: true,
            });
            Object.defineProperty(instance, IS_DAEMON_INSTANCE, {
                value: true,
                configurable: true,
            });
        }
        catch {
            throw new TypeError('@daemon instances must remain extensible until they are provided to an app context.');
        }
    }
    return state;
}
function getDaemonTarget(instance) {
    return daemonState(instance).target;
}
function sourceLabel(source) {
    const value = source;
    if (value?.[IS_CONTROLLER_INSTANCE] === true)
        return 'controller';
    if (isDaemonInstance(value))
        return '@daemon instance';
    return 'element';
}
function requireDaemonTarget(source, name) {
    if (typeof name !== 'string' || !name.trim()) {
        throw new TypeError('Daemon addresses must be non-empty strings.');
    }
    const context = getContext(source);
    if (!context) {
        throw new Error(`[snice] ${sourceLabel(source)} cannot resolve daemon "${name}" because no app context is provided.`);
    }
    const instance = context.daemons?.[name];
    if (!instance) {
        throw new Error(`[snice] app context does not provide daemon "${name}".`);
    }
    if (!isDaemonInstance(instance)) {
        throw new TypeError(`[snice] app context entry "${name}" is not an instance of an @daemon class.`);
    }
    const state = daemonState(instance);
    if (!state.active) {
        throw new Error(`[snice] daemon "${name}" is not active because its app context has not been provided.`);
    }
    return state.target;
}
function defaultCommunicationTarget(instance) {
    if (isDaemonInstance(instance)) {
        return getDaemonTarget(instance);
    }
    if (instance?.[IS_CONTROLLER_INSTANCE] === true) {
        return instance.element && typeof instance.element.dispatchEvent === 'function'
            ? instance.element
            : null;
    }
    if (instance && typeof instance.dispatchEvent === 'function') {
        return instance;
    }
    return null;
}

// @request decorator transforms methods to return Promise<T>
/**
 * Decorator for making requests from elements or controllers.
 * Uses async generator pattern for bidirectional communication.
 *
 * @param requestName The name of the request
 * @param options Optional configuration
 */
function request(requestName, options) {
    return function (originalMethod, _context) {
        // Create timing variables for debounce/throttle
        let debounceTimeout;
        let throttleLastCall = 0;
        let throttleTimeout;
        const throttleTrailingResolvers = [];
        return async function (...args) {
            const actualRequest = async () => {
                // @request always acts as requester (client side)
                const responseTimeout = options?.timeout ?? 120000; // Default 2 minute timeout
                const discoveryTimeout = options?.discoveryTimeout ?? 50; // Default 50ms discovery timeout
                // Resolve before creating timeout promises. A missing daemon/context
                // should reject immediately without leaving an orphaned discovery
                // timer behind.
                const dispatcher = options?.daemon !== undefined
                    ? requireDaemonTarget(this, options.daemon)
                    : defaultCommunicationTarget(this);
                if (!dispatcher) {
                    throw new TypeError(`@request('${requestName}') requires an element, attached controller, or provided @daemon instance.`);
                }
                // Create the generator
                const generator = originalMethod.apply(this, args);
                // Get the first yield (the request payload)
                const { value: payload, done } = await generator.next();
                if (done) {
                    // Generator returned without yielding
                    return payload;
                }
                // Create data promise and expose resolve/reject
                let dataResolve;
                let dataReject;
                const dataPromise = new Promise((resolve, reject) => {
                    dataResolve = resolve;
                    dataReject = reject;
                });
                // Create discovery timeout promise and expose resolve/reject
                let discoveryResolve;
                let discoveryReject;
                let discoveryTimeoutId;
                let discovered = false;
                const discoveryPromise = new Promise((resolve, reject) => {
                    discoveryResolve = resolve;
                    discoveryReject = reject;
                    discoveryTimeoutId = setTimeout(() => {
                        reject(new Error(`Request "${requestName}" found no @respond('${requestName}') handler within ${discoveryTimeout}ms — ` +
                            `attach a responding controller/element, or pass { optional: true } to resolve undefined when unhandled.`));
                    }, discoveryTimeout);
                });
                // Dispatch event with promises
                const eventName = `@request/${requestName}`;
                const event = new CustomEvent(eventName, {
                    bubbles: options?.bubbles !== undefined ? options.bubbles : true,
                    cancelable: options?.cancelable || false,
                    composed: true, // Allow crossing shadow DOM boundaries
                    detail: {
                        payload,
                        discovery: {
                            resolve: () => {
                                discovered = true;
                                clearTimeout(discoveryTimeoutId);
                                discoveryResolve();
                            },
                            reject: discoveryReject
                        },
                        data: {
                            resolve: dataResolve,
                            reject: dataReject
                        }
                    }
                });
                dispatcher.dispatchEvent(event);
                try {
                    // Wait for discovery timeout to be cleared (handler found) or discovery timeout to reject (no handler)
                    await discoveryPromise;
                    // If we get here, a handler was found and discovery timeout was cleared
                    // Now wait for the actual data response with the full response timeout
                    const responseTimeoutId = setTimeout(() => {
                        dataReject(new Error(`Request "${requestName}" timed out after ${responseTimeout}ms`));
                    }, responseTimeout);
                    const response = await dataPromise;
                    clearTimeout(responseTimeoutId);
                    // Send response back to generator and get final return value
                    const { value: finalValue } = await generator.next(response);
                    return finalValue;
                }
                catch (error) {
                    // optional: an unhandled request isn't an error — resume the generator
                    // with undefined so `const data = await (yield payload)` sees no data.
                    // A response TIMEOUT (handler exists but is slow) still throws.
                    if (options?.optional && !discovered) {
                        const { value: finalValue } = await generator.next(undefined);
                        return finalValue;
                    }
                    // Drive the generator's own catch block and return whatever it recovers
                    // with (e.g. a cached fallback). If the generator re-throws instead,
                    // generator.throw rejects and the error propagates to the caller.
                    const { value: recovered } = await generator.throw(error);
                    return recovered;
                }
            }; // Close actualRequest function
            // Debounce
            if (options?.debounce) {
                return new Promise((resolve, reject) => {
                    clearTimeout(debounceTimeout);
                    debounceTimeout = setTimeout(async () => {
                        try {
                            resolve(await actualRequest());
                        }
                        catch (error) {
                            reject(error);
                        }
                    }, options.debounce);
                });
            }
            // No throttle — execute immediately
            if (!options?.throttle)
                return actualRequest();
            // Throttle: time elapsed — invoke now
            const now = Date.now();
            const remaining = options.throttle - (now - throttleLastCall);
            if (remaining <= 0) {
                // Cancelling the scheduled trailing call must not orphan the promises
                // queued for it — settle them with this fresh result. Also reset
                // throttleTimeout so later within-window calls don't attach to a dead
                // timer and hang.
                clearTimeout(throttleTimeout);
                throttleTimeout = null;
                throttleLastCall = now;
                const orphaned = throttleTrailingResolvers.splice(0);
                const p = actualRequest();
                if (orphaned.length) {
                    p.then((r) => { for (const x of orphaned)
                        x.resolve(r); }, (e) => { for (const x of orphaned)
                        x.reject(e); });
                }
                return p;
            }
            // Throttle: already has pending trailing call — attach to it
            if (throttleTimeout) {
                return new Promise((resolve, reject) => {
                    throttleTrailingResolvers.push({ resolve, reject });
                });
            }
            // Throttle: schedule trailing call
            return new Promise((resolve, reject) => {
                throttleTrailingResolvers.push({ resolve, reject });
                throttleTimeout = setTimeout(async () => {
                    throttleLastCall = Date.now();
                    throttleTimeout = null;
                    const resolvers = throttleTrailingResolvers.splice(0);
                    try {
                        const result = await actualRequest();
                        for (const r of resolvers)
                            r.resolve(result);
                    }
                    catch (error) {
                        for (const r of resolvers)
                            r.reject(error);
                    }
                }, remaining);
            });
        };
    };
}
/**
 * Decorator for responding to requests in elements or controllers.
 *
 * @param requestName The name of the request to respond to
 * @param options Optional configuration
 */
function respond(requestName, options) {
    return function (target, context) {
        const propertyKey = context.name;
        context.addInitializer(function () {
            const constructor = this.constructor;
            // hasOwnProperty guards so subclasses don't mutate parent state via
            // the prototype chain.
            if (!Object.prototype.hasOwnProperty.call(constructor, RESPOND_METHODS)) {
                constructor[RESPOND_METHODS] = new Set();
            }
            if (constructor[RESPOND_METHODS].has(target))
                return;
            constructor[RESPOND_METHODS].add(target);
            if (!Object.prototype.hasOwnProperty.call(constructor, CHANNEL_HANDLERS)) {
                const inherited = constructor[CHANNEL_HANDLERS];
                constructor[CHANNEL_HANDLERS] = inherited ? [...inherited] : [];
            }
            constructor[CHANNEL_HANDLERS].push({
                channelName: requestName,
                methodName: propertyKey,
                method: target,
                options: options
            });
        });
    };
}
// Which handler descriptors an instance has already registered, so a second
// setup pass only picks up what the first one deferred.
const registeredHandlers = new WeakMap();
/**
 * Helper to setup response handlers for elements and controllers.
 *
 * Safe to call more than once for the same instance: a handler already
 * registered is skipped rather than double-bound.
 */
function setupResponseHandlers(instance, element, options) {
    const handlers = instance.constructor[CHANNEL_HANDLERS];
    if (!handlers)
        return;
    const claim = options?.claim;
    let registered = registeredHandlers.get(instance);
    if (!registered) {
        registered = new Set();
        registeredHandlers.set(instance, registered);
    }
    // Store cleanup functions
    // Initialize cleanup object if needed
    if (!instance[CLEANUP]) {
        instance[CLEANUP] = { events: [], channels: [] };
    }
    for (const handler of handlers) {
        if (registered.has(handler))
            continue;
        const boundMethod = handler.method.bind(instance);
        const eventName = `@request/${handler.channelName}`;
        // Create timing variables for debounce/throttle per handler
        let debounceTimeout;
        let throttleLastCall = 0;
        let throttleTimeout;
        const throttleTrailingResolvers = [];
        let throttleLatestArgs = null;
        // Create wrapped method with timing if needed
        const createTimedMethod = (originalMethod) => {
            if (handler.options?.debounce) {
                return (...args) => new Promise((resolve, reject) => {
                    clearTimeout(debounceTimeout);
                    debounceTimeout = setTimeout(async () => {
                        try {
                            resolve(await originalMethod(...args));
                        }
                        catch (error) {
                            reject(error);
                        }
                    }, handler.options.debounce);
                });
            }
            if (!handler.options?.throttle)
                return originalMethod;
            return (...args) => {
                const now = Date.now();
                const remaining = handler.options.throttle - (now - throttleLastCall);
                if (remaining <= 0) {
                    // Same as the @request side: settle any queued trailing callers with
                    // this fresh result rather than orphaning their promises.
                    clearTimeout(throttleTimeout);
                    throttleTimeout = null;
                    throttleLastCall = now;
                    const orphaned = throttleTrailingResolvers.splice(0);
                    throttleLatestArgs = null;
                    const result = originalMethod(...args);
                    if (orphaned.length) {
                        Promise.resolve(result).then((r) => { for (const x of orphaned)
                            x.resolve(r); }, (e) => { for (const x of orphaned)
                            x.reject(e); });
                    }
                    return result;
                }
                // Remember the LATEST args so the trailing call uses fresh input,
                // and queue every suppressed caller's resolver so they all receive
                // the trailing result instead of `undefined`.
                throttleLatestArgs = args;
                return new Promise((resolve, reject) => {
                    throttleTrailingResolvers.push({ resolve, reject });
                    if (throttleTimeout)
                        return; // already scheduled
                    throttleTimeout = setTimeout(async () => {
                        throttleLastCall = Date.now();
                        throttleTimeout = null;
                        const resolvers = throttleTrailingResolvers.splice(0);
                        const finalArgs = throttleLatestArgs ?? args;
                        throttleLatestArgs = null;
                        try {
                            const result = await originalMethod(...finalArgs);
                            for (const r of resolvers)
                                r.resolve(result);
                        }
                        catch (error) {
                            for (const r of resolvers)
                                r.reject(error);
                        }
                    }, remaining);
                });
            };
        };
        const timedMethod = createTimedMethod(boundMethod);
        // Setup response handler
        const responseHandler = (event) => {
            // Extract promises and payload
            const { data, discovery, payload } = event.detail;
            // Prevent other responders from responding
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
            // Clear the discovery timeout immediately - we found a handler
            discovery.resolve();
            // Call the timed responder method and handle the result
            const answer = claim
                ? Promise.resolve(claim()).then(() => timedMethod(payload))
                : Promise.resolve(timedMethod(payload));
            answer
                .then(result => {
                data.resolve(result);
            })
                .catch(error => {
                data.reject(error);
                console.error(`Error in response handler ${handler.methodName}:`, error);
            });
        };
        let responseTarget = element;
        if (handler.options?.daemon !== undefined) {
            try {
                responseTarget = requireDaemonTarget(instance, handler.options.daemon);
            }
            catch (error) {
                // The app context reaches a controller through its host element, so it
                // can be unreachable while the host is still disconnected. Leave the
                // handler for the later pass instead of dropping it permanently.
                if (options?.deferUnresolvableDaemons)
                    continue;
                console.warn(`[snice/@respond] ${error.message} Responder "${handler.channelName}" skipped.`);
                registered.add(handler);
                continue;
            }
        }
        registered.add(handler);
        responseTarget.addEventListener(eventName, responseHandler);
        instance[CLEANUP].channels.push(() => {
            responseTarget.removeEventListener(eventName, responseHandler);
        });
    }
}
// Helper to cleanup response handlers
function cleanupResponseHandlers(instance) {
    registeredHandlers.delete(instance);
    if (instance[CLEANUP]?.channels) {
        for (const cleanup of instance[CLEANUP].channels) {
            cleanup();
        }
        instance[CLEANUP].channels = [];
    }
}

// once/preventDefault/stopPropagation/capture/passive are @on() OPTIONS, not
// template modifiers. A dotted non-keyboard event whose suffix is one of these
// is almost certainly a mistake (e.g. `@click.once`) — it binds a listener for
// an event type the DOM never dispatches. Warn instead of failing silently. A
// legitimate custom event name like `app.ready` won't match and stays quiet.
const MODIFIER_WORDS = new Set([
    'once', 'prevent', 'preventdefault', 'stop', 'stoppropagation', 'capture', 'passive',
]);
function warnIfModifierMisuse(eventName) {
    const dot = eventName.lastIndexOf('.');
    if (dot < 0)
        return;
    const suffix = eventName.slice(dot + 1).toLowerCase();
    if (!MODIFIER_WORDS.has(suffix))
        return;
    console.warn(`snice: "@${eventName}" is not a valid modifier — once/preventDefault/` +
        `stopPropagation/capture are @on() options, not template modifiers. ` +
        `This registers a listener for event "${eventName}", which never fires.`);
}
/**
 * Parse keyboard shortcut specification
 * Examples:
 *   "enter" -> { key: "Enter" }
 *   "ctrl+s" -> { key: "s", ctrl: true }
 *   "ctrl+shift+s" -> { key: "s", ctrl: true, shift: true }
 *   "~enter" -> { key: "Enter", anyModifiers: true }
 */
function parseKeyboardFilter(spec) {
    // Handle ~ prefix for matching regardless of modifiers
    const anyModifiers = spec.startsWith('~');
    if (anyModifiers) {
        spec = spec.substring(1);
    }
    const parts = spec.split('+');
    const filter = {
        key: '',
        anyModifiers
    };
    for (const part of parts) {
        const lower = part.toLowerCase();
        if (lower === 'ctrl' || lower === 'control') {
            filter.ctrl = true;
        }
        else if (lower === 'alt') {
            filter.alt = true;
        }
        else if (lower === 'shift') {
            filter.shift = true;
        }
        else if (lower === 'meta' || lower === 'cmd' || lower === 'command') {
            filter.meta = true;
        }
        else {
            // This is the key itself - normalize common keys
            filter.key = normalizeKey(part);
        }
    }
    return filter;
}
/**
 * Normalize key names to match KeyboardEvent.key
 */
function normalizeKey(key) {
    const keyMap = {
        'esc': 'Escape',
        'escape': 'Escape',
        'enter': 'Enter',
        'return': 'Enter',
        'space': ' ',
        'spacebar': ' ',
        'up': 'ArrowUp',
        'down': 'ArrowDown',
        'left': 'ArrowLeft',
        'right': 'ArrowRight',
        'arrowup': 'ArrowUp',
        'arrowdown': 'ArrowDown',
        'arrowleft': 'ArrowLeft',
        'arrowright': 'ArrowRight',
        'delete': 'Delete',
        'del': 'Delete',
        'backspace': 'Backspace',
        'tab': 'Tab',
        'home': 'Home',
        'end': 'End',
        'pageup': 'PageUp',
        'pagedown': 'PageDown'
    };
    const lower = key.toLowerCase();
    return keyMap[lower] || key;
}
/**
 * Check if keyboard event matches the filter
 */
function matchesKeyboardFilter(event, filter) {
    // Check key match
    if (event.key !== filter.key) {
        return false;
    }
    // If anyModifiers is true, we don't care about modifiers
    if (filter.anyModifiers) {
        return true;
    }
    // Check modifiers - by default, exact match
    // If filter specifies ctrl: true, event must have ctrlKey
    // If filter doesn't specify ctrl, event must NOT have ctrlKey
    const ctrlMatch = filter.ctrl ? event.ctrlKey : !event.ctrlKey;
    const altMatch = filter.alt ? event.altKey : !event.altKey;
    const shiftMatch = filter.shift ? event.shiftKey : !event.shiftKey;
    const metaMatch = filter.meta ? event.metaKey : !event.metaKey;
    return ctrlMatch && altMatch && shiftMatch && metaMatch;
}

/**
 * @on decorator for listening to events
 * Use in elements or controllers to listen to DOM events or custom events
 */
const ON_HANDLERS = getSymbol('on-handlers');
// Per-decoration-site counter — see the registration identity note in `on()`.
let registrationCount = 0;
/**
 * @on decorator for listening to events
 *
 * Works in both elements and controllers with full event delegation support.
 *
 * @param eventName - Event name(s) to listen for
 * @param selector - Optional CSS selector for event delegation
 * @param options - Event listener options including debounce/throttle
 *
 * @example
 * ```typescript
 * // In elements
 * @element('my-button')
 * class MyButton extends HTMLElement {
 *   @on('click', 'button')
 *   handleClick(e: MouseEvent) {
 *     console.log('Button clicked!', e);
 *   }
 *
 *   @on('input', 'input', { debounce: 300 })
 *   handleInput(e: Event) {
 *     console.log('Input changed:', (e.target as HTMLInputElement).value);
 *   }
 * }
 *
 * // In controllers
 * @controller('my-controller')
 * class MyController {
 *   element!: HTMLElement;
 *
 *   @on('count-changed')
 *   handleCountChanged(e: CustomEvent) {
 *     console.log('Count changed to:', e.detail.count);
 *   }
 *
 *   @on('click', '.item', { throttle: 100 })
 *   handleItemClick(e: MouseEvent) {
 *     console.log('Item clicked');
 *   }
 * }
 * ```
 */
function on(eventName, selectorOrOptions, options) {
    // Parse arguments to support multiple call signatures
    let selector = null;
    let opts = {};
    if (typeof selectorOrOptions === 'string') {
        selector = selectorOrOptions;
        opts = options || {};
    }
    else if (selectorOrOptions && typeof selectorOrOptions === 'object') {
        opts = selectorOrOptions;
    }
    else if (selectorOrOptions === null && options) {
        opts = options;
    }
    // `target` is the options-object spelling of the positional selector; fold it
    // into the same delegation path. The positional argument wins when both are given.
    if (!selector && opts.target) {
        selector = opts.target;
    }
    const eventNames = Array.isArray(eventName) ? eventName : [eventName];
    // Registration identity — derived from EVERY decorator argument, so no
    // option can be forgotten here and silently collapse two distinct stacked
    // @on decorators into one (that is how { debounce: 300 } stacked on a plain
    // @on for the same event used to lose one of its two registrations).
    // Options are serialized generically (sorted, so literal key order doesn't
    // matter); values that can't be compared by value — an EventTarget or a
    // resolver function passed as `scope` — fall back to this decoration site's
    // own id, which is stable across instances but unique per @on application.
    const siteId = `site:${++registrationCount}`;
    let optionSignature = '';
    let hasOpaqueOption = false;
    for (const key of Object.keys(opts).sort()) {
        const value = opts[key];
        if (value === undefined)
            continue;
        if (value !== null && (typeof value === 'object' || typeof value === 'function')) {
            hasOpaqueOption = true;
            continue;
        }
        optionSignature += `${key}=${String(value)};`;
    }
    const argumentKey = `${eventNames.join(',')}::${selector ?? ''}::${optionSignature}${hasOpaqueOption ? `::${siteId}` : ''}`;
    return function (originalMethod, context) {
        const methodName = context.name;
        // Same decoration, same method name → same registration. A subclass that
        // re-declares an identically decorated method must NOT add a second
        // listener on top of the one its parent's initializer already registered.
        const registrationKey = `${methodName}::${argumentKey}`;
        context.addInitializer(function () {
            const constructor = this.constructor;
            // Dedup by registration identity, NOT by method reference — stacked @on
            // decorators share one method and must each register. Use hasOwnProperty
            // so subclasses get their OWN Set instead of inheriting — otherwise
            // parent and child share state and child registrations pollute parent
            // (and vice versa) via the prototype chain.
            if (!Object.prototype.hasOwnProperty.call(constructor, ON_METHODS)) {
                constructor[ON_METHODS] = new Set();
            }
            if (constructor[ON_METHODS].has(registrationKey))
                return;
            constructor[ON_METHODS].add(registrationKey);
            if (!Object.prototype.hasOwnProperty.call(constructor, ON_HANDLERS)) {
                // Seed with parent's handlers (if any) so inherited @on still fires.
                const inherited = constructor[ON_HANDLERS];
                constructor[ON_HANDLERS] = inherited ? [...inherited] : [];
            }
            for (const event of eventNames) {
                constructor[ON_HANDLERS].push({
                    eventName: event,
                    selector,
                    methodName,
                    method: originalMethod,
                    options: opts
                });
            }
        });
        return originalMethod;
    };
}
// Note: on.ts uses leading-edge-only throttle (no trailing call).
// createThrottled from utils has leading+trailing, so we keep a local leading-only variant.
function throttle$1(fn, delay) {
    let lastCall = 0;
    return function (...args) {
        const now = Date.now();
        if (now - lastCall >= delay) {
            lastCall = now;
            fn.apply(this, args);
        }
    };
}
/**
 * Events that don't bubble - these require capture phase for delegation
 */
const NON_BUBBLING_EVENTS = new Set([
    'scroll',
    'focus',
    'blur',
    'load',
    'unload',
    'error',
    'resize',
    'abort',
    'mouseenter',
    'mouseleave',
    'pointerenter',
    'pointerleave',
]);
/**
 * Setup event listeners for an element or controller instance
 * Called automatically during element connection or controller attachment
 */
function setupEventHandlers(instance, targetElement) {
    const handlers = instance.constructor[ON_HANDLERS];
    if (!handlers || !Array.isArray(handlers) || handlers.length === 0) {
        return;
    }
    // Initialize cleanup object if needed
    if (!instance[CLEANUP]) {
        instance[CLEANUP] = { events: [], eventCancels: [], channels: [], observers: [] };
    }
    if (!instance[CLEANUP].events)
        instance[CLEANUP].events = [];
    if (!instance[CLEANUP].eventCancels)
        instance[CLEANUP].eventCancels = [];
    if (instance[CLEANUP].events.length > 0 || instance[CLEANUP].eventCancels.length > 0) {
        // Events already set up - clean them up first to avoid duplicates
        cleanupEventHandlers(instance);
    }
    for (const handler of handlers) {
        // Get current method from instance (preserves decorator stacking)
        const currentMethod = instance[handler.methodName];
        let boundMethod = currentMethod ? currentMethod.bind(instance) : handler.method.bind(instance);
        const handlerOptions = handler.options || {};
        const debounceDelay = resolveEventTiming(instance, handlerOptions.debounce, '@on', 'debounce');
        const throttleDelay = debounceDelay && debounceDelay > 0
            ? undefined
            : resolveEventTiming(instance, handlerOptions.throttle, '@on', 'throttle');
        // Parse event name for key modifiers
        // Supports both dot notation (@keydown.enter) and colon notation (@keydown:Enter)
        // Only parse colons for keyboard events, not custom events
        const isKeyboardEvent = ['keydown', 'keyup', 'keypress'].includes(handler.eventName.split('.')[0].split(':')[0]);
        // Only keyboard events split on `.`/`:` into a key filter — a custom event
        // name may legitimately contain a dot (e.g. `app.ready`) and must be kept whole.
        const dotIndex = isKeyboardEvent ? handler.eventName.indexOf('.') : -1;
        const colonIndex = isKeyboardEvent ? handler.eventName.indexOf(':') : -1;
        const delimiterIndex = dotIndex > 0 && colonIndex > 0
            ? Math.min(dotIndex, colonIndex)
            : Math.max(dotIndex, colonIndex);
        const baseEventName = delimiterIndex > 0
            ? handler.eventName.substring(0, delimiterIndex)
            : handler.eventName;
        const keyModifier = delimiterIndex > 0
            ? handler.eventName.substring(delimiterIndex + 1)
            : null;
        if (delimiterIndex <= 0)
            warnIfModifierMisuse(handler.eventName);
        // Apply debounce (takes precedence over throttle)
        if (debounceDelay && debounceDelay > 0) {
            const debounced = createDebounced(boundMethod, debounceDelay);
            instance[CLEANUP].eventCancels.push(() => debounced.cancel());
            boundMethod = debounced;
        }
        else if (throttleDelay && throttleDelay > 0) {
            boundMethod = throttle$1(boundMethod, throttleDelay);
        }
        // Create event handler with key modifier support
        // Uses shared keyboard filter implementation from parts.ts
        let keyFilter = null;
        if (keyModifier && ['keydown', 'keyup', 'keypress'].includes(baseEventName)) {
            keyFilter = parseKeyboardFilter(keyModifier);
        }
        // Returns whether the method actually ran, so `once` can be managed
        // manually: native `once` consumes the listener on ANY invocation — a
        // non-matching key or (with multiple tree listeners) a different root
        // would either eat the single firing or allow a second one.
        const createKeyModifierHandler = (method) => {
            if (!keyFilter) {
                return (event) => { method(event); return true; };
            }
            return (event) => {
                const keyEvent = event;
                if (matchesKeyboardFilter(keyEvent, keyFilter)) {
                    method(event);
                    return true;
                }
                return false;
            };
        };
        // Apply key modifier wrapper
        const keyModifierMethod = createKeyModifierHandler(boundMethod);
        // Resolve an explicit DOM scope or an app-context daemon. They are two
        // distinct addressing models and may not be combined.
        const hasExplicitScope = handlerOptions.scope !== undefined;
        const hasDaemon = handlerOptions.daemon !== undefined;
        if (hasExplicitScope && hasDaemon) {
            console.warn(`[snice/@on] "${handler.eventName}" cannot use both scope and daemon — listener skipped.`);
            continue;
        }
        if ((hasDaemon || isDaemonInstance(instance)) && handler.selector) {
            console.warn(`[snice/@on] "${handler.eventName}" cannot delegate "${handler.selector}" on a daemon target — listener skipped.`);
            continue;
        }
        const hasExplicitTarget = hasExplicitScope || hasDaemon;
        // Tree toggles — the same light/shadow pair @query uses. Both default to
        // true: direct listeners attach in both trees, delegated listeners match
        // in both. An explicit scope/daemon owns the listener target outright, so
        // the flags are meaningless there and ignored with a warning.
        const inShadow = handlerOptions.shadow !== false;
        const inLight = handlerOptions.light !== false;
        if (hasExplicitTarget
            && (handlerOptions.light !== undefined || handlerOptions.shadow !== undefined)) {
            console.warn(`[snice/@on] light/shadow are ignored for "${handler.eventName}" — an explicit scope/daemon owns the listener target.`);
        }
        if (!hasExplicitTarget && !inShadow && !inLight) {
            console.warn(`[snice/@on] "${handler.eventName}" disables both light and shadow — listener skipped.`);
            continue;
        }
        let scopedTarget = null;
        if (hasDaemon) {
            try {
                scopedTarget = requireDaemonTarget(instance, handlerOptions.daemon);
            }
            catch (error) {
                console.warn(`[snice/@on] ${error.message} Listener "${handler.eventName}" skipped.`);
                continue;
            }
        }
        else if (hasExplicitScope) {
            scopedTarget = resolveScope(targetElement, handlerOptions.scope);
            if (!scopedTarget) {
                // Dev warning — listener silently dropped when scope cannot resolve.
                // Skip attachment so we don't bind to the wrong target.
                console.warn(`[snice/@on] scope did not resolve for "${handler.eventName}" on ${handler.methodName} — listener skipped.`);
                continue;
            }
        }
        // Main event handler with error handling and event delegation
        if (handler.selector) {
            // Delegated event handling. Listener roots by tree toggle:
            // - shadow → the shadow root (when present)
            // - light  → the host element (hears light-DOM bubbles)
            // - explicit scope: the scoped target only
            const componentShadowRoot = targetElement.shadowRoot;
            const delegationRoots = hasExplicitTarget
                ? [scopedTarget]
                : [
                    ...(inShadow && componentShadowRoot ? [componentShadowRoot] : []),
                    ...(inLight ? [targetElement] : []),
                ];
            if (delegationRoots.length === 0) {
                console.warn(`[snice/@on] "${handler.eventName}" cannot delegate "${handler.selector}" — no shadow root and light disabled; listener skipped.`);
                continue;
            }
            // One event can reach more than one root (e.g. a slotted click seen by
            // both the shadow root and the host); a per-handler Symbol keeps the
            // handler to one invocation per event. The symbol is only stamped on a
            // match so a non-matching root never blocks the other.
            const handledSymbol = Symbol();
            // Auto-enable capture for non-bubbling events when using delegation
            const needsCapture = NON_BUBBLING_EVENTS.has(baseEventName);
            const useCapture = handlerOptions.capture !== undefined
                ? handlerOptions.capture
                : needsCapture;
            // `once` is managed manually (never native): the handler fires exactly
            // once — a non-matching event or key must not consume it, and a match
            // must retire the listeners on EVERY root, not just the one that fired.
            const listenerOptions = {
                capture: useCapture,
                once: false,
                passive: handlerOptions.passive || false,
            };
            const listenerGroup = [];
            const removeGroup = () => {
                for (const entry of listenerGroup) {
                    entry.target.removeEventListener(baseEventName, entry.handler, listenerOptions);
                }
            };
            for (const eventRoot of delegationRoots) {
                // Delegation matches only elements visible in the listener's own tree
                // scope: a shadow-root listener matches its shadow tree, anything else
                // matches its containing document/root. This mirrors native retargeting
                // (child-component internals never match) while still matching a shadow
                // wrapper when the click lands on light-DOM content slotted into it —
                // `target.closest()` alone cannot cross that slot boundary.
                const treeScope = eventRoot instanceof ShadowRoot
                    ? eventRoot
                    : (eventRoot.getRootNode?.() ?? document);
                const findDelegateMatch = (event) => {
                    if (typeof event.composedPath !== 'function') {
                        // Fallback for environments without composedPath: ancestor walk.
                        const target = event.target;
                        return (target.matches && target.matches(handler.selector) && target)
                            || (target.closest && target.closest(handler.selector))
                            || null;
                    }
                    for (const node of event.composedPath()) {
                        const el = node;
                        if (el === eventRoot)
                            break;
                        if (el.nodeType !== 1)
                            continue;
                        if (el.getRootNode() !== treeScope)
                            continue;
                        if (el.matches(handler.selector))
                            return el;
                    }
                    return null;
                };
                const delegatedHandler = (event) => {
                    if (event[handledSymbol])
                        return;
                    const matchingElement = findDelegateMatch(event);
                    if (!matchingElement)
                        return;
                    event[handledSymbol] = true;
                    if (handlerOptions.preventDefault)
                        event.preventDefault();
                    if (handlerOptions.stopPropagation) {
                        event.stopPropagation();
                        event.stopImmediatePropagation();
                    }
                    let fired = false;
                    try {
                        fired = keyModifierMethod(event);
                    }
                    catch (error) {
                        // A throw can only escape user code, so the handler DID run.
                        fired = true;
                        console.error(`Error in event handler ${handler.methodName}:`, error);
                    }
                    if (fired && handlerOptions.once)
                        removeGroup();
                };
                eventRoot.addEventListener(baseEventName, delegatedHandler, listenerOptions);
                listenerGroup.push({ target: eventRoot, handler: delegatedHandler });
                instance[CLEANUP].events.push({
                    target: eventRoot,
                    eventName: baseEventName,
                    handler: delegatedHandler,
                    options: listenerOptions,
                });
            }
        }
        else {
            // Direct event handling.
            // - Default: shadow root + host element (so events inside shadow and on
            //   the host itself both fire). A per-handler Symbol dedupes.
            // - light/shadow narrow the attachment: shadow → the shadow root
            //   listener, light → the host listener.
            // - With explicit scope: attach to the scoped target ONLY (no duplication).
            const shadowRoot = (hasExplicitTarget || !inShadow)
                ? null
                : targetElement.shadowRoot;
            if (!hasExplicitTarget && !shadowRoot && !inLight) {
                console.warn(`[snice/@on] "${handler.eventName}" requests shadow only but the element has no shadow root — listener skipped.`);
                continue;
            }
            // Per-handler private Symbol so dedup is scoped to THIS handler's two
            // listeners (shadowRoot + host). Using Symbol.for() with the method name
            // would collide across components that share a method name (e.g. a parent
            // and nested child both defining `handleClick`), causing the parent's
            // handler to be silently swallowed when events bubble up through a
            // shadow boundary.
            const handledSymbol = Symbol();
            // `once` is managed manually (never native): a key-filtered handler must
            // not be consumed by a non-matching key, and with listeners in two trees
            // the first real firing must retire BOTH, not just the one that fired.
            const listenerOptions = {
                capture: handlerOptions.capture || false,
                once: false,
                passive: handlerOptions.passive || false,
            };
            const listenerGroup = [];
            const removeGroup = () => {
                for (const entry of listenerGroup) {
                    entry.target.removeEventListener(baseEventName, entry.handler, listenerOptions);
                }
            };
            const wrappedMethod = (event) => {
                if (event[handledSymbol])
                    return;
                event[handledSymbol] = true;
                if (handlerOptions.preventDefault)
                    event.preventDefault();
                if (handlerOptions.stopPropagation)
                    event.stopPropagation();
                let fired = false;
                try {
                    fired = keyModifierMethod(event);
                }
                catch (error) {
                    // A throw can only escape user code, so the handler DID run.
                    fired = true;
                    console.error(`Error in event handler ${handler.methodName}:`, error);
                }
                if (fired && handlerOptions.once)
                    removeGroup();
            };
            const attach = (target) => {
                target.addEventListener(baseEventName, wrappedMethod, listenerOptions);
                listenerGroup.push({ target, handler: wrappedMethod });
                instance[CLEANUP].events.push({
                    target,
                    eventName: baseEventName,
                    handler: wrappedMethod,
                    options: listenerOptions,
                });
            };
            if (hasExplicitTarget) {
                attach(scopedTarget);
                continue;
            }
            // Listen on shadow root for events inside shadow DOM
            if (shadowRoot)
                attach(shadowRoot);
            // Also listen on host element (for clicks on host itself, light-DOM
            // bubbles, or when no shadow root) — unless light is disabled.
            if (inLight)
                attach(targetElement);
        }
    }
}
/**
 * Cleanup event listeners for a controller instance
 * Called automatically by the controller system during detach
 */
function cleanupEventHandlers(instance) {
    if (!instance[CLEANUP]?.events)
        return;
    for (const cancel of instance[CLEANUP].eventCancels ?? [])
        cancel();
    instance[CLEANUP].eventCancels = [];
    for (const { target, eventName, handler, options } of instance[CLEANUP].events) {
        target.removeEventListener(eventName, handler, options);
    }
    instance[CLEANUP].events = [];
}

// @dispatch decorator - auto-dispatches custom events from method return values
const DISPATCH_GENERATION = getSymbol('dispatch-generation');
function isDispatchGenerationActive(instance, generation) {
    return (instance[DISPATCH_GENERATION] ?? 0) === generation;
}
/**
 * Decorator that automatically dispatches a custom event after a method is called.
 * The return value of the method becomes the event detail.
 *
 * @param eventName The name of the event to dispatch
 * @param options Optional configuration extending EventInit
 */
function dispatch(eventName, options) {
    return function (originalMethod, _context) {
        // Decorator-instance identity avoids collisions such as event `a_b` on
        // method `c` versus event `a` on method `b_c`.
        const timerKey = Symbol(`@dispatch:${eventName}`);
        return function (...args) {
            // Resolve against the actual decorated instance for every invocation.
            // Capture the generation before calling an async method so teardown can
            // invalidate work that has not reached the scheduling step yet.
            const generation = this[DISPATCH_GENERATION] ?? 0;
            // Create timing wrappers for dispatch (per-instance)
            if (!this[DISPATCH_TIMERS]) {
                this[DISPATCH_TIMERS] = new Map();
            }
            if (!this[DISPATCH_TIMERS].has(timerKey)) {
                this[DISPATCH_TIMERS].set(timerKey, {
                    debounceTimeout: null,
                    throttleLastCall: 0,
                    throttleTimeout: null,
                    invocation: 0,
                });
            }
            const timers = this[DISPATCH_TIMERS].get(timerKey);
            const debounceDelay = resolveEventTiming(this, options?.debounce, '@dispatch', 'debounce');
            const throttleDelay = debounceDelay && debounceDelay > 0
                ? undefined
                : resolveEventTiming(this, options?.throttle, '@dispatch', 'throttle');
            const invocation = ++timers.invocation;
            // A new invocation supersedes deferred work for this decorated method.
            // In particular, a resolver changing debounce to 0 must not leave the
            // previous positive-delay timer armed. Throttle trailing work is also
            // rebuilt below using this invocation's freshly resolved interval.
            if (timers.debounceTimeout)
                clearTimeout(timers.debounceTimeout);
            timers.debounceTimeout = null;
            if (timers.throttleTimeout)
                clearTimeout(timers.throttleTimeout);
            timers.throttleTimeout = null;
            timers.latestDetail = undefined;
            if (debounceDelay && debounceDelay > 0) {
                // Switching from throttle to debounce starts a new timing regime.
                timers.throttleLastCall = 0;
            }
            else if (!throttleDelay || throttleDelay <= 0) {
                // An un-timed invocation cancels both old regimes completely.
                timers.throttleLastCall = 0;
            }
            // Call the original method with preserved this context
            const result = originalMethod.apply(this, args);
            // Helper to dispatch the event
            const doDispatch = (detail) => {
                // Skip dispatch if result is undefined and dispatchOnUndefined is false
                if (detail === undefined && options?.dispatchOnUndefined === false) {
                    return;
                }
                // Copy only platform EventInit fields; Snice-only decorator options do
                // not belong in the CustomEvent constructor dictionary.
                const event = new CustomEvent(eventName, {
                    bubbles: options?.bubbles ?? true,
                    cancelable: options?.cancelable ?? false,
                    composed: options?.composed ?? true,
                    detail
                });
                if (options?.scope !== undefined && options?.daemon !== undefined) {
                    throw new TypeError(`@dispatch('${eventName}') cannot use both scope and daemon.`);
                }
                if (options?.daemon !== undefined) {
                    requireDaemonTarget(this, options.daemon).dispatchEvent(event);
                    return;
                }
                if (options?.scope !== undefined) {
                    const target = resolveScope(this, options.scope);
                    if (!target) {
                        console.warn(`[snice/@dispatch] scope did not resolve for "${eventName}" — event not dispatched.`);
                        return;
                    }
                    target.dispatchEvent(event);
                    return;
                }
                const target = defaultCommunicationTarget(this);
                if (!target) {
                    throw new TypeError(`@dispatch('${eventName}') requires an element, attached controller, or provided @daemon instance.`);
                }
                target.dispatchEvent(event);
            };
            // Helper to handle timed dispatch
            const timedDispatch = (detail) => {
                if (!isDispatchGenerationActive(this, generation))
                    return;
                if (debounceDelay && debounceDelay > 0) {
                    timers.debounceTimeout = setTimeout(() => {
                        timers.debounceTimeout = null;
                        if (isDispatchGenerationActive(this, generation) && timers.invocation === invocation) {
                            doDispatch(detail);
                        }
                    }, debounceDelay);
                    return;
                }
                if (!throttleDelay || throttleDelay <= 0) {
                    doDispatch(detail);
                    return;
                }
                const now = Date.now();
                const remaining = throttleDelay - (now - timers.throttleLastCall);
                if (remaining <= 0) {
                    clearTimeout(timers.throttleTimeout);
                    timers.throttleLastCall = now;
                    doDispatch(detail);
                    return;
                }
                // Record the LATEST detail so the trailing dispatch carries fresh
                // data, not the first-suppressed-call detail captured by closure.
                timers.latestDetail = detail;
                // Always schedule from the last actual dispatch using the interval
                // resolved for THIS invocation. A later invocation can therefore
                // lengthen or shorten the pending trailing window coherently.
                timers.throttleTimeout = setTimeout(() => {
                    timers.throttleLastCall = Date.now();
                    timers.throttleTimeout = null;
                    const d = timers.latestDetail;
                    timers.latestDetail = undefined;
                    if (isDispatchGenerationActive(this, generation) && timers.invocation === invocation) {
                        doDispatch(d);
                    }
                }, remaining);
            };
            // Handle async methods
            if (result instanceof Promise) {
                return result.then((resolvedResult) => {
                    const usesDeferredTiming = (debounceDelay ?? 0) > 0 || (throttleDelay ?? 0) > 0;
                    if (isDispatchGenerationActive(this, generation)
                        && (!usesDeferredTiming || timers.invocation === invocation)) {
                        timedDispatch(resolvedResult);
                    }
                    return resolvedResult;
                });
            }
            // Sync method
            timedDispatch(result);
            return result;
        };
    };
}
/**
 * Clear any pending debounce/throttle dispatch timers on an instance (e.g. on
 * disconnect, so a queued event doesn't fire into a detached node). A dispatch
 * is a one-shot signal, so pending ones are dropped, not replayed.
 */
function clearDispatchTimers(instance) {
    const generation = (instance[DISPATCH_GENERATION] ?? 0) + 1;
    instance[DISPATCH_GENERATION] = generation;
    const timers = instance[DISPATCH_TIMERS];
    if (!timers)
        return;
    for (const t of timers.values()) {
        if (t.debounceTimeout)
            clearTimeout(t.debounceTimeout);
        if (t.throttleTimeout)
            clearTimeout(t.throttleTimeout);
        t.debounceTimeout = null;
        t.throttleTimeout = null;
        t.throttleLastCall = 0;
        t.latestDetail = undefined;
        t.invocation = (t.invocation ?? 0) + 1;
    }
}

/**
 * @context decorator for receiving router context updates
 */
const CONTEXT_HANDLERS = getSymbol('context-handlers');
/**
 * @context decorator for receiving router context updates
 *
 * @example
 * ```typescript
 * @element('my-layout')
 * class MyLayout extends HTMLElement {
 *   @context
 *   handleContext(ctx: Context) {
 *     this.renderNav(ctx.placards, ctx.currentRoute);
 *   }
 *
 *   @context({ debounce: 300 })
 *   handleContextDebounced(ctx: Context) {
 *     // Called after 300ms of no updates
 *   }
 * }
 * ```
 */
function context$1(options = {}) {
    return function (originalMethod, context) {
        const methodName = context.name;
        context.addInitializer(function () {
            const constructor = this.constructor;
            // hasOwnProperty guards so subclasses don't mutate parent state via
            // the prototype chain.
            if (!Object.prototype.hasOwnProperty.call(constructor, CONTEXT_METHODS)) {
                constructor[CONTEXT_METHODS] = new Set();
            }
            if (constructor[CONTEXT_METHODS].has(originalMethod))
                return;
            constructor[CONTEXT_METHODS].add(originalMethod);
            if (!Object.prototype.hasOwnProperty.call(constructor, CONTEXT_HANDLERS)) {
                const inherited = constructor[CONTEXT_HANDLERS];
                constructor[CONTEXT_HANDLERS] = inherited ? [...inherited] : [];
            }
            constructor[CONTEXT_HANDLERS].push({
                methodName,
                method: originalMethod,
                options,
            });
        });
        return originalMethod;
    };
}
/**
 * Set up context handlers for an element or attached controller.
 * Called automatically by the corresponding managed lifecycle.
 */
function setupContextHandler(element, catchUp = false) {
    const handlers = element.constructor[CONTEXT_HANDLERS];
    if (!handlers || !Array.isArray(handlers) || handlers.length === 0) {
        return;
    }
    // Get the Context instance from the router
    const ctx = element[CONTEXT_HANDLER] ?? getNavigationContext(element);
    if (!ctx) {
        return;
    }
    // Store the Context instance for cleanup
    element[NAVIGATION_CONTEXT_INSTANCE] = ctx;
    // A controller can finish attaching after the Router has already announced
    // the current navigation. Track whether an update arrives during this turn;
    // if none does, catch it up once from the Context's current state.
    let receivedContextUpdate = false;
    // Register each handler with the Context
    for (const handler of handlers) {
        const { methodName, method, options } = handler;
        const wrappedMethodName = `__wrapped_${methodName}`;
        // Create wrapped method with timing controls
        element[wrappedMethodName] = function (context) {
            receivedContextUpdate = true;
            // `once` is tracked PER method (not a single element-level flag) so
            // multiple once handlers on the same element each fire.
            const called = element[CONTEXT_CALLED] ||
                (element[CONTEXT_CALLED] = new Set());
            if (options.once && called.has(methodName)) {
                return;
            }
            const callMethod = () => {
                method.call(element, context);
                // Handle once option
                if (options.once) {
                    called.add(methodName);
                    // Unregister ONLY this handler, not the whole element — otherwise the
                    // element's other @context handlers would stop receiving updates.
                    const ctx = element[NAVIGATION_CONTEXT_INSTANCE];
                    if (ctx && typeof ctx[CONTEXT_UNREGISTER] === 'function') {
                        ctx[CONTEXT_UNREGISTER](element, wrappedMethodName);
                    }
                }
            };
            // Per-handler timer slot to avoid debounce/throttle handlers on the same
            // element overwriting each other's state.
            const timerSlot = element[CONTEXT_TIMER] ||
                (element[CONTEXT_TIMER] = {});
            const timerKey = methodName;
            if (options.debounce) {
                clearTimeout(timerSlot[timerKey]?.timeout);
                timerSlot[timerKey] = {
                    timeout: setTimeout(callMethod, options.debounce),
                };
                return;
            }
            if (options.throttle) {
                const now = Date.now();
                const lastCall = timerSlot[timerKey]?.lastCall || 0;
                if (now - lastCall >= options.throttle) {
                    timerSlot[timerKey] = { lastCall: now };
                    callMethod();
                }
                return;
            }
            callMethod();
        };
        // Register with the Context using the wrapped method name
        if (typeof ctx[CONTEXT_REGISTER] === 'function') {
            ctx[CONTEXT_REGISTER](element, wrappedMethodName);
        }
    }
    if (catchUp && typeof ctx[CONTEXT_NOTIFY_ELEMENT] === 'function') {
        return new Promise((resolve) => {
            const notifyIfNeeded = () => {
                try {
                    if (!receivedContextUpdate
                        && element[NAVIGATION_CONTEXT_INSTANCE] === ctx) {
                        ctx[CONTEXT_NOTIFY_ELEMENT](element);
                    }
                }
                finally {
                    resolve();
                }
            };
            if (catchUp === 'task')
                setTimeout(notifyIfNeeded, 0);
            else
                queueMicrotask(notifyIfNeeded);
        });
    }
}
/**
 * Clean up context handlers for an element or attached controller.
 */
function cleanupContextHandler(element) {
    const handlers = element.constructor[CONTEXT_HANDLERS];
    if (!handlers || !Array.isArray(handlers) || handlers.length === 0) {
        return;
    }
    // Clear any pending debounce timers (per-handler slots)
    const timerSlot = element[CONTEXT_TIMER];
    if (timerSlot && typeof timerSlot === 'object') {
        for (const key of Object.keys(timerSlot)) {
            if (timerSlot[key]?.timeout)
                clearTimeout(timerSlot[key].timeout);
        }
        delete element[CONTEXT_TIMER];
    }
    for (const handler of handlers) {
        // Clean up wrapped method
        const wrappedMethodName = `__wrapped_${handler.methodName}`;
        delete element[wrappedMethodName];
    }
    // Unregister from Context if available
    const ctx = element[NAVIGATION_CONTEXT_INSTANCE];
    if (ctx && typeof ctx[CONTEXT_UNREGISTER] === 'function') {
        ctx[CONTEXT_UNREGISTER](element);
    }
    delete element[NAVIGATION_CONTEXT_INSTANCE];
    delete element[CONTEXT_CALLED];
}

// Controller-scoped cleanup registry
class ControllerScope {
    constructor() {
        this.cleanupFns = new Map();
        this.pendingOperations = new Set();
    }
    register(key, cleanup) {
        this.cleanupFns.set(key, cleanup);
    }
    unregister(key) {
        this.cleanupFns.delete(key);
    }
    async cleanup() {
        // Wait for all pending operations
        await Promise.all(this.pendingOperations);
        // Run all cleanup functions
        for (const cleanup of this.cleanupFns.values()) {
            try {
                await cleanup();
            }
            catch (error) {
                console.error('Error during cleanup:', error);
            }
        }
        this.cleanupFns.clear();
    }
    async runOperation(operation) {
        const promise = operation();
        const voidPromise = promise.then(() => { }, () => { });
        this.pendingOperations.add(voidPromise);
        try {
            const result = await promise;
            this.pendingOperations.delete(voidPromise);
            return result;
        }
        catch (error) {
            this.pendingOperations.delete(voidPromise);
            throw error;
        }
    }
}
/**
 * Decorator to register a controller class with a name
 * @param name The name to register the controller under
 */
function controller(name) {
    return function (constructor, _context) {
        // Access globalThis.snice directly to ensure consistency
        const registry = globalThis.snice?.controllerRegistry;
        if (!registry) {
            throw new Error('Snice global registry not initialized');
        }
        registry.set(name, constructor);
        // Mark as controller class for channel decorator detection
        constructor.prototype[IS_CONTROLLER_CLASS] = true;
        // Preserve the registry name on the class. Direct class attachments still
        // skip registry lookup; this is only their inspectable DOM label.
        Object.defineProperty(constructor, CONTROLLER_REGISTRY_NAME, {
            value: name,
            configurable: true
        });
        const pending = snice.pendingControllerAttachments.get(name);
        if (pending) {
            snice.pendingControllerAttachments.delete(name);
            queueMicrotask(() => {
                for (const element of pending) {
                    const nativeName = element[NATIVE_CONTROLLER];
                    const desiredName = nativeName === undefined
                        ? element[CONTROLLER]
                        : nativeName;
                    if (desiredName !== name)
                        continue;
                    attachController(element, name).catch(error => {
                        if (error?.name !== 'ControllerAttachAborted') {
                            console.error(`Failed to attach controller "${name}":`, error);
                        }
                    });
                }
            });
        }
        return constructor;
    };
}
/** Queue an element until its controller module finishes registering. */
function deferControllerAttachment(element, controllerName) {
    // Registration can win the microtask race between attachController's
    // rejected promise and this recovery path. Retry immediately in that case.
    if (snice.controllerRegistry.has(controllerName)) {
        queueMicrotask(() => {
            const nativeName = element[NATIVE_CONTROLLER];
            const desiredName = nativeName === undefined
                ? element[CONTROLLER]
                : nativeName;
            if (desiredName !== controllerName)
                return;
            attachController(element, controllerName).catch(error => {
                if (error?.name !== 'ControllerAttachAborted') {
                    console.error(`Failed to attach controller "${controllerName}":`, error);
                }
            });
        });
        return;
    }
    let pending = snice.pendingControllerAttachments.get(controllerName);
    if (!pending) {
        pending = new Set();
        snice.pendingControllerAttachments.set(controllerName, pending);
    }
    pending.add(element);
    // Give synchronously evaluated and microtask-delayed controller modules a
    // chance to register, while keeping genuinely missing controllers loud.
    queueMicrotask(() => {
        if (snice.pendingControllerAttachments.get(controllerName)?.has(element)) {
            console.error(`Failed to attach controller "${controllerName}":`, new Error(`Controller "${controllerName}" not found in registry`));
        }
    });
}
/**
 * True when the value is a controller class that went through @controller().
 * The decorator is required — it registers the class, marks it, and flushes
 * pending attachments. Direct class binding only changes the attach side.
 */
function isControllerClass(value) {
    return typeof value === 'function'
        && value.prototype?.[IS_CONTROLLER_CLASS] === true;
}
/** Human-readable label for a controller reference (registry name or class). */
function controllerLabel(controller) {
    return typeof controller === 'string'
        ? controller
        : (controller?.name || '(anonymous controller class)');
}
/** Registry/decorator name used for a direct class attachment's DOM marker. */
function directControllerAttributeName(controllerClass) {
    const registeredName = Object.prototype.hasOwnProperty.call(controllerClass, CONTROLLER_REGISTRY_NAME)
        ? controllerClass[CONTROLLER_REGISTRY_NAME]
        : undefined;
    return typeof registeredName === 'string'
        ? registeredName
        : controllerLabel(controllerClass);
}
/**
 * Write the inspectable controller marker without feeding it back into the
 * custom-element attribute channel. Native MutationObservers are asynchronous
 * and are excluded independently by DIRECT_CONTROLLER.
 */
function writeControllerAttribute(element, value) {
    if (value === null) {
        if (!element.hasAttribute('controller'))
            return;
    }
    else if (element.getAttribute('controller') === value) {
        return;
    }
    element[CONTROLLER_ATTRIBUTE_SYNC] = true;
    try {
        if (value === null)
            element.removeAttribute('controller');
        else
            element.setAttribute('controller', value);
    }
    finally {
        delete element[CONTROLLER_ATTRIBUTE_SYNC];
    }
}
/**
 * Restore the diagnostic attribute for an attached class controller. Returns
 * true when the direct class channel owns the element.
 */
function restoreDirectControllerAttribute(element) {
    if (!element[DIRECT_CONTROLLER])
        return false;
    const source = element[CONTROLLER_NAME_KEY];
    if (typeof source !== 'function')
        return false;
    writeControllerAttribute(element, directControllerAttributeName(source));
    return true;
}
/**
 * Attaches a controller to an element
 * @param element The element to attach the controller to
 * @param controller The registry name of the controller, or the decorated
 *                   controller class itself (classes skip the registry)
 */
async function attachController(element, controller) {
    if (typeof controller !== 'string') {
        if (typeof controller !== 'function') {
            throw new Error('attachController: expected a controller name or a controller class, ' +
                `got ${typeof controller}. Controller instances are not supported — pass the class itself.`);
        }
        if (!isControllerClass(controller)) {
            throw new Error(`Controller class "${controllerLabel(controller)}" must be decorated with ` +
                `@controller('name') before it can be attached.`);
        }
    }
    const controllerName = controllerLabel(controller);
    const existingController = element[CONTROLLER_KEY];
    const existingSource = element[CONTROLLER_NAME_KEY];
    // For attribute-managed native elements, check if this is actually the
    // desired controller. Class attachments take the element over instead.
    const nativeController = element[NATIVE_CONTROLLER];
    if (typeof controller === 'string' && nativeController !== undefined && nativeController !== controller) {
        // This attachment is outdated, skip it
        return;
    }
    if (existingSource === controller && existingController) {
        // Already attached and controller exists (name or class reference match)
        if (typeof controller !== 'string') {
            restoreDirectControllerAttribute(element);
        }
        return;
    }
    // If there's an existing controller, detach it first
    if (existingController) {
        await detachController(element);
        // Light-DOM reconciliation can issue commit() and reconnected() requests
        // for the same class while the previous controller is still detaching.
        // If the other request already installed that exact source, this request
        // is redundant. Different sources retain the established rapid-switch
        // behavior and continue through the normal replacement path.
        const replacement = element[CONTROLLER_KEY];
        const replacementSource = element[CONTROLLER_NAME_KEY];
        if (replacement && replacementSource === controller) {
            if (typeof controller !== 'string')
                restoreDirectControllerAttribute(element);
            return;
        }
    }
    let ControllerClass;
    if (typeof controller === 'string') {
        // Access globalThis.snice directly to ensure consistency
        const registry = globalThis.snice?.controllerRegistry;
        if (!registry) {
            throw new Error('Snice global registry not initialized');
        }
        ControllerClass = registry.get(controller);
        if (!ControllerClass) {
            throw new Error(`Controller "${controller}" not found in registry`);
        }
    }
    else {
        ControllerClass = controller;
    }
    // Create controller instance with unique ID and scope
    const controllerInstance = new ControllerClass();
    if (typeof controller !== 'string') {
        // Construction succeeded, so the class channel can safely take ownership.
        // Setting this before construction would leave a stale observer guard when
        // a controller constructor throws.
        element[DIRECT_CONTROLLER] = true;
        delete element[NATIVE_CONTROLLER];
    }
    snice.controllerIdCounter += 1;
    const controllerId = snice.controllerIdCounter;
    const scope = new ControllerScope();
    // Mark this as a controller instance
    controllerInstance[IS_CONTROLLER_INSTANCE] = true;
    controllerInstance[CONTROLLER_ID] = controllerId;
    controllerInstance.element = element;
    // Pass router context from element to controller if it exists
    const routerContext = element[ROUTER_CONTEXT];
    if (routerContext !== undefined) {
        controllerInstance[ROUTER_CONTEXT] = routerContext;
    }
    // Store references. CONTROLLER_NAME_KEY holds the attach source — the
    // registry name string, or the class reference itself for class attaches
    // (reference equality is the dedupe key for classes).
    element[CONTROLLER_KEY] = controllerInstance;
    element[CONTROLLER_NAME_KEY] = controller;
    element[CONTROLLER_OPERATIONS] = scope;
    if (typeof controller !== 'string') {
        // The class reference remains the sole attachment authority. This
        // attribute is diagnostic only; custom-element callbacks and native
        // MutationObservers are guarded from treating it as a string attachment.
        restoreDirectControllerAttribute(element);
    }
    // @respond handlers are installed before the readiness gate below, exactly
    // as elements install theirs in connectedCallback. A child's mount-time
    // @request can be the very thing keeping the host from becoming ready, so
    // the responder has to be reachable while `ready` is still pending. Such a
    // request also releases the gate — it proves the host subtree is live and
    // that something is blocked on this attachment — and is answered once
    // attach() and the remaining setup have run.
    //
    // A `@respond({ daemon })` handler resolves its target through the host's app
    // context, which is unreachable while the host is still disconnected (attach
    // before appendChild). Those are deferred here and registered by the second
    // pass past the gate, where they behaved correctly before this ordering.
    let requestClaimed = false;
    let releaseReadyGate;
    let completeAttach;
    let failAttach;
    const attached = new Promise((resolve, reject) => {
        completeAttach = resolve;
        failAttach = reject;
    });
    attached.catch(() => { }); // No requester when nothing was claimed
    setupResponseHandlers(controllerInstance, element, {
        deferUnresolvableDaemons: true,
        claim: () => {
            requestClaimed = true;
            releaseReadyGate?.();
            return attached;
        },
    });
    // Normally a controller waits for its host's full initialization. The sole
    // exception is attachController(host, ...) from that host's own @ready
    // handler: awaiting ready there would wait on the current handler itself.
    if (!element[READY_HANDLERS_RUNNING]) {
        // Race against abort (detachController) and a 30s deadline so a caller
        // that forgets to append the element gets a clear error.
        const abort = new AbortController();
        element[CONTROLLER_ABORT] = abort;
        const ATTACH_DEADLINE_MS = 30_000;
        try {
            await Promise.race([
                element.ready,
                new Promise(resolve => {
                    if (requestClaimed)
                        return resolve();
                    releaseReadyGate = resolve;
                }),
                new Promise((_, reject) => {
                    const onAbort = () => {
                        // Tagged so fire-and-forget call sites can tell this designed
                        // teardown path apart from real attach failures and stay quiet.
                        const error = new Error(`attachController("${controllerName}"): aborted before element was ready (likely detached or never connected)`);
                        error.name = 'ControllerAttachAborted';
                        reject(error);
                    };
                    if (abort.signal.aborted)
                        return onAbort();
                    abort.signal.addEventListener('abort', onAbort, { once: true });
                    const timerId = setTimeout(() => reject(new Error(`attachController("${controllerName}"): element did not become ready within ${ATTACH_DEADLINE_MS}ms (did you forget to appendChild it?)`)), ATTACH_DEADLINE_MS);
                    abort.signal.addEventListener('abort', () => clearTimeout(timerId), { once: true });
                }),
            ]);
        }
        catch (error) {
            // Attach never got past `ready` (aborted or deadline). Roll back the
            // stored references and the early responders so a later attach does not
            // short-circuit against an instance whose attach() never ran.
            cleanupResponseHandlers(controllerInstance);
            failAttach(error);
            if (element[CONTROLLER_KEY] === controllerInstance) {
                if (typeof controller !== 'string') {
                    writeControllerAttribute(element, null);
                }
                delete element[CONTROLLER_KEY];
                delete element[CONTROLLER_NAME_KEY];
                delete element[CONTROLLER_OPERATIONS];
                if (typeof controller !== 'string') {
                    delete element[DIRECT_CONTROLLER];
                }
            }
            throw error;
        }
        finally {
            if (element[CONTROLLER_ABORT] === abort) {
                delete element[CONTROLLER_ABORT];
            }
        }
    }
    // Past `ready` — mark the controller so detach knows attach was actually run.
    controllerInstance[CONTROLLER_ATTACHED] = true;
    try {
        // Run attach in the controller's scope
        await scope.runOperation(async () => {
            await controllerInstance.attach(element);
        });
        // @context has the same managed lifecycle on controllers as @on,
        // @observe and @respond. Catch up controllers whose async attach completed
        // after the Router announced the current navigation.
        await setupContextHandler(controllerInstance, 'microtask');
        // Setup @observe observers for controller
        setupObservers(controllerInstance, element);
        // Second @respond pass: registers only what the pre-gate pass deferred —
        // daemon-scoped responders whose app context was not resolvable yet.
        setupResponseHandlers(controllerInstance, element);
        // Setup @on event handlers for controller
        setupEventHandlers(controllerInstance, element);
    }
    catch (error) {
        // A claimed request must not wait on an attachment that never completed.
        failAttach(error);
        throw error;
    }
    // Answer whatever was claimed while the host was still becoming ready.
    completeAttach();
    element.dispatchEvent(new CustomEvent('controller-attached', {
        detail: { name: controllerName, controller: controllerInstance }
    }));
}
/**
 * Detaches a controller from an element
 * @param element The element to detach the controller from
 */
async function detachController(element) {
    // If attachController is currently awaiting `.ready`, short-circuit that
    // wait so the pending attach rejects immediately instead of hanging.
    const pendingAbort = element[CONTROLLER_ABORT];
    if (pendingAbort)
        pendingAbort.abort();
    const controllerInstance = element[CONTROLLER_KEY];
    const controllerSource = element[CONTROLLER_NAME_KEY];
    const scope = element[CONTROLLER_OPERATIONS];
    if (!controllerInstance) {
        return;
    }
    // Invalidate dispatch work before every teardown path, including an attach
    // that is still waiting for host readiness and will return early below.
    clearDispatchTimers(controllerInstance);
    // Claim the controller immediately — before any await — so a second detach
    // that overlaps this one (e.g. disconnect's fire-and-forget detach racing a
    // controller reassignment) finds no instance and returns, instead of running
    // detach() a second time on the same controller. The rest of this function
    // works off the locals captured above.
    if (typeof controllerSource === 'function') {
        // Remove the class channel's diagnostic marker while its guards are still
        // active. A queued native MutationObserver therefore sees no attribute and
        // cannot resurrect a registry attachment after detach.
        writeControllerAttribute(element, null);
    }
    delete element[CONTROLLER_KEY];
    delete element[CONTROLLER_NAME_KEY];
    delete element[CONTROLLER_OPERATIONS];
    if (typeof controllerSource === 'function') {
        // Class attach owned the element — release it back to the attribute channel.
        delete element[DIRECT_CONTROLLER];
    }
    // A controller whose attach() was aborted before it ever ran must not have
    // detach() (or the teardown/event) run on it.
    if (!controllerInstance[CONTROLLER_ATTACHED]) {
        return;
    }
    // Run detach in the controller's scope
    if (scope) {
        await scope.runOperation(async () => {
            await controllerInstance.detach(element);
        });
    }
    else {
        await controllerInstance.detach(element);
    }
    // detach() itself may have invoked a decorated dispatch method.
    clearDispatchTimers(controllerInstance);
    controllerInstance.element = null;
    // Cleanup @observe observers for controller
    cleanupObservers(controllerInstance);
    // Cleanup @channel handlers for controller
    cleanupResponseHandlers(controllerInstance);
    // Cleanup @on event handlers for controller
    cleanupEventHandlers(controllerInstance);
    // Cleanup @context handlers for controller
    cleanupContextHandler(controllerInstance);
    // Cleanup the controller scope
    if (scope) {
        await scope.cleanup();
    }
    // Clean up router context reference
    delete controllerInstance[ROUTER_CONTEXT];
    element.dispatchEvent(new CustomEvent('controller-detached', {
        detail: {
            name: controllerSource === undefined ? undefined : controllerLabel(controllerSource),
            controller: controllerInstance
        }
    }));
}
/**
 * Gets the controller instance attached to an element
 * @param element The element to get the controller from
 * @returns The controller instance or undefined
 */
function getController(element) {
    return element[CONTROLLER_KEY];
}
/**
 * Enable controller support for native HTML elements
 * This sets up a MutationObserver to watch for controller attributes
 * on non-custom elements (elements without hyphens in their tag names)
 */
function useNativeElementControllers() {
    // Return if already initialized
    if (globalThis.sniceNativeControllersInitialized) {
        return;
    }
    globalThis.sniceNativeControllersInitialized = true;
    // Process elements that already have controller attribute
    function processElement(element) {
        if (!(element instanceof HTMLElement))
            return;
        // `tagName` can briefly be null when happy-dom/native mutation observers
        // fire on a node that's been detached mid-callback. Skip cleanly.
        if (!element.tagName || element.tagName.includes('-'))
            return;
        if (element[IS_ELEMENT_CLASS])
            return;
        // A controller CLASS is attached (template binding or direct
        // attachController(el, Class)). The class channel owns the element:
        // attribute writes must not attach over it, and insertion sweeps must
        // not detach it.
        if (element[DIRECT_CONTROLLER]) {
            // External writes cannot replace a class binding. Keep the DOM marker
            // honest about the class that is actually attached.
            restoreDirectControllerAttribute(element);
            return;
        }
        const controllerName = element.getAttribute('controller');
        const currentControllerName = element[NATIVE_CONTROLLER];
        // No change
        if (controllerName === currentControllerName)
            return;
        // No attribute and never attribute-managed — nothing for this channel to
        // do. Without this guard, every added native element (including ones a
        // class controller was imperatively attached to before insertion) would
        // fall into the removal branch and get spuriously detached.
        if (!controllerName && currentControllerName === undefined)
            return;
        // Controller removed
        if (!controllerName) {
            delete element[NATIVE_CONTROLLER];
            delete element[CONTROLLER_NAME_KEY];
            detachController(element).catch(error => {
                console.error(`Failed to detach controller from native element:`, error);
            });
            return;
        }
        // Controller added or changed
        element[NATIVE_CONTROLLER] = controllerName;
        if (!element.ready) {
            element.ready = Promise.resolve();
        }
        if (currentControllerName) {
            detachController(element).catch(error => {
                console.error(`Failed to detach old controller from native element:`, error);
            });
        }
        attachController(element, controllerName).catch(error => {
            // Detached before ready — designed teardown, not a failure
            if (error?.name === 'ControllerAttachAborted') {
                console.debug(`Controller "${controllerName}" attach aborted (element detached before ready)`);
                return;
            }
            if (error?.message === `Controller "${controllerName}" not found in registry`) {
                deferControllerAttachment(element, controllerName);
                return;
            }
            console.error(`Failed to attach controller "${controllerName}" to native element:`, error);
        });
    }
    // Set up MutationObserver to watch for controller attributes
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'controller') {
                processElement(mutation.target);
            }
            else if (mutation.type === 'childList') {
                // Process added nodes
                mutation.addedNodes.forEach(node => {
                    if (node instanceof HTMLElement) {
                        // Process the node itself
                        processElement(node);
                        // Process all descendants with controller attribute
                        node.querySelectorAll('[controller]').forEach(processElement);
                    }
                });
            }
        }
    });
    function startObserving() {
        document.querySelectorAll('[controller]').forEach(processElement);
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['controller'],
            childList: true,
            subtree: true
        });
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', startObserving);
    }
    else {
        startObserving();
    }
    // Store observer reference for cleanup if needed
    globalThis.sniceNativeControllerObserver = observer;
}

/**
 * Template system for Snice v3.0.0
 * Provides html`` and css`` tagged template processors with differential rendering
 */
// Unique symbols for type checking
// Use Symbol.for() to ensure symbols are shared across multiple Snice instances
const HTML_RESULT = Symbol.for('snice:html-result');
const CSS_RESULT = Symbol.for('snice:css-result');
// Static css`` sites are the common stylesheet path and can be reused forever.
// Interpolated sites are intentionally not retained: caching every distinct
// primitive interpolation would turn a dynamic css`` call into an unbounded
// per-callsite value history.
const cssResultCache = new WeakMap();
/**
 * Tagged template function for creating HTML templates
 *
 * @example
 * ```typescript
 * html`<div class="card">
 *   <h1>${this.title}</h1>
 *   <button @click=${this.handleClick}>Click me</button>
 * </div>`
 * ```
 */
function html(strings, ...values) {
    return {
        _$litType$: HTML_RESULT,
        strings,
        values
    };
}
/**
 * Tagged template function for SVG fragments.
 *
 * Bare SVG elements (<circle>, <path>, ...) parse as unknown elements in an
 * HTML context. Use svg`` for fragments composed into a surrounding <svg>:
 *
 * @example
 * ```typescript
 * html`<svg viewBox="0 0 100 100">${svg`<circle cx="50" cy="50" r=${r}></circle>`}</svg>`
 * ```
 */
function svg(strings, ...values) {
    return {
        _$litType$: HTML_RESULT,
        strings,
        values,
        svg: true
    };
}
/**
 * Tagged template function for creating CSS
 *
 * @example
 * ```typescript
 * css`:host {
 *   display: block;
 *   padding: 1rem;
 * }`
 * ```
 */
function css(strings, ...values) {
    const cached = values.length === 0 ? cssResultCache.get(strings) : undefined;
    if (cached)
        return cached;
    // Combine strings and values into final CSS text
    let cssText = strings[0];
    for (let i = 0; i < values.length; i++) {
        // Inline a nested css`` result by its text so style fragments compose;
        // String() on a CSSResult object would yield "[object Object]".
        const value = values[i];
        cssText += (isCSSResult(value) ? value.cssText : String(value)) + strings[i + 1];
    }
    const result = {
        _$litType$: CSS_RESULT,
        cssText
    };
    // Try to create constructable stylesheet for better performance
    // This will be cached and reused across instances. Guard against envs
    // without a Document global (for example, workers).
    if (typeof CSSStyleSheet !== 'undefined' &&
        typeof Document !== 'undefined' &&
        'adoptedStyleSheets' in Document.prototype) {
        try {
            const sheet = new CSSStyleSheet();
            sheet.replaceSync(cssText);
            result.styleSheet = sheet;
        }
        catch (e) {
            // Fall back to regular <style> tag if constructable stylesheets fail
        }
    }
    if (values.length === 0)
        cssResultCache.set(strings, result);
    return result;
}
/**
 * Check if a value is a TemplateResult
 */
function isTemplateResult(value) {
    return value && value._$litType$ === HTML_RESULT;
}
/**
 * Check if a value is a CSSResult
 */
function isCSSResult(value) {
    return value && value._$litType$ === CSS_RESULT;
}
/**
 * Nothing - represents no value (different from null/undefined)
 * Used to remove content from templates
 * Use Symbol.for() to ensure symbols are shared across multiple Snice instances
 */
const nothing = Symbol.for('snice:nothing');
// Unique symbol for unsafe HTML
// Use Symbol.for() to ensure symbols are shared across multiple Snice instances
const UNSAFE_HTML = Symbol.for('snice:unsafe-html');
/**
 * Mark a string as raw HTML that should not be escaped
 * WARNING: Only use with sanitized content - using user input can lead to XSS!
 *
 * @example
 * ```typescript
 * const htmlString = '<span class="bold">Hello</span>';
 * html`<div>${unsafeHTML(htmlString)}</div>`
 * ```
 */
function unsafeHTML(html) {
    return {
        _$litType$: UNSAFE_HTML,
        html
    };
}
/**
 * Check if a value is an UnsafeHTML wrapper
 */
function isUnsafeHTML(value) {
    return value && value._$litType$ === UNSAFE_HTML;
}

const REPEAT_RESULT = Symbol.for('snice:repeat-result');
/**
 * Render a keyed collection without adding wrapper elements.
 *
 * Keys are mandatory because repeat() is the explicit identity-preserving list
 * API. Duplicate keys throw before DOM reconciliation rather than silently
 * reusing the wrong row.
 */
function repeat(items, options) {
    if (!options || typeof options.key !== 'function' || typeof options.render !== 'function') {
        throw new TypeError('snice: repeat() requires { key, render }.');
    }
    const array = items == null ? [] : Array.from(items);
    const keys = array.map(options.key);
    const seen = new Set();
    for (const key of keys) {
        if (seen.has(key)) {
            throw new Error(`snice: repeat() received duplicate key ${String(key)}.`);
        }
        seen.add(key);
    }
    return {
        _$repeat$: REPEAT_RESULT,
        items: array,
        keys,
        values: array.map(options.render),
        empty: array.length === 0
            ? typeof options.empty === 'function'
                ? options.empty()
                : options.empty
            : undefined
    };
}
function isRepeatResult(value) {
    return !!value && value._$repeat$ === REPEAT_RESULT;
}

const renderHosts = new WeakSet();
function getRenderRoot(element) {
    return element[RENDER_ROOT] || element.shadowRoot || null;
}
function ensureRenderRoot(element) {
    const existing = getRenderRoot(element);
    if (existing)
        return existing;
    const options = element.constructor[ELEMENT_OPTIONS];
    let root;
    if (options?.renderRoot === 'light' || options?.shadow === false) {
        root = element;
    }
    else if (typeof element.createRenderRoot === 'function') {
        root = element.createRenderRoot();
    }
    else {
        const mode = typeof options?.shadow === 'string' ? options.shadow : 'open';
        root = element.attachShadow({ mode, delegatesFocus: options?.delegatesFocus });
    }
    if (root !== element && !(root instanceof ShadowRoot)) {
        throw new TypeError('snice: createRenderRoot() must return the host element or a ShadowRoot.');
    }
    element[RENDER_ROOT] = root;
    renderHosts.add(element);
    return root;
}
function findRenderHost(node) {
    const root = node.getRootNode();
    if (root instanceof ShadowRoot)
        return root.host;
    let current = node;
    while (current) {
        if (current instanceof HTMLElement && renderHosts.has(current))
            return current;
        current = current.parentNode;
    }
    return null;
}

const SAFE_TAG = /^[a-z][a-z0-9._-]{0,127}$/;
const SAFE_CLASS = /^[A-Za-z_$][A-Za-z0-9_$]{0,127}$/;
const AMBIGUOUS_RENDER_HOST_IDENTITY = Symbol('ambiguous-render-host-identity');
// Keys are weak; values contain only a sanitized label or the ambiguity
// sentinel. The same frozen identity is recorded for the exact constructor
// and its exact prototype only after the caller verifies registration.
const registeredRenderHosts = new WeakMap();
function registerRenderHostIdentity(constructor, tagName) {
    try {
        if (typeof constructor !== 'function' || !SAFE_TAG.test(tagName))
            return;
        const prototypeDescriptor = Object.getOwnPropertyDescriptor(constructor, 'prototype');
        const prototype = prototypeDescriptor && 'value' in prototypeDescriptor
            ? prototypeDescriptor.value
            : undefined;
        if ((typeof prototype !== 'object' && typeof prototype !== 'function') || prototype === null)
            return;
        const nameDescriptor = Object.getOwnPropertyDescriptor(constructor, 'name');
        const name = nameDescriptor && 'value' in nameDescriptor && typeof nameDescriptor.value === 'string'
            ? nameDescriptor.value
            : '';
        const className = SAFE_CLASS.test(name) ? name : '';
        const identity = Object.freeze({
            label: className ? `<${tagName}> (${className})` : `<${tagName}>`,
        });
        const constructorState = registeredRenderHosts.get(constructor);
        const prototypeState = registeredRenderHosts.get(prototype);
        const existingStates = [constructorState, prototypeState].filter((state) => state !== undefined);
        const ambiguous = existingStates.some(state => state === AMBIGUOUS_RENDER_HOST_IDENTITY || state.label !== identity.label);
        const nextState = ambiguous
            ? AMBIGUOUS_RENDER_HOST_IDENTITY
            : existingStates[0] ?? identity;
        // Update both exact keys in the same synchronous registration call. Once
        // either key is ambiguous, no later registration can restore attribution.
        registeredRenderHosts.set(constructor, nextState);
        registeredRenderHosts.set(prototype, nextState);
    }
    catch {
        // Registration diagnostics are best-effort and must not affect the
        // successful custom-elements registration that already occurred.
    }
}
function registeredIdentity(host) {
    const prototype = Object.getPrototypeOf(host);
    if (!prototype)
        return undefined;
    const constructorDescriptor = Object.getOwnPropertyDescriptor(prototype, 'constructor');
    const constructor = constructorDescriptor && 'value' in constructorDescriptor
        ? constructorDescriptor.value
        : undefined;
    if (typeof constructor !== 'function')
        return undefined;
    const prototypeIdentity = registeredRenderHosts.get(prototype);
    const constructorIdentity = registeredRenderHosts.get(constructor);
    return prototypeIdentity !== AMBIGUOUS_RENDER_HOST_IDENTITY &&
        prototypeIdentity === constructorIdentity
        ? prototypeIdentity
        : undefined;
}
/** Capture once at the render boundary; failures fall back without masking the render error. */
function captureRenderHostIdentity(host) {
    let label = '<element>';
    try {
        const registration = registeredIdentity(host);
        if (registration)
            label = registration.label;
    }
    catch {
        // Host/prototype objects can be proxied. Attribution is best-effort
        // and must never replace the original render or template error.
    }
    return Object.freeze({ label, marker: Symbol('render-host-identity') });
}
class ContextualRenderError extends Error {
    #identityMarker;
    constructor(identity, message, cause) {
        super(message, { cause });
        this.#identityMarker = identity.marker;
    }
    belongsTo(identity) {
        return this.#identityMarker === identity.marker;
    }
}
function contextualizeRenderError(identity, error) {
    if (error instanceof ContextualRenderError && error.belongsTo(identity))
        return error;
    const message = error instanceof Error ? error.message : String(error);
    const contextual = new ContextualRenderError(identity, `snice: render failed for ${identity.label}: ${message}`, error);
    if (error instanceof Error)
        contextual.name = error.name;
    return contextual;
}

// Unique marker for dynamic template parts. It parses as a comment node but
// does not get escaped in attributes.
const templateMarker = `snice$${Math.random().toFixed(9).slice(2)}$`;
const templateMarkerMatch = '?' + templateMarker;
// A true comment, not a processing instruction (<?...>): in HTML content both
// parse to the same comment node, but PIs are dropped inside foreign content
// (<svg>), which would silently kill any node binding inside an svg block.
const templateNodeMarker = `<!--${templateMarkerMatch}-->`;
// Escape the `$` chars — as a bare RegExp they'd be end anchors and the
// pattern would never match, so marker-bearing text/comments never split.
const templateMarkerRegex = new RegExp(templateMarker.replace(/\$/g, '\\$'), 'g');
function hasTemplateMarker(value) {
    return value?.includes(templateMarker) ?? false;
}

// Template cache - templates with same string array can be reused
const templateCache = new WeakMap();
// Sentinel for "not yet set" - distinct from undefined/null
const NOT_COMMITTED = Symbol('not-committed');
const pendingDefinitionRegistries = new WeakMap();
function isUpgradedBy(element, constructor) {
    return Object.prototype.isPrototypeOf.call(constructor.prototype, element);
}
function flushPreUpgradePropertyBindings(element, registry) {
    const tagName = element.localName;
    const constructor = tagName && registry?.get(tagName);
    if (!constructor || !isUpgradedBy(element, constructor))
        return;
    const target = element;
    const markedBindings = target[PRE_UPGRADE_PROPERTY_BINDINGS];
    if (!markedBindings?.size)
        return;
    for (const propertyName of Array.from(markedBindings)) {
        // A Snice @property initializer consumes its own marker and expando while
        // the element upgrades. Anything left belongs to a class that retained
        // the native/custom property channel, so replay the latest parked value
        // through that now-upgraded channel.
        if (Object.prototype.hasOwnProperty.call(target, propertyName)) {
            const value = target[propertyName];
            delete target[propertyName];
            target[propertyName] = value;
        }
        markedBindings.delete(propertyName);
    }
    delete target[PRE_UPGRADE_PROPERTY_BINDINGS];
}
function schedulePreUpgradePropertyFlush(element, registry) {
    if (!registry?.whenDefined || pendingDefinitionRegistries.get(element) === registry)
        return;
    pendingDefinitionRegistries.set(element, registry);
    const reference = new WeakRef(element);
    void registry.whenDefined(element.localName).then(() => {
        const target = reference.deref();
        if (target)
            flushPreUpgradePropertyBindings(target, registry);
    });
}
function markPreUpgradePropertyBinding(element, propertyName, registry) {
    const tagName = element.localName;
    if (!tagName?.includes('-'))
        return false;
    const registeredConstructor = registry?.get(tagName);
    if (registeredConstructor) {
        // Template contents can still contain an unupgraded HTMLElement after the
        // definition has been registered. Registry presence alone therefore does
        // not prove that the reactive accessor exists on this clone yet.
        const isUpgraded = isUpgradedBy(element, registeredConstructor);
        if (isUpgraded)
            return false;
        // A registered non-Snice element (or a Snice element that did not
        // redeclare this member) must retain the native IDL channel. Parking its
        // role/title/etc. would replace native reflection with an own expando.
        const reactiveProperties = registeredConstructor[PROPERTIES];
        if (!reactiveProperties?.has(propertyName))
            return false;
    }
    const target = element;
    if (!target[PRE_UPGRADE_PROPERTY_BINDINGS])
        target[PRE_UPGRADE_PROPERTY_BINDINGS] = new Set();
    target[PRE_UPGRADE_PROPERTY_BINDINGS].add(propertyName);
    schedulePreUpgradePropertyFlush(element, registry);
    return true;
}
function commitPropertyValue(element, propertyName, value, registry) {
    if (!markPreUpgradePropertyBinding(element, propertyName, registry)) {
        element[propertyName] = value;
        return;
    }
    // An undefined custom element can inherit a native IDL setter with the same
    // name. Assignment would invoke that setter (for example HTMLElement.role),
    // stringify structured data into an attribute, and leave no own value for
    // @property's upgrade adoption. Park every pre-upgrade binding as the own,
    // configurable value that the initializer already knows how to replay.
    Object.defineProperty(element, propertyName, {
        configurable: true,
        enumerable: true,
        writable: true,
        value,
    });
}
// noChange sentinel - preserves the currently committed value
const noChange = Symbol.for('snice:no-change');
// live() wrapper - marks a property binding value for comparison against the
// actual DOM property instead of the last committed value
const LIVE_MARKER = Symbol.for('snice:live');
/**
 * Wrap a property binding value so the commit compares against the element's
 * CURRENT DOM property rather than the last committed value. Use for inputs
 * whose DOM state the user can change out from under the binding:
 *
 * ```typescript
 * html`<input .value=${live(this.text)} />`
 * ```
 *
 * Without live(), re-rendering with an unchanged bound value skips the DOM
 * write, leaving user-typed text in place.
 */
function live(value) {
    return { _$liveMarker$: LIVE_MARKER, value };
}
function isLive(value) {
    return value && value._$liveMarker$ === LIVE_MARKER;
}
function contextualizeTemplatePreparationError(error, strings) {
    if (error instanceof Error && /\bNear "[\s\S]*"\.$/.test(error.message))
        return error;
    const message = error instanceof Error ? error.message : String(error);
    const source = Array.from(strings).join('${…}').replace(/\s+/g, ' ').trim();
    const tag = /<([a-z][a-z0-9-]*)>/i.exec(message)?.[1];
    const binding = /binding "([^"]+)"/i.exec(message)?.[1];
    const clue = tag ? `<${tag}` : binding ?? '';
    const clueIndex = clue ? source.indexOf(clue) : -1;
    const center = clueIndex >= 0 ? clueIndex : 0;
    const start = Math.max(0, center - 40);
    const end = Math.min(source.length, center + 80);
    const prefix = start > 0 ? '…' : '';
    const suffix = end < source.length ? '…' : '';
    return new Error(`${message} Near "${prefix}${source.slice(start, end)}${suffix}".`, {
        cause: error
    });
}
/**
 * Check if value is a primitive (can be compared with ===)
 */
const isPrimitive = (value) => value === null || (typeof value !== 'object' && typeof value !== 'function');
/**
 * Check if value is iterable (array or has Symbol.iterator)
 */
const isIterable = (value) => Array.isArray(value) || typeof value?.[Symbol.iterator] === 'function';
const isPromiseLike = (value) => !!value && typeof value.then === 'function';
const isAsyncIterable = (value) => !!value && typeof value[Symbol.asyncIterator] === 'function';
/**
 * A prepared template ready for rendering
 */
class Template {
    constructor(result, element, attrNamesForParts) {
        this.parts = [];
        /**
         * Value index of a `key=${...}` binding in this template, or -1 if unkeyed.
         * Used by keyed list reconciliation in NodePart._commitIterable.
         */
        this.keyIndex = -1;
        this.element = element;
        const walker = document.createTreeWalker(element.content, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT);
        let partIndex = 0;
        const nodesToRemove = [];
        let node;
        while ((node = walker.nextNode()) !== null) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node;
                const tagName = element.tagName.toLowerCase();
                const parentTag = element.parentElement?.tagName.toLowerCase();
                if ((tagName === 'else' || tagName === 'else-if') && parentTag !== 'if') {
                    throw new Error(`snice: <${tagName}> must be a direct child of <if>.`);
                }
                if ((tagName === 'when' || tagName === 'default') && parentTag !== 'case') {
                    throw new Error(`snice: <${tagName}> must be a direct child of <case>.`);
                }
                // Handle virtual elements: <if>, <else-if>, <case>, <when>
                // Keep them in the DOM with display:contents for now
                // Will optimize later with proper template extraction
                if (tagName === 'if') {
                    // <if value="${condition}">children</if>
                    const valueAttr = element.getAttribute('value');
                    if (valueAttr && valueAttr.includes(templateMarker)) {
                        // Remove the value attribute
                        element.removeAttribute('value');
                        this.parts.push({
                            type: 'conditional-if',
                            index: partIndex++,
                            element // Keep the <if> element
                        });
                        // Continue processing children normally
                    }
                    else {
                        throw new Error('snice: <if> requires a condition expression.');
                    }
                    continue;
                }
                // Handle <case> element
                if (tagName === 'case') {
                    // <case value="${value}">children</case>
                    const valueAttr = element.getAttribute('value');
                    if (valueAttr && valueAttr.includes(templateMarker)) {
                        for (const child of Array.from(element.childNodes)) {
                            if (child.nodeType === Node.ELEMENT_NODE) {
                                const branchTag = child.tagName.toLowerCase();
                                if (branchTag !== 'when' && branchTag !== 'default') {
                                    throw new Error('snice: <case> may contain only direct <when> and <default> branches.');
                                }
                            }
                            else if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim() ||
                                child.nodeType === Node.COMMENT_NODE && child.data.includes(templateMarker)) {
                                throw new Error('snice: content inside <case> must be nested in a <when> or <default> branch.');
                            }
                        }
                        // Remove the value attribute
                        element.removeAttribute('value');
                        this.parts.push({
                            type: 'conditional-case',
                            index: partIndex++,
                            element // Keep the <case> element
                        });
                        // Continue processing children normally
                    }
                    else {
                        throw new Error('snice: <case> requires a value expression.');
                    }
                    continue;
                }
                if (tagName === 'else-if') {
                    const valueAttr = element.getAttribute('value');
                    if (valueAttr && valueAttr.includes(templateMarker)) {
                        element.removeAttribute('value');
                        this.parts.push({
                            type: 'conditional-else-if',
                            index: partIndex++,
                            element
                        });
                    }
                    else {
                        throw new Error('snice: <else-if> requires a condition expression.');
                    }
                    continue;
                }
                if (tagName === 'when') {
                    const valueAttr = element.getAttribute('value');
                    if (valueAttr && valueAttr.includes(templateMarker)) {
                        element.removeAttribute('value');
                        this.parts.push({
                            type: 'conditional-when',
                            index: partIndex++,
                            element
                        });
                    }
                    continue;
                }
                if (element.hasAttributes()) {
                    const attributes = element.attributes;
                    const attrsToRemove = [];
                    for (let i = 0; i < attributes.length; i++) {
                        const attr = attributes[i];
                        const value = attr.value;
                        // Check for attribute bindings
                        if (value.includes(templateMarker)) {
                            attrsToRemove.push(attr);
                            // Get original attribute name with preserved case
                            const originalName = attrNamesForParts[partIndex] || attr.name;
                            // Extract static string segments by splitting on marker
                            const attrStrings = value.split(templateMarker);
                            // Number of expressions = number of markers = attrStrings.length - 1
                            const expressionCount = attrStrings.length - 1;
                            // Property/boolean/event bindings take a SINGLE expression — they
                            // can't concatenate static text (a `.prop` is an object, not a
                            // string). They consume the first expression; any static text or
                            // extra expressions are ignored. But the value index must still
                            // advance by the true marker count, or every later binding shifts.
                            const isSingleExpressionBinding = /^@@?|^[.?]/.test(originalName) ||
                                originalName.startsWith('...') ||
                                originalName.startsWith('class:') ||
                                originalName.startsWith('style:');
                            if (isSingleExpressionBinding) {
                                const hasStaticText = attrStrings.some((s) => s !== '');
                                if (expressionCount > 1 || hasStaticText) {
                                    console.warn(`snice: binding "${originalName}" takes a single expression; ` +
                                        `static text and extra interpolations are ignored. ` +
                                        `Use a plain attribute for string interpolation.`);
                                }
                            }
                            if (originalName.startsWith('...')) {
                                const spreadName = originalName.slice(3).toLowerCase();
                                if (!['props', 'properties', 'attrs', 'attributes', 'events'].includes(spreadName)) {
                                    throw new Error(`snice: unknown spread binding "${originalName}". ` +
                                        'Use ...props, ...attrs, or ...events.');
                                }
                                this.parts.push({
                                    type: 'spread',
                                    index: partIndex,
                                    name: spreadName,
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (originalName.startsWith('class:')) {
                                if (!originalName.slice(6))
                                    throw new Error('snice: class: binding requires a class name.');
                                this.parts.push({
                                    type: 'class',
                                    index: partIndex,
                                    name: originalName.slice(6),
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (originalName.startsWith('style:')) {
                                if (!originalName.slice(6))
                                    throw new Error('snice: style: binding requires a CSS property name.');
                                this.parts.push({
                                    type: 'style',
                                    index: partIndex,
                                    name: originalName.slice(6),
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (originalName.startsWith('@@')) {
                                if (!originalName.slice(2).split('|')[0])
                                    throw new Error('snice: event binding requires an event name.');
                                // Escaped event binding for events with @ in the name (e.g., @@snice/event -> @snice/event)
                                this.parts.push({
                                    type: 'event',
                                    index: partIndex,
                                    name: originalName.slice(1), // Keep the @ in the event name
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (originalName.startsWith('@')) {
                                if (!originalName.slice(1).split('|')[0])
                                    throw new Error('snice: event binding requires an event name.');
                                // Event binding (single value only)
                                this.parts.push({
                                    type: 'event',
                                    index: partIndex,
                                    name: originalName.slice(1),
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (originalName.startsWith('.')) {
                                if (!originalName.slice(1))
                                    throw new Error('snice: property binding requires a property name.');
                                // Property binding (single value only)
                                this.parts.push({
                                    type: 'property',
                                    index: partIndex,
                                    name: originalName.slice(1),
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (originalName.startsWith('?')) {
                                if (!originalName.slice(1))
                                    throw new Error('snice: boolean binding requires an attribute name.');
                                // Boolean attribute (single value only)
                                this.parts.push({
                                    type: 'boolean-attribute',
                                    index: partIndex,
                                    name: originalName.slice(1),
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else if (attr.name === 'controller' &&
                                attrStrings.length === 2 && attrStrings[0] === '' && attrStrings[1] === '') {
                                // Bare controller=${...} binding: accepts a decorated
                                // controller class directly, or a registry name string.
                                // Interpolated forms (controller="a-${x}") stay plain
                                // string attributes.
                                this.parts.push({
                                    type: 'controller',
                                    index: partIndex,
                                    element
                                });
                                partIndex += expressionCount;
                            }
                            else {
                                // Regular attribute - supports multiple interpolations
                                // Store static string segments for interpolation
                                this.parts.push({
                                    type: 'attribute',
                                    index: partIndex,
                                    name: attr.name,
                                    element,
                                    attrStrings
                                });
                                // Increment by number of expressions consumed
                                partIndex += expressionCount;
                            }
                        }
                    }
                    // Remove marker attributes
                    for (const attr of attrsToRemove) {
                        element.removeAttribute(attr.name);
                    }
                }
            }
            else if (node.nodeType === Node.COMMENT_NODE) {
                const comment = node;
                // Check for marker match (processing instruction becomes comment)
                if (comment.data === templateMarkerMatch) {
                    // Node part
                    const parent = comment.parentNode;
                    const endNode = document.createComment('');
                    parent.insertBefore(endNode, comment.nextSibling);
                    this.parts.push({
                        type: 'node',
                        index: partIndex++,
                        startNode: comment,
                        endNode
                    });
                }
                else if (comment.data.includes(templateMarker)) {
                    // Binding(s) inside an authored HTML comment (<!-- ${x} -->).
                    // Render values into the comment text; critically, consume the
                    // right number of value indices so later parts stay aligned.
                    const commentStrings = comment.data.split(templateMarkerRegex);
                    this.parts.push({
                        type: 'comment',
                        index: partIndex,
                        startNode: comment,
                        attrStrings: commentStrings
                    });
                    partIndex += commentStrings.length - 1;
                }
            }
            else if (node.nodeType === Node.TEXT_NODE) {
                const text = node;
                const data = text.data;
                if (data.includes(templateMarker)) {
                    // Split text node at markers
                    const parent = text.parentNode;
                    const parts = data.split(templateMarkerRegex);
                    const lastIndex = parts.length - 1;
                    for (let i = 0; i < lastIndex; i++) {
                        parent.insertBefore(document.createTextNode(parts[i]), text);
                        const comment = document.createComment('');
                        const endNode = document.createComment('');
                        parent.insertBefore(comment, text);
                        parent.insertBefore(endNode, text);
                        this.parts.push({
                            type: 'node',
                            index: partIndex++,
                            startNode: comment,
                            endNode
                        });
                    }
                    // Last part
                    if (parts[lastIndex] !== '') {
                        text.data = parts[lastIndex];
                    }
                    else {
                        nodesToRemove.push(text);
                    }
                }
            }
        }
        // Remove marker nodes
        for (const node of nodesToRemove) {
            node.parentNode?.removeChild(node);
        }
        // Record the value index of a `key=${...}` binding (first one wins) so
        // list rendering can associate DOM with keys instead of indices.
        for (const part of this.parts) {
            if (part.type === 'attribute' && part.name === 'key') {
                this.keyIndex = part.index;
                break;
            }
        }
    }
}
/**
 * Prepare a template for rendering
 *
 * Uses state tracking to correctly handle multi-interpolation in attributes.
 * Tracks whether we're inside a tag and inside a quoted attribute value.
 */
function prepareTemplate(result) {
    // Check cache first
    const { strings } = result;
    const cached = templateCache.get(strings);
    if (cached) {
        return cached;
    }
    // Build HTML with markers and extract original attribute names
    const htmlParts = [];
    const attrNamesForParts = [];
    // State tracking for multi-interpolation support
    let inTag = false; // Inside a tag (between < and >)
    let inAttrValue = false; // Inside a quoted attribute value
    let inComment = false; // Inside an HTML comment (<!-- ... -->)
    let attrQuoteChar = ''; // The quote character (' or ")
    let currentAttrName = ''; // The current attribute name
    for (let i = 0; i < strings.length; i++) {
        const str = strings[i];
        htmlParts.push(str);
        if (i < strings.length - 1) {
            // Update state by scanning the string
            for (let j = 0; j < str.length; j++) {
                const char = str[j];
                if (inComment) {
                    // Only the closing --> matters inside a comment
                    if (char === '-' && str.startsWith('-->', j)) {
                        inComment = false;
                        j += 2;
                    }
                }
                else if (!inTag) {
                    // Looking for tag start (or comment start)
                    if (char === '<') {
                        if (str.startsWith('<!--', j)) {
                            inComment = true;
                            j += 3;
                        }
                        else if (/[A-Za-z!/?]/.test(str[j + 1] || '')) {
                            inTag = true;
                        }
                    }
                }
                else if (!inAttrValue) {
                    // Inside tag, but not in attribute value
                    if (char === '>') {
                        inTag = false;
                    }
                    else if (char === '"' || char === "'") {
                        inAttrValue = true;
                        attrQuoteChar = char;
                    }
                    else if (char === '=') {
                        // Extract attribute name (look backwards for it).
                        // Includes `~` for the "any-modifier" keyboard prefix
                        // (e.g. `@keydown.~enter`) so it isn't truncated to `.enter`.
                        let attrStart = j - 1;
                        while (attrStart >= 0 && /[\w\-\.@\?/:\+~|]/.test(str[attrStart])) {
                            attrStart--;
                        }
                        currentAttrName = str.substring(attrStart + 1, j).trim();
                    }
                }
                else {
                    // Inside quoted attribute value
                    if (char === attrQuoteChar) {
                        inAttrValue = false;
                        attrQuoteChar = '';
                    }
                }
            }
            // Now determine what kind of marker to insert based on current state
            if (inComment) {
                // Binding inside an HTML comment — insert a plain marker into the
                // comment text. The Template scanner turns it into a CommentPart so
                // the value index stays aligned (a swallowed marker would shift every
                // binding after the comment).
                attrNamesForParts.push('');
                htmlParts.push(templateMarker);
            }
            else if (inAttrValue) {
                // We're inside a quoted attribute value - this is an attribute binding
                // For subsequent interpolations in same attribute, keep using same attr name
                attrNamesForParts.push(currentAttrName);
                htmlParts.push(templateMarker);
            }
            else if (inTag) {
                // Inside tag but not in attr value - check for special cases
                // Check if this is start of attribute value (= at end of string)
                const trimmed = str.trimEnd();
                if (trimmed.endsWith('=')) {
                    // Extract attribute name (same `~` inclusion as the in-tag branch
                    // above — supports `@keydown.~enter` keyboard modifier prefix).
                    let attrStart = trimmed.length - 2;
                    while (attrStart >= 0 && /[\w\-\.@\?\/:\+~|]/.test(trimmed[attrStart])) {
                        attrStart--;
                    }
                    currentAttrName = trimmed.substring(attrStart + 1, trimmed.length - 1).trim();
                    attrNamesForParts.push(currentAttrName);
                    htmlParts.push(templateMarker);
                }
                else {
                    // Check if this is a meta element (<if> or <case>)
                    const metaElementMatch = str.match(/<(if|else-if|case|when)\s*$/);
                    if (metaElementMatch) {
                        currentAttrName = 'value';
                        attrNamesForParts.push('value');
                        htmlParts.push(`value="${templateMarker}"`);
                    }
                    else {
                        const nearbyTemplate = (str.slice(-40) + '${…}' + (strings[i + 1] ?? '').slice(0, 40)).replace(/\s+/g, ' ').trim();
                        throw new Error('snice: expressions directly in opening tags are not supported; ' +
                            'use an explicit attribute, property, event, class, style, or named spread binding. ' +
                            `Near "${nearbyTemplate}".`);
                    }
                }
            }
            else {
                // Outside any tag - this is node content
                attrNamesForParts.push('');
                htmlParts.push(templateNodeMarker);
            }
        }
    }
    const html = htmlParts.join('');
    const template = document.createElement('template');
    if (result.svg) {
        // Parse in the SVG namespace by wrapping, then unwrap: the parsed nodes
        // keep their namespace when moved out of the temporary <svg> element.
        template.innerHTML = `<svg>${html}</svg>`;
        const wrapper = template.content.firstElementChild;
        while (wrapper.firstChild) {
            template.content.insertBefore(wrapper.firstChild, wrapper);
        }
        template.content.removeChild(wrapper);
    }
    else {
        template.innerHTML = html;
    }
    let tmpl;
    try {
        tmpl = new Template(result, template, attrNamesForParts);
    }
    catch (error) {
        throw contextualizeTemplatePreparationError(error, strings);
    }
    // Cache the template for reuse
    templateCache.set(strings, tmpl);
    return tmpl;
}
/**
 * Extract the key of a list item rendered from a template with a
 * `key=${...}` binding. Returns undefined for unkeyed items.
 */
function getItemKey(item, renderIdentity) {
    if (!isTemplateResult(item))
        return undefined;
    let tmpl;
    try {
        tmpl = prepareTemplate(item);
    }
    catch (error) {
        throw renderIdentity ? contextualizeRenderError(renderIdentity, error) : error;
    }
    return tmpl.keyIndex === -1 ? undefined : item.values[tmpl.keyIndex];
}
/**
 * Instance of a rendered template
 */
class TemplateInstance {
    #renderIdentity;
    #customElementRegistry;
    constructor(result, renderIdentity = null, customElementRegistry = document.defaultView?.customElements ?? globalThis.customElements ?? null) {
        this.parts = [];
        this.fragment = null;
        this.conditionalParts = []; // if/case parts with their indices
        this.regularParts = []; // all other parts with their indices
        this.#renderIdentity = renderIdentity;
        this.#customElementRegistry = customElementRegistry;
        try {
            this.template = prepareTemplate(result);
        }
        catch (error) {
            throw renderIdentity ? contextualizeRenderError(renderIdentity, error) : error;
        }
        this.strings = result.strings;
    }
    /**
     * Check if this instance uses the same template strings
     * (template identity is based on the strings array reference)
     */
    isSameTemplate(strings) {
        return this.strings === strings;
    }
    /** Internal attribution value; intentionally contains no live renderer host. */
    getRenderHostIdentity() {
        return this.#renderIdentity;
    }
    renderFragment() {
        if (!this.fragment) {
            // First render - clone template and create parts
            this.fragment = this.template.element.content.cloneNode(true);
            // Build a map of nodes from template to cloned fragment
            const walker = document.createTreeWalker(this.template.element.content, NodeFilter.SHOW_ALL);
            const clonedWalker = document.createTreeWalker(this.fragment, NodeFilter.SHOW_ALL);
            const nodeMap = new Map();
            let templateNode = walker.currentNode;
            let clonedNode = clonedWalker.currentNode;
            while (templateNode && clonedNode) {
                nodeMap.set(templateNode, clonedNode);
                templateNode = walker.nextNode();
                clonedNode = clonedWalker.nextNode();
            }
            const dynamicWhenElements = new Set();
            for (const definition of this.template.parts) {
                if (definition.type === 'conditional-when') {
                    dynamicWhenElements.add(nodeMap.get(definition.element));
                }
            }
            for (let i = 0; i < this.template.parts.length; i++) {
                const partDef = this.template.parts[i];
                let part;
                switch (partDef.type) {
                    case 'node':
                        const startNode = nodeMap.get(partDef.startNode);
                        const endNode = nodeMap.get(partDef.endNode);
                        part = new NodePart(startNode, endNode, this.#renderIdentity, this.#customElementRegistry);
                        break;
                    case 'attribute':
                        const attrElement = nodeMap.get(partDef.element);
                        part = new AttributePart(attrElement, partDef.name, partDef.attrStrings);
                        break;
                    case 'property':
                        const propElement = nodeMap.get(partDef.element);
                        part = new PropertyPart(propElement, partDef.name, this.#customElementRegistry);
                        break;
                    case 'boolean-attribute':
                        const boolElement = nodeMap.get(partDef.element);
                        part = new BooleanAttributePart(boolElement, partDef.name);
                        break;
                    case 'event':
                        const eventElement = nodeMap.get(partDef.element);
                        part = new EventPart(eventElement, partDef.name);
                        break;
                    case 'class':
                        const classElement = nodeMap.get(partDef.element);
                        part = new ClassPart(classElement, partDef.name);
                        break;
                    case 'style':
                        const styleElement = nodeMap.get(partDef.element);
                        part = new StylePart(styleElement, partDef.name);
                        break;
                    case 'spread':
                        const spreadElement = nodeMap.get(partDef.element);
                        part = new SpreadPart(spreadElement, partDef.name, this.#customElementRegistry);
                        break;
                    case 'controller':
                        const controllerElement = nodeMap.get(partDef.element);
                        part = new ControllerPart(controllerElement);
                        break;
                    case 'conditional-if':
                        const conditionalIfElement = nodeMap.get(partDef.element);
                        part = new ConditionalIfPart(conditionalIfElement);
                        break;
                    case 'conditional-else-if':
                        const conditionalElseIfElement = nodeMap.get(partDef.element);
                        part = new ConditionalElseIfPart(conditionalElseIfElement);
                        break;
                    case 'conditional-case':
                        const conditionalCaseElement = nodeMap.get(partDef.element);
                        part = new ConditionalCasePart(conditionalCaseElement, dynamicWhenElements);
                        break;
                    case 'conditional-when':
                        const conditionalWhenElement = nodeMap.get(partDef.element);
                        part = new ConditionalWhenPart(conditionalWhenElement);
                        break;
                    case 'comment':
                        const commentNode = nodeMap.get(partDef.startNode);
                        part = new CommentPart(commentNode, partDef.attrStrings);
                        break;
                    default:
                        throw new Error(`Unknown part type: ${partDef.type}`);
                }
                this.parts.push(part);
                // Separate conditional parts from regular parts for optimized update
                // Use partDef.index (the VALUE index) not i (the part array index)
                if (part instanceof ConditionalIfPart ||
                    part instanceof ConditionalElseIfPart ||
                    part instanceof ConditionalCasePart ||
                    part instanceof ConditionalWhenPart) {
                    this.conditionalParts.push({ part, index: partDef.index });
                }
                else {
                    this.regularParts.push({ part, index: partDef.index });
                }
            }
        }
        return this.fragment;
    }
    render(values) {
        const fragment = this.renderFragment();
        // Commit values to parts
        this.update(values);
        return fragment;
    }
    update(values) {
        // Optimized: Process conditional parts first (if any), then regular parts
        // Using pre-separated arrays with cached indices avoids instanceof and indexOf calls
        // Process conditional parts first (they control visibility)
        for (const { part, index } of this.conditionalParts) {
            part.commit(values[index]);
        }
        // Conditions are staged above, then flushed once. This avoids transiently
        // mounting an else/default branch while later else-if/when expressions in
        // the same update are still being committed.
        const flushed = new Set();
        for (const { part } of this.conditionalParts) {
            const coordinator = part instanceof ConditionalElseIfPart
                ? part.coordinator
                : part instanceof ConditionalWhenPart
                    ? part.coordinator
                    : part;
            if (flushed.has(coordinator))
                continue;
            flushed.add(coordinator);
            if (coordinator instanceof ConditionalIfPart || coordinator instanceof ConditionalCasePart) {
                coordinator.flush();
            }
        }
        for (const { part, index } of this.regularParts) {
            // AttributeParts with interpolation consume multiple values
            if (part instanceof AttributePart && part.strings !== undefined) {
                // Pass full values array and starting index for interpolation
                part.commit(values, index);
                // The part consumes (strings.length - 1) values
                // But since we're iterating by template part index, this is handled by the Template
            }
            else if (part instanceof CommentPart) {
                // CommentParts also consume (strings.length - 1) values
                part.commit(values, index);
            }
            else {
                part.commit(values[index]);
            }
        }
        // Branch switches can move event-bearing nodes between the live DOM and
        // parked fragments. Reconcile connection state after every update so
        // listeners detach while parked and reattach when their branch returns.
        let lifecycleError;
        for (const part of this.parts) {
            try {
                if (part.isConnected)
                    part.reconnected();
                else
                    part.disconnected();
            }
            catch (error) {
                lifecycleError ??= error;
            }
        }
        if (lifecycleError)
            throw lifecycleError;
    }
    clear() {
        let lifecycleError;
        for (const part of this.parts) {
            try {
                part.destroy();
            }
            catch (error) {
                lifecycleError ??= error;
            }
        }
        if (lifecycleError)
            throw lifecycleError;
    }
    disconnected(preserveEventListeners = false) {
        let lifecycleError;
        for (const part of this.parts) {
            try {
                part.disconnected(preserveEventListeners);
            }
            catch (error) {
                lifecycleError ??= error;
            }
        }
        if (lifecycleError)
            throw lifecycleError;
    }
    reconnected() {
        let lifecycleError;
        for (const part of this.parts) {
            try {
                part.reconnected();
            }
            catch (error) {
                lifecycleError ??= error;
            }
        }
        if (lifecycleError)
            throw lifecycleError;
    }
}
/**
 * Base class for all parts
 */
class Part {
    get isConnected() {
        return this.element?.isConnected ?? false;
    }
    disconnected(_preserveEventListeners = false) { }
    reconnected() { }
    destroy() {
        let lifecycleError;
        try {
            this.clear();
        }
        catch (error) {
            lifecycleError = error;
        }
        if (lifecycleError)
            throw lifecycleError;
    }
}
/**
 * NodePart handles text content and nested templates
 *
 * Lit-HTML style optimizations:
 * - Reuses TemplateInstance when same template strings are rendered
 * - Reuses text nodes when updating primitive → primitive
 * - Reuses child NodeParts when rendering arrays
 */
class NodePart extends Part {
    #renderIdentity;
    #customElementRegistry;
    constructor(startNode, endNode, renderIdentity = null, customElementRegistry = null) {
        super();
        this.type = 'node';
        this._committedValue = NOT_COMMITTED;
        this._itemKeys = null; // keys of the last committed iterable, when fully keyed
        this._asyncSource = null;
        this._asyncVersion = 0;
        this._asyncIterator = null;
        this._asyncRunning = false;
        this._asyncStarted = false;
        this._asyncCompleted = false;
        this._asyncPaused = false;
        this._committingAsyncValue = false;
        this.startNode = startNode;
        this.endNode = endNode;
        this.#renderIdentity = renderIdentity;
        this.#customElementRegistry = customElementRegistry;
    }
    _withRenderContext(error) {
        return this.#renderIdentity ? contextualizeRenderError(this.#renderIdentity, error) : error;
    }
    commit(value) {
        // Handle noChange sentinel
        if (value === noChange) {
            return;
        }
        if (isPromiseLike(value) || isAsyncIterable(value)) {
            this._commitAsync(value);
            return;
        }
        if (!this._committingAsyncValue && this._asyncSource)
            this._cancelAsync(true);
        // Handle nothing/null/undefined/empty - clear content
        if (value === nothing || value == null || value === '') {
            if (this._committedValue !== nothing) {
                const cleanupError = this._clear();
                this._committedValue = nothing;
                if (cleanupError)
                    throw cleanupError;
            }
            this._committedValue = nothing;
            return;
        }
        // Handle primitives (string, number, boolean, bigint, symbol)
        if (isPrimitive(value)) {
            if (value !== this._committedValue) {
                this._commitText(value);
            }
            return;
        }
        // Handle nested templates
        if (isTemplateResult(value)) {
            this._commitTemplateResult(value);
            return;
        }
        // Explicit keyed repeat() result. It feeds keys directly into the list
        // reconciler, so rendered roots do not need a synthetic key attribute or
        // wrapper element.
        if (isRepeatResult(value)) {
            if (value.values.length === 0) {
                this.commit(value.empty ?? nothing);
            }
            else {
                this._commitIterable(value.values, value.keys);
            }
            return;
        }
        // Handle unsafe HTML
        if (isUnsafeHTML(value)) {
            this._commitUnsafeHTML(value);
            return;
        }
        // Handle DOM nodes
        if (value.nodeType !== undefined) {
            this._commitNode(value);
            return;
        }
        // Handle iterables (arrays, etc.)
        if (isIterable(value)) {
            this._commitIterable(value);
            return;
        }
        // Fallback: convert to string
        this._commitText(value);
    }
    /**
     * Commit a primitive value as text
     * OPTIMIZATION: Reuses existing text node if previous value was also primitive
     */
    _commitText(value) {
        // Try to reuse existing text node
        const canReuse = this._committedValue !== NOT_COMMITTED &&
            this._committedValue !== nothing &&
            isPrimitive(this._committedValue) &&
            !Array.isArray(this._committedValue);
        if (canReuse) {
            const textNode = this.startNode.nextSibling;
            if (textNode && textNode.nodeType === Node.TEXT_NODE) {
                textNode.data = String(value);
                this._committedValue = value;
                return;
            }
        }
        const cleanupError = this._clear();
        this._insertBefore(document.createTextNode(String(value)));
        this._committedValue = value;
        if (cleanupError)
            throw cleanupError;
    }
    /**
     * Commit a TemplateResult
     * OPTIMIZATION: Reuses TemplateInstance if same template strings
     */
    _commitTemplateResult(result) {
        // Check if we can reuse the existing TemplateInstance
        const committedInstance = this._committedValue;
        if (committedInstance instanceof TemplateInstance) {
            // Check if same template by comparing strings reference
            const cachedTemplate = templateCache.get(result.strings);
            if (cachedTemplate && committedInstance.template === cachedTemplate) {
                // SAME TEMPLATE - just update values (efficient path!)
                committedInstance.update(result.values);
                return;
            }
        }
        // Different template or first render: prepare and validate the replacement
        // while it is detached. If a binding throws, the currently
        // committed range remains intact and its lifecycle stays connected.
        const instance = new TemplateInstance(result, this.#renderIdentity, this.#customElementRegistry);
        const fragment = instance.renderFragment();
        try {
            instance.update(result.values);
        }
        catch (error) {
            instance.clear();
            throw error;
        }
        const previousValue = this._committedValue;
        const previousKeys = this._itemKeys;
        const previousFragment = this.endNode.ownerDocument.createDocumentFragment();
        let previousNode = this.startNode.nextSibling;
        while (previousNode && previousNode !== this.endNode) {
            const next = previousNode.nextSibling;
            previousFragment.appendChild(previousNode);
            previousNode = next;
        }
        this._insertBefore(fragment);
        this._committedValue = instance;
        this._itemKeys = null;
        try {
            if (this.isConnected)
                instance.reconnected();
        }
        catch (error) {
            try {
                instance.clear();
            }
            catch (cleanupError) {
                console.error('snice: failed to clean up a rejected nested template:', cleanupError);
            }
            let node = this.startNode.nextSibling;
            while (node && node !== this.endNode) {
                const next = node.nextSibling;
                node.remove();
                node = next;
            }
            this._insertBefore(previousFragment);
            this._committedValue = previousValue;
            this._itemKeys = previousKeys;
            throw error;
        }
        this._disposeCommitted(previousValue);
    }
    /**
     * Commit an iterable (array, Set, etc.)
     * OPTIMIZATION: Reuses existing NodeParts for items
     */
    _commitIterable(value, explicitKeys) {
        const items = Array.isArray(value) ? value : Array.from(value);
        const newKeys = explicitKeys
            ? Array.from(explicitKeys)
            : items.map(item => getItemKey(item, this.#renderIdentity));
        if (newKeys.length !== items.length) {
            throw new Error('snice: keyed iterable produced a different number of keys and values.');
        }
        const definedKeys = newKeys.filter(key => key !== undefined);
        if (definedKeys.length > 0 && definedKeys.length !== newKeys.length) {
            console.warn('snice: a list mixes keyed and unkeyed items. Identity falls back to position; ' +
                'use repeat(items, { key, render }) for deterministic updates.');
        }
        if (definedKeys.length > 0 && new Set(definedKeys).size !== definedKeys.length) {
            throw new Error('snice: a keyed list contains duplicate keys.');
        }
        let cleanupError;
        // If previous value wasn't an array of parts, start fresh
        if (!Array.isArray(this._committedValue) ||
            !(this._committedValue[0] instanceof NodePart)) {
            cleanupError = this._clear();
            this._committedValue = [];
            this._itemKeys = null;
        }
        const allKeyed = items.length > 0 && newKeys.every(k => k !== undefined);
        const oldKeys = this._itemKeys;
        // Keyed reconciliation: when every item carries a key=${...} binding and
        // the key order changed, DOM (and its state) follows the key, not the
        // index. Unchanged key order falls through to the cheap index path.
        if (allKeyed && oldKeys && this._committedValue.length > 0) {
            const sameOrder = oldKeys.length === newKeys.length &&
                newKeys.every((k, i) => k === oldKeys[i]);
            if (!sameOrder) {
                cleanupError ??= this._reconcileKeyed(items, newKeys, this._committedValue, oldKeys);
                this._itemKeys = newKeys;
                if (cleanupError)
                    throw cleanupError;
                return;
            }
        }
        const itemParts = this._committedValue;
        let partIndex = 0;
        for (const item of items) {
            let itemPart;
            if (partIndex >= itemParts.length) {
                // Need new NodePart - create with markers
                const startMarker = document.createComment('');
                const endMarker = document.createComment('');
                this._insertBefore(startMarker);
                this._insertBefore(endMarker);
                itemPart = new NodePart(startMarker, endMarker, this.#renderIdentity, this.#customElementRegistry);
                itemParts.push(itemPart);
            }
            else {
                // Reuse existing NodePart
                itemPart = itemParts[partIndex];
            }
            // Set value on this part (recursively handles templates, text, etc.)
            itemPart.commit(item);
            partIndex++;
        }
        // Cleanup excess parts from previous render
        if (partIndex < itemParts.length) {
            // Clear DOM for removed items
            for (let i = partIndex; i < itemParts.length; i++) {
                const part = itemParts[i];
                // Dispose async state as well as removing the content. These item parts
                // are leaving the list permanently, not merely being moved.
                try {
                    part.destroy();
                }
                catch (error) {
                    cleanupError ??= error;
                }
                // Remove the markers themselves
                part.startNode.remove();
                part.endNode.remove();
            }
            // Truncate array
            itemParts.length = partIndex;
        }
        // Remember keys so the next commit can reconcile by key
        this._itemKeys = allKeyed ? newKeys : null;
        if (cleanupError)
            throw cleanupError;
    }
    /**
     * Reorder/reuse item parts so each key keeps its DOM.
     * Old parts whose keys disappeared are removed; new keys get fresh parts.
     */
    _reconcileKeyed(items, newKeys, oldParts, oldKeys) {
        const parent = this.endNode.parentNode;
        let cleanupError;
        // Map old parts by key (first occurrence wins on duplicate keys)
        const oldByKey = new Map();
        for (let i = 0; i < oldParts.length; i++) {
            if (!oldByKey.has(oldKeys[i]))
                oldByKey.set(oldKeys[i], oldParts[i]);
        }
        // Choose a part for each new item: reuse by key, or create during placement
        const reused = new Set();
        const newParts = new Array(items.length).fill(null);
        for (let i = 0; i < items.length; i++) {
            const candidate = oldByKey.get(newKeys[i]);
            if (candidate && !reused.has(candidate)) {
                reused.add(candidate);
                newParts[i] = candidate;
            }
        }
        // Remove parts whose keys are gone (before placement, so the DOM walk
        // below only ever sees surviving nodes)
        for (const part of oldParts) {
            if (!reused.has(part)) {
                try {
                    part.destroy();
                }
                catch (error) {
                    cleanupError ??= error;
                }
                part.startNode.remove();
                part.endNode.remove();
            }
        }
        // Place parts in order. `ref` walks the surviving DOM; a part already
        // sitting at `ref` is skipped, anything else is moved (or created) there.
        let ref = this.startNode.nextSibling;
        for (let i = 0; i < newParts.length; i++) {
            let part = newParts[i];
            if (part) {
                if (part.startNode === ref) {
                    // Already in position — advance past its range
                    ref = part.endNode.nextSibling;
                }
                else {
                    // Move the part's whole range [startNode..endNode] before ref.
                    // Detach into a fragment first, then insert in one operation —
                    // node identity (and any state inside) is preserved either way.
                    const range = document.createDocumentFragment();
                    let node = part.startNode;
                    const stop = part.endNode.nextSibling;
                    while (node && node !== stop) {
                        const next = node.nextSibling;
                        range.appendChild(node);
                        node = next;
                    }
                    parent.insertBefore(range, ref);
                }
            }
            else {
                // New key — fresh part inserted at the current position
                const startMarker = document.createComment('');
                const endMarker = document.createComment('');
                parent.insertBefore(startMarker, ref);
                parent.insertBefore(endMarker, ref);
                part = new NodePart(startMarker, endMarker, this.#renderIdentity, this.#customElementRegistry);
                newParts[i] = part;
            }
            part.commit(items[i]);
        }
        this._committedValue = newParts;
        return cleanupError;
    }
    /**
     * Commit a DOM node directly
     */
    _commitNode(node) {
        if (this._committedValue !== node) {
            const cleanupError = this._clear();
            this._insertBefore(node);
            this._committedValue = node;
            if (cleanupError)
                throw cleanupError;
        }
    }
    /**
     * Commit unsafe HTML string
     */
    _commitUnsafeHTML(value) {
        // Can't diff arbitrary HTML, but when the STRING is unchanged the DOM is
        // already correct — skip the clear+reparse so live state (typed input
        // values, focus, scroll) inside the block survives unrelated re-renders.
        const prev = this._committedValue;
        if (isUnsafeHTML(prev) && prev.html === value.html) {
            return;
        }
        const cleanupError = this._clear();
        const temp = document.createElement('template');
        temp.innerHTML = value.html;
        this._insertBefore(temp.content);
        this._committedValue = value;
        if (cleanupError)
            throw cleanupError;
    }
    _commitAsync(source) {
        // A Promise/stream object is the identity of the async operation. Once it
        // settles, unrelated parent renders must keep the committed result rather
        // than clearing it and subscribing to the same source again.
        if (source === this._asyncSource)
            return;
        this._cancelAsync(false);
        this._asyncSource = source;
        this._asyncStarted = false;
        this._asyncCompleted = false;
        this._asyncPaused = false;
        const cleanupError = this._clear();
        this._committedValue = nothing;
        this._startAsyncSource();
        if (cleanupError)
            throw cleanupError;
    }
    _startAsyncSource(forceConnected = false) {
        const source = this._asyncSource;
        if (!source || this._asyncRunning || this._asyncCompleted ||
            (!forceConnected && !this.isConnected))
            return;
        this._asyncRunning = true;
        this._asyncStarted = true;
        this._asyncPaused = false;
        const version = ++this._asyncVersion;
        if (isAsyncIterable(source)) {
            try {
                this._asyncIterator = source[Symbol.asyncIterator]();
            }
            catch (error) {
                this._asyncRunning = false;
                this._asyncCompleted = true;
                console.error('snice: async iterable template value failed:', this._withRenderContext(error));
                return;
            }
            void (async () => {
                try {
                    while (this._asyncIterator) {
                        const result = await this._asyncIterator.next();
                        if (result.done || version !== this._asyncVersion)
                            break;
                        this._commitAsyncValue(result.value);
                    }
                }
                catch (error) {
                    if (version === this._asyncVersion) {
                        console.error('snice: async iterable template value failed:', this._withRenderContext(error));
                    }
                }
                finally {
                    if (version === this._asyncVersion) {
                        this._asyncIterator = null;
                        this._asyncRunning = false;
                        this._asyncCompleted = true;
                    }
                }
            })();
            return;
        }
        Promise.resolve(source).then(value => {
            if (version !== this._asyncVersion)
                return;
            this._asyncRunning = false;
            this._asyncCompleted = true;
            try {
                this._commitAsyncValue(value);
            }
            catch (error) {
                console.error('snice: promise template value failed:', this._withRenderContext(error));
            }
        }, error => {
            if (version !== this._asyncVersion)
                return;
            this._asyncRunning = false;
            this._asyncCompleted = true;
            console.error('snice: promise template value failed:', this._withRenderContext(error));
        });
    }
    _commitAsyncValue(value) {
        this._committingAsyncValue = true;
        try {
            this.commit(value);
        }
        finally {
            this._committingAsyncValue = false;
        }
    }
    _cancelAsync(clearSource) {
        this._asyncVersion++;
        const iterator = this._asyncIterator;
        this._asyncIterator = null;
        this._asyncRunning = false;
        if (clearSource) {
            this._asyncSource = null;
            this._asyncStarted = false;
            this._asyncCompleted = false;
            this._asyncPaused = false;
        }
        if (iterator?.return) {
            try {
                void Promise.resolve(iterator.return()).catch(() => { });
            }
            catch {
                // Cancellation is best-effort; internal state is already detached.
            }
        }
    }
    _insertBefore(node) {
        this.endNode.parentNode?.insertBefore(node, this.endNode);
    }
    _clear() {
        const parent = this.startNode.parentNode;
        const committed = this._committedValue;
        let cleanupError;
        try {
            this._disposeCommitted(committed);
        }
        catch (error) {
            cleanupError = error;
        }
        if (!parent) {
            this._committedValue = nothing;
            return cleanupError;
        }
        let node = this.startNode.nextSibling;
        while (node && node !== this.endNode) {
            const next = node.nextSibling;
            parent.removeChild(node);
            node = next;
        }
        this._committedValue = nothing;
        return cleanupError;
    }
    _disposeCommitted(committed) {
        let lifecycleError;
        if (committed instanceof TemplateInstance) {
            try {
                committed.clear();
            }
            catch (error) {
                lifecycleError = error;
            }
        }
        else if (Array.isArray(committed)) {
            for (const item of committed) {
                if (!(item instanceof NodePart))
                    continue;
                try {
                    item.destroy();
                }
                catch (error) {
                    lifecycleError ??= error;
                }
            }
        }
        if (lifecycleError)
            throw lifecycleError;
    }
    clear() {
        this._cancelAsync(true);
        const cleanupError = this._clear();
        if (cleanupError)
            throw cleanupError;
    }
    get isConnected() {
        return this.startNode.isConnected;
    }
    disconnected(preserveEventListeners = false) {
        let lifecycleError;
        if (this._committedValue instanceof TemplateInstance) {
            try {
                this._committedValue.disconnected(preserveEventListeners);
            }
            catch (error) {
                lifecycleError ??= error;
            }
        }
        else if (Array.isArray(this._committedValue)) {
            for (const part of this._committedValue) {
                if (!(part instanceof NodePart))
                    continue;
                try {
                    part.disconnected(preserveEventListeners);
                }
                catch (error) {
                    lifecycleError ??= error;
                }
            }
        }
        if (this._asyncSource && this._asyncRunning) {
            this._cancelAsync(false);
            this._asyncPaused = true;
        }
        if (lifecycleError)
            throw lifecycleError;
    }
    reconnected() {
        let lifecycleError;
        if (this._committedValue instanceof TemplateInstance) {
            try {
                this._committedValue.reconnected();
            }
            catch (error) {
                lifecycleError ??= error;
            }
        }
        else if (Array.isArray(this._committedValue)) {
            for (const part of this._committedValue) {
                if (!(part instanceof NodePart))
                    continue;
                try {
                    part.reconnected();
                }
                catch (error) {
                    lifecycleError ??= error;
                }
            }
        }
        if (this._asyncSource && (!this._asyncStarted || this._asyncPaused)) {
            this._startAsyncSource(true);
        }
        if (lifecycleError)
            throw lifecycleError;
    }
}
/**
 * AttributePart handles regular attribute updates
 *
 * Supports multiple interpolations: class="a ${b} c ${d} e"
 * Lit-HTML style: tracks each value separately for dirty checking
 */
class AttributePart extends Part {
    constructor(element, name, strings) {
        super();
        this.type = 'attribute';
        this._committedValue = NOT_COMMITTED;
        this.lastValues = null;
        this.lastValueIndex = 0;
        this.element = element;
        this.name = name;
        // Determine if this is interpolation or single-value binding
        if (strings && (strings.length > 2 || strings[0] !== '' || strings[1] !== '')) {
            // Interpolation case: class="foo ${x} bar ${y}"
            // strings = ["foo ", " bar ", ""]
            this._committedValue = new Array(strings.length - 1).fill(NOT_COMMITTED);
            this.strings = strings;
        }
        else {
            // Single-value case: class="${x}"
            this._committedValue = NOT_COMMITTED;
        }
    }
    /**
     * Commit value(s) to the attribute
     *
     * For single-value binding: commit(value)
     * For interpolation: commit(values, startIndex) where values is full template values array
     */
    commit(value, valueIndex = 0) {
        let change = false;
        let finalValue;
        // === SINGLE-VALUE BINDING ===
        if (this.strings === undefined) {
            if (value === noChange)
                return;
            change = !isPrimitive(value) ||
                (value !== this._committedValue && value !== noChange);
            if (change) {
                this._committedValue = value;
                finalValue = value;
            }
            else {
                return; // No change, skip DOM update
            }
        }
        // === INTERPOLATION BINDING ===
        else {
            const values = value;
            this.lastValues = [...values];
            this.lastValueIndex = valueIndex;
            const committedValues = this._committedValue;
            finalValue = this.strings[0];
            for (let i = 0; i < this.strings.length - 1; i++) {
                let v = values[valueIndex + i];
                // Handle noChange sentinel
                if (v === noChange) {
                    v = committedValues[i];
                    // First-ever commit: there is no previous value to keep — resolve
                    // to empty. Without this, the NOT_COMMITTED symbol would be
                    // string-concatenated below and throw.
                    if (v === NOT_COMMITTED)
                        v = '';
                }
                // Track if any value changed
                change = change || !isPrimitive(v) || v !== committedValues[i];
                // Handle nothing sentinel - removes entire attribute
                if (v === nothing) {
                    finalValue = nothing;
                }
                else if (finalValue !== nothing) {
                    finalValue = finalValue + (v ?? '') + this.strings[i + 1];
                }
                // Always record each value for future comparison
                committedValues[i] = v;
            }
            if (!change) {
                return; // No change, skip DOM update
            }
        }
        // Commit to DOM
        this._commitValue(finalValue);
    }
    _commitValue(value) {
        // Only the `nothing` sentinel removes the attribute. null/undefined
        // commit an empty attribute value, consistent with the interpolated
        // path above (which renders them as '').
        if (value === nothing) {
            this.element.removeAttribute(this.name);
        }
        else {
            this.element.setAttribute(this.name, String(value ?? ''));
        }
    }
    clear() {
        this.element.removeAttribute(this.name);
    }
}
/**
 * CommentPart renders binding values into an authored HTML comment's text
 * (<!-- debug: ${x} -->). Its real job is index alignment: each binding in a
 * comment consumes one value slot, so bindings after the comment stay bound
 * to their own values.
 */
class CommentPart extends Part {
    constructor(node, strings) {
        super();
        this.type = 'node';
        this.lastValues = null;
        this.lastValueIndex = 0;
        this.node = node;
        this.strings = strings;
        this._committedValues = new Array(strings.length - 1).fill(NOT_COMMITTED);
    }
    /**
     * Commit values into the comment text.
     * commit(values, startIndex) — like interpolated AttributePart, this part
     * consumes (strings.length - 1) values from the template values array.
     */
    commit(values, startIndex = 0) {
        const vals = values;
        this.lastValues = [...vals];
        this.lastValueIndex = startIndex;
        let change = false;
        let text = this.strings[0];
        for (let i = 0; i < this.strings.length - 1; i++) {
            let v = vals[startIndex + i];
            if (v === noChange) {
                v = this._committedValues[i];
                if (v === NOT_COMMITTED)
                    v = '';
            }
            change = change || !isPrimitive(v) || v !== this._committedValues[i];
            this._committedValues[i] = v;
            text += String(v === nothing ? '' : (v ?? '')) + this.strings[i + 1];
        }
        if (change) {
            if (text.includes('--') || text.endsWith('-')) {
                throw new Error('snice: comment expressions cannot produce "--" or end with "-".');
            }
            this.node.data = text;
        }
    }
    clear() { }
}
/** Toggle one class without rebuilding the element's full class string. */
class ClassPart extends Part {
    constructor(element, name) {
        super();
        this.type = 'class';
        this.committed = NOT_COMMITTED;
        this.element = element;
        this.name = name;
    }
    commit(value) {
        if (value === noChange)
            return;
        const enabled = value !== nothing && Boolean(value);
        if (enabled === this.committed)
            return;
        this.committed = enabled;
        this.element.classList.toggle(this.name, enabled);
    }
    clear() {
        this.element.classList.remove(this.name);
        this.committed = NOT_COMMITTED;
    }
}
/** Set one CSS property, including custom properties, declaratively. */
class StylePart extends Part {
    constructor(element, name) {
        super();
        this.type = 'style';
        this.committed = NOT_COMMITTED;
        this.element = element;
        this.name = name;
    }
    commit(value) {
        if (value === noChange || Object.is(value, this.committed))
            return;
        this.committed = value;
        const style = this.element.style;
        if (value === nothing || value == null || value === false) {
            style.removeProperty(this.name);
        }
        else {
            style.setProperty(this.name, String(value));
        }
    }
    clear() {
        this.element.style.removeProperty(this.name);
        this.committed = NOT_COMMITTED;
    }
}
function validateSpreadEvents(next, label) {
    const names = new Set();
    for (const [rawName, value] of Object.entries(next)) {
        const name = rawName.startsWith('@') ? rawName.slice(1) : rawName;
        if (!name)
            throw new TypeError(`snice: ${label} contains an empty event name.`);
        if (names.has(name)) {
            throw new TypeError(`snice: ${label} contains duplicate event name "${name}".`);
        }
        names.add(name);
        const listenerObject = !!value && typeof value === 'object' &&
            typeof value.handleEvent === 'function';
        if (value !== nothing && value != null && value !== false &&
            typeof value !== 'function' && !listenerObject) {
            throw new TypeError(`snice: ${label} event "${name}" expects a function, EventListenerObject, or null.`);
        }
    }
}
/** Named spreads: ...props, ...attrs, and ...events. */
class SpreadPart extends Part {
    #customElementRegistry;
    constructor(element, name, customElementRegistry = null) {
        super();
        this.type = 'spread';
        this.committed = {};
        this.listeners = new Map();
        this.consumedOnce = new Map();
        this.element = element;
        this.name = name;
        this.#customElementRegistry = customElementRegistry;
    }
    commit(value) {
        if (value === noChange)
            return;
        if (value === nothing || value == null)
            value = {};
        if (typeof value !== 'object' || Array.isArray(value)) {
            throw new TypeError(`snice: ...${this.name} expects an object.`);
        }
        const next = value;
        if (this.name === 'events')
            this.commitEvents(next);
        else if (this.name === 'props' || this.name === 'properties')
            this.commitProperties(next);
        else
            this.commitAttributes(next);
        this.committed = { ...next };
    }
    commitProperties(next) {
        for (const key of Object.keys(this.committed)) {
            if (!Object.prototype.hasOwnProperty.call(next, key)) {
                commitPropertyValue(this.element, key, undefined, this.#customElementRegistry);
            }
        }
        for (const [key, value] of Object.entries(next)) {
            if (Object.is(this.committed[key], value))
                continue;
            commitPropertyValue(this.element, key, value === nothing ? undefined : value, this.#customElementRegistry);
        }
    }
    commitAttributes(next) {
        for (const key of Object.keys(this.committed)) {
            if (!Object.prototype.hasOwnProperty.call(next, key))
                this.element.removeAttribute(key);
        }
        for (const [key, value] of Object.entries(next)) {
            if (Object.is(this.committed[key], value))
                continue;
            if (value === nothing || value == null || value === false)
                this.element.removeAttribute(key);
            else
                this.element.setAttribute(key, value === true ? '' : String(value));
        }
    }
    commitEvents(next) {
        validateSpreadEvents(next, `...${this.name}`);
        for (const [name, value] of this.consumedOnce) {
            const rawName = Object.keys(next).find(key => this.eventName(key) === name);
            if (!rawName || !Object.is(next[rawName], value))
                this.consumedOnce.delete(name);
        }
        for (const [name, entry] of this.listeners) {
            const rawName = Object.keys(next).find(key => this.eventName(key) === name);
            if (rawName && Object.is(next[rawName], entry.value))
                continue;
            this.element.removeEventListener(name, entry.listener, entry.options);
            this.listeners.delete(name);
        }
        for (const [rawName, value] of Object.entries(next)) {
            const name = this.eventName(rawName);
            if (this.listeners.has(name))
                continue;
            const listenerObject = !!value && typeof value === 'object' &&
                typeof value.handleEvent === 'function';
            if (value === nothing || value == null || value === false)
                continue;
            if (this.consumedOnce.has(name) && Object.is(this.consumedOnce.get(name), value))
                continue;
            this.consumedOnce.delete(name);
            const options = listenerObject ? value : undefined;
            const listener = ((event) => {
                if (options?.once)
                    this.consumedOnce.set(name, value);
                if (listenerObject)
                    value.handleEvent(event);
                else
                    value.call(this.resolveHost(), event);
            });
            this.element.addEventListener(name, listener, options);
            this.listeners.set(name, { value, listener, options });
        }
    }
    eventName(name) {
        return name.startsWith('@') ? name.slice(1) : name;
    }
    resolveHost() {
        return findRenderHost(this.element) || this.element;
    }
    clear() {
        if (this.name === 'events') {
            for (const [name, entry] of this.listeners) {
                this.element.removeEventListener(name, entry.listener, entry.options);
            }
            this.listeners.clear();
            this.consumedOnce.clear();
        }
        else if (this.name !== 'props' && this.name !== 'properties') {
            for (const key of Object.keys(this.committed))
                this.element.removeAttribute(key);
        }
        // A property spread is being destroyed with the element that owns it.
        // Resetting native/custom properties here is unnecessary and can throw for
        // setters that reject undefined (textarea.value is a common example).
        // Stale keys are still removed during an ordinary live spread update.
        this.committed = {};
    }
    disconnected(preserveEventListeners = false) {
        if (this.name === 'events' && !preserveEventListeners)
            this.detachListeners();
    }
    reconnected() {
        if (this.name === 'props' || this.name === 'properties') {
            flushPreUpgradePropertyBindings(this.element, this.#customElementRegistry);
        }
        if (this.name === 'events' && this.listeners.size === 0)
            this.commitEvents(this.committed);
    }
    detachListeners() {
        for (const [name, entry] of this.listeners) {
            this.element.removeEventListener(name, entry.listener, entry.options);
        }
        this.listeners.clear();
    }
}
/**
 * PropertyPart handles property bindings (.property=${value})
 */
class PropertyPart extends Part {
    #customElementRegistry;
    constructor(element, name, customElementRegistry = null) {
        super();
        this.type = 'property';
        this._committedValue = NOT_COMMITTED;
        this.element = element;
        this.name = name;
        this.#customElementRegistry = customElementRegistry;
    }
    commit(value) {
        if (value === noChange)
            return;
        // live(): compare against the element's actual DOM property, so state
        // the user changed (typed text, toggled checkbox) is reset even when the
        // bound value itself is unchanged.
        if (isLive(value)) {
            value = value.value;
            if (value === noChange)
                return;
            const domValue = this.element[this.name];
            if (value === domValue) {
                this._committedValue = value;
                return;
            }
        }
        else {
            // Dirty check - skip if same value
            if (value === this._committedValue)
                return;
        }
        this._committedValue = value;
        commitPropertyValue(this.element, this.name, value === nothing ? undefined : value, this.#customElementRegistry);
    }
    reconnected() {
        flushPreUpgradePropertyBindings(this.element, this.#customElementRegistry);
    }
    clear() {
        // This part is being destroyed with its owning template. The element is
        // about to leave the tree, so writing `undefined` is both unnecessary and
        // unsafe for native setters such as HTMLTextAreaElement.value.
        this._committedValue = NOT_COMMITTED;
    }
}
/**
 * ControllerPart handles bare controller=${...} bindings.
 *
 * Accepts a controller class decorated with @controller (attached directly,
 * no registry lookup) or a registry name string (delegated to the attribute
 * channel with the exact semantics of a static controller="name"). While a
 * class is bound it owns the element: the decorator name is reflected for
 * inspection, while the attribute/MutationObserver attach channel stays inert.
 *
 * Renders commit while the tree is detached and connect afterwards, so
 * attachment happens on reconnected()/connected commits; every hook is
 * idempotent because the update loop reconciles connection state on each pass.
 */
class ControllerPart extends Part {
    constructor(element) {
        super();
        this.type = 'controller';
        this._committedValue = NOT_COMMITTED;
        this.element = element;
    }
    commit(value) {
        if (value === noChange)
            return;
        // Normalize every "no controller" shape to null
        const next = (value == null || value === false || value === '' || value === nothing)
            ? null
            : value;
        const first = this._committedValue === NOT_COMMITTED;
        const previous = first ? null : this._committedValue;
        if (!first && next === previous)
            return;
        this._committedValue = next;
        const el = this.element;
        // Leaving a class binding: release the element back to the attribute channel.
        if (typeof previous === 'function' && typeof next !== 'function') {
            if (el[PENDING_CONTROLLER_BINDING] !== undefined) {
                delete el[PENDING_CONTROLLER_BINDING];
            }
            else if (el[IS_ELEMENT_CLASS]) {
                el.controller = null;
            }
            else {
                this._detach();
            }
        }
        if (typeof next === 'function') {
            if (this.isConnected) {
                this._apply(next);
            }
            else if (this.element.localName.includes('-') && !el[IS_ELEMENT_CLASS]) {
                // Park on not-yet-upgraded custom elements even while detached — the
                // element picks the class up in connectedCallback on upgrade.
                el[PENDING_CONTROLLER_BINDING] = next;
            }
            // Detached native/upgraded elements attach in reconnected().
        }
        else if (typeof next === 'string') {
            this.element.setAttribute('controller', next);
        }
        else if (typeof previous === 'string') {
            this.element.removeAttribute('controller');
        }
    }
    /** Drive the element toward the committed class. Idempotent. */
    _apply(controllerClass) {
        const el = this.element;
        if (el[IS_ELEMENT_CLASS]) {
            if (el.controller !== controllerClass) {
                // Setter handles detach of the old controller and attach of the new.
                el.controller = controllerClass;
            }
            else {
                // Same value: the setter would no-op, but the element may have
                // self-detached on disconnect. attachController dedupes when the
                // controller is still attached.
                this._attach(controllerClass);
            }
            return;
        }
        if (this.element.localName.includes('-')) {
            // Custom element that hasn't upgraded yet (defined later, or cloned
            // into a detached fragment). Park the class — connectedCallback picks
            // it up on upgrade. Assigning the property now would shadow the future
            // prototype accessor with an own expando property.
            el[PENDING_CONTROLLER_BINDING] = controllerClass;
            return;
        }
        // Native element — attach directly. Mirror the observer path's ready shim.
        if (!el.ready)
            el.ready = Promise.resolve();
        this._attach(controllerClass);
    }
    _attach(controllerClass) {
        attachController(this.element, controllerClass).catch((error) => {
            if (error?.name === 'ControllerAttachAborted')
                return;
            console.error(`Failed to attach controller "${controllerClass?.name || controllerClass}":`, error);
        });
    }
    _detach() {
        detachController(this.element).catch((error) => {
            console.error('Failed to detach controller:', error);
        });
    }
    disconnected(_preserveEventListeners = false) {
        const committed = this._committedValue;
        if (typeof committed !== 'function')
            return;
        const el = this.element;
        if (el[PENDING_CONTROLLER_BINDING] !== undefined)
            return; // never attached
        if (el[IS_ELEMENT_CLASS])
            return; // snice elements detach themselves on disconnect
        this._detach();
    }
    reconnected() {
        const committed = this._committedValue;
        if (typeof committed !== 'function')
            return;
        this._apply(committed);
    }
    clear() {
        const el = this.element;
        if (el[PENDING_CONTROLLER_BINDING] !== undefined)
            delete el[PENDING_CONTROLLER_BINDING];
        const committed = this._committedValue;
        this._committedValue = NOT_COMMITTED;
        if (typeof committed !== 'function')
            return;
        if (el[IS_ELEMENT_CLASS])
            return; // the element's own teardown detaches
        this._detach();
    }
}
/**
 * BooleanAttributePart handles boolean attributes (?attribute=${value})
 */
class BooleanAttributePart extends Part {
    constructor(element, name) {
        super();
        this.type = 'boolean-attribute';
        this._committedValue = NOT_COMMITTED;
        this.element = element;
        this.name = name;
    }
    commit(value) {
        if (value === noChange)
            return;
        // Coerce to boolean
        const boolValue = !!value && value !== nothing;
        // Dirty check - skip if same boolean value
        if (boolValue === this._committedValue)
            return;
        this._committedValue = boolValue;
        if (boolValue) {
            this.element.setAttribute(this.name, '');
        }
        else {
            this.element.removeAttribute(this.name);
        }
    }
    clear() {
        this.element.removeAttribute(this.name);
        this._committedValue = NOT_COMMITTED;
    }
}
/**
 * EventPart handles event listener bindings with keyboard shortcut support
 */
class EventPart extends Part {
    constructor(element, eventName) {
        super();
        this.type = 'event';
        this.listener = null;
        this.listenerOptions = undefined;
        this.value = undefined;
        this.keyFilter = null;
        this.host = null; // Cache host element
        this.modifiers = new Set();
        this.once = false;
        this.onceConsumed = false;
        this.element = element;
        const [eventSpec, ...modifierSpecs] = eventName.split('|');
        const aliases = {
            preventdefault: 'prevent',
            stoppropagation: 'stop',
            stopimmediatepropagation: 'immediate'
        };
        const allowed = new Set(['prevent', 'stop', 'immediate', 'once', 'capture', 'passive', 'self']);
        for (const raw of modifierSpecs) {
            const normalized = aliases[raw.toLowerCase()] || raw.toLowerCase();
            if (!allowed.has(normalized)) {
                throw new Error(`snice: unknown event modifier "${raw}" in @${eventName}. ` +
                    'Supported modifiers: prevent, stop, immediate, once, capture, passive, self.');
            }
            this.modifiers.add(normalized);
        }
        if (this.modifiers.has('passive') && this.modifiers.has('prevent')) {
            throw new Error(`snice: @${eventName} cannot combine passive and prevent.`);
        }
        // Parse keyboard shortcuts:
        // Supports both dot notation (@keydown.enter) and colon notation (@keydown:Enter) to match @on decorator
        // Only parse colons for keyboard events, not custom events
        const isKeyboardEvent = ['keydown', 'keyup', 'keypress'].includes(eventSpec.split('.')[0].split(':')[0]);
        // Only keyboard events split on `.`/`:` into a key filter — a custom event
        // name may legitimately contain a dot (e.g. `app.ready`) and must be kept whole.
        const dotIndex = isKeyboardEvent ? eventSpec.indexOf('.') : -1;
        const colonIndex = isKeyboardEvent ? eventSpec.indexOf(':') : -1;
        // Use whichever delimiter comes first (dot or colon)
        const delimiterIndex = dotIndex > 0 && colonIndex > 0
            ? Math.min(dotIndex, colonIndex)
            : Math.max(dotIndex, colonIndex);
        if (delimiterIndex > 0) {
            const baseEvent = eventSpec.substring(0, delimiterIndex);
            const keySpec = eventSpec.substring(delimiterIndex + 1);
            this.eventName = baseEvent;
            this.keyFilter = parseKeyboardFilter(keySpec);
        }
        else {
            this.eventName = eventSpec;
            warnIfModifierMisuse(eventSpec);
        }
    }
    commit(value) {
        // Handle noChange sentinel
        if (value === noChange)
            return;
        // Handle nothing sentinel - remove listener
        if (value === nothing) {
            value = null;
        }
        const isListenerObject = !!value &&
            typeof value === 'object' &&
            typeof value.handleEvent === 'function';
        if (value !== null && value !== undefined && value !== false &&
            typeof value !== 'function' && !isListenerObject) {
            throw new TypeError(`snice: @${this.eventName} expects a function, EventListenerObject, or null.`);
        }
        if (isListenerObject && this.modifiers.has('prevent') && !!value.passive) {
            throw new Error(`snice: @${this.eventName} cannot combine a passive listener object with prevent.`);
        }
        // Skip if same value (but null/undefined always triggers update for cleanup)
        if (value === this.value && value !== null && value !== undefined)
            return;
        // Remove old listener (with the same capture option it was added with)
        if (this.listener) {
            this.element.removeEventListener(this.eventName, this.listener, this.listenerOptions);
            this.listener = null;
            this.listenerOptions = undefined;
        }
        // Add new listener
        if (value === null || value === undefined || value === false) {
            this.value = value;
            this.onceConsumed = false;
            return;
        }
        this.onceConsumed = false;
        try {
            this.attachListener(value, isListenerObject);
            this.value = value;
        }
        catch (error) {
            this.value = undefined;
            throw error;
        }
    }
    attachListener(value, isListenerObject) {
        // Accept plain functions AND EventListenerObject ({ handleEvent }). For
        // listener objects, the object itself doubles as the options bag.
        const keyFilter = this.keyFilter;
        const listener = ((event) => {
            if (this.modifiers.has('self') && event.target !== this.element)
                return;
            if (keyFilter && !matchesKeyboardFilter(event, keyFilter))
                return;
            if (this.once) {
                this.onceConsumed = true;
                this.element.removeEventListener(this.eventName, listener, listenerOptions);
                this.listener = null;
                this.listenerOptions = undefined;
            }
            if (this.modifiers.has('prevent'))
                event.preventDefault();
            if (this.modifiers.has('immediate'))
                event.stopImmediatePropagation();
            else if (this.modifiers.has('stop'))
                event.stopPropagation();
            if (isListenerObject) {
                // DOM spec: `this` inside handleEvent is the listener object itself
                value.handleEvent(event);
                return;
            }
            // Resolve the host (the custom element owning the shadow root) at dispatch
            // time, not bind time: a binding inside an initially-hidden <if>/<case>
            // branch commits while off-DOM, where getRootNode() has no host — caching
            // that null permanently would call the handler with `this = null` once the
            // branch is shown. By the time an event fires, the element is in the DOM.
            if (!this.host) {
                this.host = findRenderHost(this.element);
            }
            value.call(this.host || this.element, event);
        });
        const valueOptions = isListenerObject ? value : {};
        this.once = this.modifiers.has('once') || !!valueOptions.once;
        const listenerOptions = {
            capture: this.modifiers.has('capture') || valueOptions.capture,
            passive: this.modifiers.has('passive') || valueOptions.passive,
            signal: valueOptions.signal
        };
        this.element.addEventListener(this.eventName, listener, listenerOptions);
        this.listener = listener;
        this.listenerOptions = listenerOptions;
    }
    clear() {
        this.detachListener();
        this.value = undefined;
        this.host = null;
        this.onceConsumed = false;
    }
    detachListener() {
        if (this.listener) {
            this.element.removeEventListener(this.eventName, this.listener, this.listenerOptions);
            this.listener = null;
            this.listenerOptions = undefined;
        }
    }
    disconnected(preserveEventListeners = false) {
        if (!preserveEventListeners)
            this.detachListener();
        this.host = null;
    }
    reconnected() {
        if (!this.listener && !this.onceConsumed && this.value != null && this.value !== false) {
            const isListenerObject = typeof this.value === 'object';
            this.attachListener(this.value, isListenerObject);
        }
    }
}
/**
 * ConditionalIfPart handles <if> conditional rendering
 * Removes/inserts DOM nodes based on condition
 */
// Sentinel value to distinguish "not yet set" from undefined
const NOT_SET = Symbol('not-set');
class ConditionalIfPart extends Part {
    constructor(ifElement) {
        super();
        this.type = 'node';
        this.conditions = [NOT_SET];
        this.branches = [];
        this.defaultBranch = -1;
        this.currentBranch = -1;
        const parent = ifElement.parentNode;
        // Create comment boundary markers
        this.startNode = document.createComment('if');
        this.endNode = document.createComment('/if');
        // Insert markers where <if> is
        parent.insertBefore(this.startNode, ifElement);
        parent.insertBefore(this.endNode, ifElement.nextSibling);
        const primary = document.createDocumentFragment();
        this.branches.push(primary);
        let sawAlternative = false;
        let lastBranch = primary;
        for (const child of Array.from(ifElement.childNodes)) {
            const tag = child.nodeType === Node.ELEMENT_NODE
                ? child.tagName.toLowerCase()
                : '';
            if (tag === 'else-if' || tag === 'else') {
                sawAlternative = true;
                const wrapper = child;
                const fragment = document.createDocumentFragment();
                while (wrapper.firstChild)
                    fragment.appendChild(wrapper.firstChild);
                const index = this.branches.push(fragment) - 1;
                lastBranch = fragment;
                if (tag === 'else-if') {
                    if (this.defaultBranch !== -1) {
                        throw new Error('snice: <else> must be the final branch inside <if>.');
                    }
                    this.conditions[index] = NOT_SET;
                    conditionalElseIfOwners.set(wrapper, { coordinator: this, index });
                }
                else {
                    if (this.defaultBranch !== -1) {
                        throw new Error('snice: <if> may contain only one <else> branch.');
                    }
                    this.defaultBranch = index;
                }
                wrapper.remove();
                continue;
            }
            if (!sawAlternative) {
                primary.appendChild(child);
            }
            else if (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) {
                child.remove();
            }
            else {
                lastBranch.appendChild(child);
            }
        }
        parent.removeChild(ifElement);
    }
    commit(value) {
        if (value !== noChange)
            this.conditions[0] = value;
    }
    setAlternative(index, value) {
        if (value !== noChange)
            this.conditions[index] = value;
    }
    flush() {
        let next = -1;
        for (let i = 0; i < this.conditions.length; i++) {
            if (this.conditions[i] !== NOT_SET &&
                this.conditions[i] !== nothing &&
                Boolean(this.conditions[i])) {
                next = i;
                break;
            }
        }
        if (next === -1)
            next = this.defaultBranch;
        if (next === this.currentBranch)
            return;
        this.collectCurrent();
        this.currentBranch = next;
        if (next !== -1 && this.branches[next].hasChildNodes()) {
            this.startNode.parentNode?.insertBefore(this.branches[next], this.endNode);
        }
    }
    collectCurrent() {
        if (this.currentBranch === -1)
            return;
        const fragment = this.branches[this.currentBranch];
        let node = this.startNode.nextSibling;
        while (node && node !== this.endNode) {
            const next = node.nextSibling;
            fragment.appendChild(node);
            node = next;
        }
    }
    clear() {
        this.collectCurrent();
        this.currentBranch = -1;
    }
    get isConnected() {
        return this.startNode.isConnected;
    }
}
const conditionalElseIfOwners = new WeakMap();
class ConditionalElseIfPart extends Part {
    constructor(element) {
        super();
        this.type = 'node';
        const owner = conditionalElseIfOwners.get(element);
        if (!owner)
            throw new Error('snice: <else-if> must be a direct child of <if>.');
        this.coordinator = owner.coordinator;
        this.index = owner.index;
    }
    commit(value) {
        this.coordinator.setAlternative(this.index, value);
    }
    clear() { }
    get isConnected() {
        return this.coordinator.isConnected;
    }
}
/**
 * ConditionalCasePart handles <case>/<when>/<default> conditional rendering
 * Removes/inserts matching branch based on value
 */
class ConditionalCasePart extends Part {
    constructor(caseElement, dynamicWhenElements) {
        super();
        this.type = 'node';
        this.value = NOT_SET;
        this.branches = [];
        this.defaultBranch = -1;
        this.currentBranch = -1;
        const parent = caseElement.parentNode;
        // Create comment boundary markers
        this.startNode = document.createComment('case');
        this.endNode = document.createComment('/case');
        // Insert markers where <case> is
        parent.insertBefore(this.startNode, caseElement);
        parent.insertBefore(this.endNode, caseElement.nextSibling);
        // Extract branches from <when> and <default> children
        for (const child of Array.from(caseElement.children)) {
            const childTag = child.tagName.toLowerCase();
            if (childTag === 'when') {
                const fragment = document.createDocumentFragment();
                while (child.firstChild) {
                    fragment.appendChild(child.firstChild);
                }
                const dynamic = dynamicWhenElements.has(child);
                const index = this.branches.push({
                    fragment,
                    dynamic,
                    expected: dynamic ? NOT_SET : (child.getAttribute('value') ?? '')
                }) - 1;
                if (dynamic)
                    conditionalWhenOwners.set(child, { coordinator: this, index });
            }
            else if (childTag === 'default') {
                if (this.defaultBranch !== -1) {
                    throw new Error('snice: <case> may contain only one <default> branch.');
                }
                const fragment = document.createDocumentFragment();
                while (child.firstChild) {
                    fragment.appendChild(child.firstChild);
                }
                this.defaultBranch = this.branches.push({
                    fragment,
                    dynamic: false,
                    expected: NOT_SET
                }) - 1;
            }
        }
        // Remove the <case> element from DOM
        parent.removeChild(caseElement);
    }
    commit(value) {
        if (value !== noChange)
            this.value = value;
    }
    setExpected(index, value) {
        if (value !== noChange)
            this.branches[index].expected = value;
    }
    flush() {
        let next = this.branches.findIndex((branch, index) => {
            if (index === this.defaultBranch)
                return false;
            return branch.dynamic
                ? Object.is(this.value, branch.expected)
                : String(this.value) === branch.expected;
        });
        if (next === -1)
            next = this.defaultBranch;
        if (next === this.currentBranch)
            return;
        this.collectCurrent();
        this.currentBranch = next;
        if (next !== -1 && this.branches[next].fragment.hasChildNodes()) {
            this.startNode.parentNode?.insertBefore(this.branches[next].fragment, this.endNode);
        }
    }
    collectCurrent() {
        if (this.currentBranch === -1)
            return;
        const fragment = this.branches[this.currentBranch].fragment;
        let node = this.startNode.nextSibling;
        while (node && node !== this.endNode) {
            const next = node.nextSibling;
            fragment.appendChild(node);
            node = next;
        }
    }
    clear() {
        this.collectCurrent();
        this.currentBranch = -1;
    }
    get isConnected() {
        return this.startNode.isConnected;
    }
}
const conditionalWhenOwners = new WeakMap();
class ConditionalWhenPart extends Part {
    constructor(element) {
        super();
        this.type = 'node';
        const owner = conditionalWhenOwners.get(element);
        if (!owner)
            throw new Error('snice: dynamic <when> must be a direct child of <case>.');
        this.coordinator = owner.coordinator;
        this.index = owner.index;
    }
    commit(value) {
        this.coordinator.setExpected(this.index, value);
    }
    clear() { }
    get isConnected() {
        return this.coordinator.isConnected;
    }
}

const scheduledHosts = new WeakSet();
const processedCandidates = new WeakSet();
const focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    'summary',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]:not([tabindex="-1"])'
].join(',');
function hasMeaningfulFocus(document) {
    const active = document.activeElement;
    return Boolean(active && active !== document.body && active !== document.documentElement);
}
function isFocused(target) {
    const root = target.getRootNode();
    const active = root.activeElement;
    return active === target || Boolean(active && target.contains(active));
}
function tryFocus(target) {
    try {
        target.focus();
    }
    catch {
        return false;
    }
    return isFocused(target);
}
function explicitCandidates(root) {
    if (!root)
        return [];
    return Array.from(root.querySelectorAll('[autofocus]'))
        .filter(candidate => !processedCandidates.has(candidate));
}
function fallbackCandidates(root) {
    if (!root)
        return [];
    return Array.from(root.querySelectorAll(focusableSelector));
}
function hasCandidate(host) {
    if (host.hasAttribute('autofocus') && !processedCandidates.has(host))
        return true;
    return explicitCandidates(getRenderRoot(host)).length > 0;
}
/**
 * Complete native autofocus processing after a Snice element becomes usable.
 * Browsers do not consistently revisit late-upgraded hosts or autofocus
 * controls inserted into Shadow DOM, so Snice supplies that missing pass.
 */
function applyAutofocus(host) {
    if (!host.isConnected)
        return;
    const root = getRenderRoot(host);
    const candidates = [];
    if (host.hasAttribute('autofocus') && !processedCandidates.has(host))
        candidates.push(host);
    candidates.push(...explicitCandidates(root));
    if (candidates.length === 0)
        return;
    // Match native first-wins behavior and preserve focus deliberately
    // established by application code or an earlier autofocus candidate.
    if (hasMeaningfulFocus(host.ownerDocument)) {
        for (const candidate of candidates)
            processedCandidates.add(candidate);
        return;
    }
    for (const candidate of candidates) {
        processedCandidates.add(candidate);
        if (tryFocus(candidate))
            return;
        // A decorated custom-element host is not necessarily a focusable area.
        // If its public focus() has no effect, use its first native focus target
        // without adding tabindex or changing the host's accessibility semantics.
        if (candidate === host) {
            for (const fallback of fallbackCandidates(root)) {
                if (tryFocus(fallback))
                    return;
            }
        }
    }
}
/** Schedule one post-ready, post-paint autofocus pass for a Snice host. */
function scheduleAutofocus(host) {
    if (scheduledHosts.has(host) || !hasCandidate(host))
        return;
    scheduledHosts.add(host);
    const run = () => {
        const view = host.ownerDocument.defaultView;
        const apply = () => {
            scheduledHosts.delete(host);
            applyAutofocus(host);
        };
        if (view?.requestAnimationFrame)
            view.requestAnimationFrame(apply);
        else
            queueMicrotask(apply);
    };
    const ready = host.ready;
    Promise.resolve(ready).then(run, run);
}
/** Allow an explicitly removed/re-added host autofocus attribute to run again. */
function resetHostAutofocus(host) {
    processedCandidates.delete(host);
}

/**
 * @render and @styles decorators for Snice v3.0.0
 * Provides automatic differential rendering on property changes
 */
/**
 * When true, render errors are rethrown instead of logged, so tests and dev
 * environments fail loudly rather than leaving the element silently stale.
 * Production default is false (log and keep the previous DOM).
 */
let strictRenderErrors = false;
function setStrictRenderErrors(value) {
    strictRenderErrors = value;
}
/**
 * Ensure the element has a pending `rendered` promise. Called whenever a
 * render is requested; resolved by performRender once the render commits.
 */
function ensureRenderedPromise(element) {
    if (!element[RENDERED_RESOLVE]) {
        element[RENDERED_PROMISE] = new Promise((resolve) => {
            element[RENDERED_RESOLVE] = resolve;
        });
    }
}
/** Resolve the element's pending `rendered` promise, if any. */
function resolveRendered(element) {
    const resolve = element[RENDERED_RESOLVE];
    if (resolve) {
        element[RENDERED_RESOLVE] = null;
        resolve();
    }
}
/**
 * Global render scheduler for microtask batching
 * Batches multiple property changes into a single render
 */
class RenderScheduler {
    constructor() {
        this.pending = new Set();
        this.scheduled = false;
    }
    /**
     * Schedule an element for rendering
     * Batches renders in a microtask unless sync option is enabled
     */
    schedule(element, options) {
        // Sync rendering - execute immediately
        if (options.sync) {
            performRender(element, options);
            return;
        }
        // Async rendering - batch in microtask
        this.pending.add(element);
        if (!this.scheduled) {
            this.scheduled = true;
            queueMicrotask(() => this.flush());
        }
    }
    /**
     * Flush all pending renders
     */
    flush() {
        const elements = Array.from(this.pending);
        this.pending.clear();
        this.scheduled = false;
        for (const element of elements) {
            // Skip elements that were disconnected between scheduling and flush, but
            // remember the dropped render so reconnect can replay it — otherwise the
            // shadow DOM stays stale for a property changed while detached. The flag
            // lives on the element, so it's GC'd with it (no leak if never reattached).
            if (!element.isConnected) {
                element[PENDING_RECONNECT_RENDER] = true;
                continue;
            }
            const options = element[RENDER_OPTIONS] || {};
            performRender(element, options);
        }
    }
    /** Remove a disconnected element from the queue */
    remove(element) {
        this.pending.delete(element);
    }
}
const renderScheduler = new RenderScheduler();
function hasRendered(element) {
    return Object.prototype.hasOwnProperty.call(element, RENDER_INSTANCE);
}
function flushRenderCallbacks(element) {
    const callbacks = element[RENDER_CALLBACKS];
    if (!callbacks || callbacks.length === 0)
        return;
    const cbs = [...callbacks];
    element[RENDER_CALLBACKS] = [];
    cbs.forEach(cb => cb());
}
/**
 * Backstop against a render() that mutates an observed property, which makes
 * the property setter request another render from inside the current one. In
 * `sync` mode that re-enters performRender synchronously and, unchecked,
 * recurses to a stack overflow. The counter caps synchronous nesting and turns
 * a silent crash into an actionable error. Normal renders never nest (child
 * renders defer to a microtask), so this stays at depth 1 in the common case.
 *
 * The depth is tracked PER ELEMENT (on RENDER_DEPTH), not globally: a runaway
 * component must not consume a shared budget and reject an unrelated component
 * that happens to render inside its call stack.
 */
const MAX_RENDER_DEPTH = 50;
/**
 * Perform the actual render of an element
 */
function performRender(element, options, precomputedResult) {
    const capturedIdentity = captureRenderHostIdentity(element);
    let renderIdentity = capturedIdentity;
    const hasPrecomputedResult = arguments.length >= 3;
    const renderMethod = element[RENDER_METHOD];
    if (!renderMethod) {
        resolveRendered(element);
        return;
    }
    // If once is true and we've already rendered, skip
    if (options.once && hasRendered(element)) {
        resolveRendered(element);
        return;
    }
    const depth = (element[RENDER_DEPTH] ?? 0);
    if (depth >= MAX_RENDER_DEPTH) {
        const tag = element.tagName ? element.tagName.toLowerCase() : 'element';
        console.error(`snice: maximum render depth (${MAX_RENDER_DEPTH}) exceeded for <${tag}>. ` +
            `render() is mutating an observed property, causing an infinite render loop.`);
        resolveRendered(element);
        return;
    }
    element[RENDER_DEPTH] = depth + 1;
    try {
        const result = hasPrecomputedResult
            ? precomputedResult
            : renderMethod.call(element);
        const renderRoot = ensureRenderRoot(element);
        // Non-differential rendering (string)
        if (options.differential === false) {
            if (typeof result !== 'string') {
                console.warn('Render method with differential: false must return a string');
                return;
            }
            const template = document.createElement('template');
            template.innerHTML = result;
            const instance = element[RENDER_INSTANCE];
            instance?.clear();
            const removable = Array.from(renderRoot.childNodes).filter(node => !(node.nodeType === Node.ELEMENT_NODE &&
                node.tagName === 'STYLE' &&
                node.hasAttribute('data-snice-style')));
            for (const node of removable)
                node.remove();
            renderRoot.appendChild(template.content);
            // A present, null render instance records a successful string render so
            // `once` works without pretending the string tree is a TemplateInstance.
            element[RENDER_INSTANCE] = null;
            flushRenderCallbacks(element);
            scheduleAutofocus(element);
            return;
        }
        // Differential rendering (template)
        if (!isTemplateResult(result)) {
            console.warn('Render method must return html`` template result');
            return;
        }
        let instance = element[RENDER_INSTANCE];
        if (instance && instance.isSameTemplate(result.strings)) {
            renderIdentity = instance.getRenderHostIdentity() ?? capturedIdentity;
            instance.update(result.values);
            flushRenderCallbacks(element);
            scheduleAutofocus(element);
            return;
        }
        const customElementRegistry = renderRoot instanceof ShadowRoot
            ? renderRoot.customElementRegistry ?? renderRoot.ownerDocument.defaultView?.customElements ?? null
            : renderRoot.ownerDocument.defaultView?.customElements ?? null;
        const nextInstance = new TemplateInstance(result, capturedIdentity, customElementRegistry);
        const nextFragment = nextInstance.renderFragment();
        // Commit while detached first. A malformed binding can then fail without
        // exposing a half-updated tree. Connection-aware parts are paired with
        // reconnected() immediately after the successful mount.
        try {
            nextInstance.update(result.values);
        }
        catch (error) {
            nextInstance.clear();
            throw error;
        }
        // Park the previous tree without disposing it, mount the prepared tree,
        // then run connection hooks. If reconnecting a listener or async part
        // fails, restore the still-live previous tree instead of leaving a
        // partially mounted replacement behind.
        const previousFragment = document.createDocumentFragment();
        for (const child of Array.from(renderRoot.childNodes)) {
            if (child.nodeType === Node.ELEMENT_NODE &&
                child.tagName === 'STYLE' &&
                child.hasAttribute('data-snice-style'))
                continue;
            previousFragment.appendChild(child);
        }
        renderRoot.appendChild(nextFragment);
        try {
            if (element.isConnected)
                nextInstance.reconnected();
        }
        catch (error) {
            try {
                nextInstance.clear();
            }
            catch (cleanupError) {
                console.error('snice: failed to clean up a rejected render tree:', cleanupError);
            }
            for (const child of Array.from(renderRoot.childNodes)) {
                if (child.nodeType === Node.ELEMENT_NODE &&
                    child.tagName === 'STYLE' &&
                    child.hasAttribute('data-snice-style'))
                    continue;
                child.remove();
            }
            renderRoot.appendChild(previousFragment);
            throw error;
        }
        element[RENDER_INSTANCE] = nextInstance;
        let cleanupError;
        try {
            instance?.clear();
        }
        catch (error) {
            cleanupError = error;
        }
        flushRenderCallbacks(element);
        scheduleAutofocus(element);
        if (cleanupError)
            throw cleanupError;
    }
    catch (error) {
        const contextual = contextualizeRenderError(renderIdentity, error);
        if (strictRenderErrors)
            throw contextual;
        console.error('Error rendering element:', contextual);
    }
    finally {
        element[RENDER_DEPTH] = depth;
        resolveRendered(element);
    }
}
/** Commit immediately using the element's configured render options. */
function renderElementNow(element) {
    ensureRenderedPromise(element);
    const options = element[RENDER_OPTIONS] || {};
    performRender(element, { ...options, once: false });
}
/**
 * Request a render for an element
 * Respects debounce/throttle/once/sync options
 * @param immediate - Force immediate render (used for initial render)
 */
function requestRender(element, immediate = false) {
    const options = element[RENDER_OPTIONS] || {};
    // Handle once option
    if (options.once && hasRendered(element)) {
        if (element.isConnected)
            reconnectRenderTree(element);
        return;
    }
    // Arm the `rendered` promise: it resolves when this request's render
    // commits (immediately below for sync/immediate, later for batched/
    // debounced/throttled renders).
    ensureRenderedPromise(element);
    // Force immediate render (for initial render)
    if (immediate) {
        performRender(element, options);
        return;
    }
    // Handle debounce
    if (options.debounce !== undefined && options.debounce > 0) {
        if (!element[RENDER_TIMERS]) {
            element[RENDER_TIMERS] = {};
        }
        clearTimeout(element[RENDER_TIMERS].debounce);
        element[RENDER_TIMERS].debounce = setTimeout(() => {
            element[RENDER_TIMERS].debounce = null;
            renderScheduler.schedule(element, options);
        }, options.debounce);
        return;
    }
    // Handle throttle
    if (options.throttle !== undefined && options.throttle > 0) {
        if (!element[RENDER_TIMERS]) {
            element[RENDER_TIMERS] = { lastThrottle: 0 };
        }
        const timers = element[RENDER_TIMERS];
        const now = Date.now();
        if (timers.lastThrottle === 0 || now - timers.lastThrottle >= options.throttle) {
            timers.lastThrottle = now;
            renderScheduler.schedule(element, options);
            return;
        }
        if (!timers.throttleTimer) {
            const remaining = options.throttle - (now - timers.lastThrottle);
            timers.throttleTimer = setTimeout(() => {
                timers.throttleTimer = null;
                timers.lastThrottle = Date.now();
                renderScheduler.schedule(element, options);
            }, remaining);
        }
        return;
    }
    // Normal rendering (with microtask batching unless sync)
    renderScheduler.schedule(element, options);
}
/**
 * Clear any pending debounce/throttle render timers on an element (e.g. on
 * disconnect, so they don't fire on a dead element and retain it until they
 * expire). Returns true if a render was actually pending, so the caller can
 * arrange a replay on reconnect via PENDING_RECONNECT_RENDER.
 */
function clearRenderTimers(element) {
    const timers = element[RENDER_TIMERS];
    if (!timers)
        return false;
    let hadPending = false;
    if (timers.debounce) {
        clearTimeout(timers.debounce);
        timers.debounce = null;
        hadPending = true;
    }
    if (timers.throttleTimer) {
        clearTimeout(timers.throttleTimer);
        timers.throttleTimer = null;
        hadPending = true;
    }
    // Deliberately do NOT reset timers.lastThrottle: a plain DOM move is a
    // disconnect+reconnect, and zeroing the cooldown would let the next render
    // fire inside the throttle window. Preserving it keeps throttle honest
    // across moves; on a true disconnect the element is discarded anyway.
    return hadPending;
}
/** Pause connection-aware render parts while an element is detached. */
function disconnectRenderTree(element) {
    const instance = element[RENDER_INSTANCE];
    // A host disconnection is not the same thing as parking a conditional
    // branch. The shadow/light render tree still belongs to the host, so native
    // event listeners remain usable on retained node references. Async iteration
    // is paused, and parked branches continue to detach their listeners through
    // the ordinary Part lifecycle.
    instance?.disconnected(true);
}
/** Resume connection-aware render parts when an existing tree reconnects. */
function reconnectRenderTree(element) {
    const instance = element[RENDER_INSTANCE];
    instance?.reconnected();
}
/**
 * @render decorator for component rendering
 *
 * Marks a method as the render method for the component.
 * The method should return html`...` template.
 * Automatically re-renders when properties change (unless once: true).
 *
 * @example
 * ```typescript
 * @render()
 * renderContent() {
 *   return html`<div>${this.count}</div>`;
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Debounced rendering
 * @render({ debounce: 100 })
 * renderContent() {
 *   return html`<div>${this.searchTerm}</div>`;
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Render only once (manual re-renders only)
 * @render({ once: true })
 * renderContent() {
 *   return html`<div>Static content</div>`;
 * }
 * ```
 */
function render(options = {}) {
    return function (originalMethod, context) {
        context.name;
        context.addInitializer(function () {
            // Store the render method and options
            this[RENDER_METHOD] = originalMethod;
            this[RENDER_OPTIONS] = options;
        });
        // Return wrapped method that triggers re-render when called manually
        return function (...args) {
            // Call original method to get the template
            const result = originalMethod.apply(this, args);
            // Always render when method is called manually (even if once: true)
            // Force immediate render to bypass all options, pass precomputed result to avoid calling method twice
            const configured = this[RENDER_OPTIONS] || options;
            performRender(this, { ...configured, once: false }, result);
            return result;
        };
    };
}
/**
 * @styles decorator for component styles
 *
 * Marks a method as the styles method for the component.
 * The method should return css`...` template.
 * Styles are applied once when the component is connected.
 *
 * @example
 * ```typescript
 * @styles()
 * styles() {
 *   return css`:host { display: block; }`;
 * }
 * ```
 */
function styles() {
    return function (originalMethod, context) {
        context.addInitializer(function () {
            // Collect parent styles methods before overwriting with child's
            if (this[STYLES_METHOD] && !this[PARENT_STYLES_METHODS]) {
                this[PARENT_STYLES_METHODS] = [this[STYLES_METHOD]];
            }
            else if (this[STYLES_METHOD] && this[PARENT_STYLES_METHODS]) {
                this[PARENT_STYLES_METHODS].push(this[STYLES_METHOD]);
            }
            this[STYLES_METHOD] = originalMethod;
        });
        return originalMethod;
    };
}
/**
 * Apply styles to an element
 * Called during element initialization
 */
function applyStyles(element) {
    const stylesMethod = element[STYLES_METHOD];
    const staticResults = [];
    const constructors = [];
    let constructor = element.constructor;
    while (constructor && constructor !== HTMLElement && constructor !== Function.prototype) {
        constructors.unshift(constructor);
        constructor = Object.getPrototypeOf(constructor);
    }
    for (const current of constructors) {
        if (!Object.prototype.hasOwnProperty.call(current, 'styles'))
            continue;
        const value = current.styles;
        const values = Array.isArray(value) ? value : [value];
        for (const result of values) {
            if (isCSSResult(result))
                staticResults.push(result);
            else
                console.warn('Static styles must be a css`` result or an array of css`` results');
        }
    }
    if (!stylesMethod && staticResults.length === 0)
        return;
    // Only apply once
    if (element[STYLES_APPLIED])
        return;
    element[STYLES_APPLIED] = true;
    try {
        // Collect all CSS results: parent styles first, then child styles
        const allResults = [...staticResults];
        const parentMethods = element[PARENT_STYLES_METHODS];
        if (parentMethods) {
            for (const method of parentMethods) {
                const r = method.call(element);
                if (isCSSResult(r))
                    allResults.push(r);
            }
        }
        if (stylesMethod) {
            const result = stylesMethod.call(element);
            if (!isCSSResult(result)) {
                console.warn('Styles method must return css`` template result');
                return;
            }
            allResults.push(result);
        }
        const renderRoot = ensureRenderRoot(element);
        // Retain matching framework-owned style elements instead of adding a
        // duplicate stylesheet during a repeated setup pass.
        const existingStyles = Array.from(renderRoot.childNodes).filter(node => node.nodeType === Node.ELEMENT_NODE &&
            node.tagName === 'STYLE' &&
            node.hasAttribute('data-snice-style'));
        if (existingStyles.length === allResults.length &&
            existingStyles.every((style, index) => style.textContent === allResults[index].cssText)) {
            return;
        }
        for (const style of existingStyles)
            style.remove();
        // Prefer constructable stylesheets
        if (renderRoot instanceof ShadowRoot && allResults.every(r => !!r.styleSheet) && 'adoptedStyleSheets' in renderRoot) {
            try {
                renderRoot.adoptedStyleSheets = allResults.map(r => r.styleSheet);
                return;
            }
            catch {
                // A stylesheet constructed in another Window/Document realm cannot be
                // adopted here. Fall through to equivalent <style> elements.
            }
        }
        // Fallback — one <style> tag per stylesheet, preserving cascade order.
        // Marked so the template-switch cleanup keeps these (framework styles) but
        // still removes template-emitted <style> tags (which would otherwise pile up).
        for (const r of allResults) {
            const style = document.createElement('style');
            style.setAttribute('data-snice-style', '');
            style.textContent = r.cssText;
            renderRoot.appendChild(style);
        }
    }
    catch (error) {
        console.error('Error applying styles:', error);
    }
}

/**
 * Method decorators for common patterns
 * @debounce, @throttle, @once, @memoize
 */
const DEBOUNCE_TIMERS = getSymbol('debounce-timers');
const THROTTLE_TIMERS = getSymbol('throttle-timers');
const ONCE_CALLED = getSymbol('once-called');
const MEMOIZE_CACHE = getSymbol('memoize-cache');
/**
 * @debounce decorator - delays function execution until after wait time has elapsed
 * since the last invocation
 *
 * @param wait - Time to wait in milliseconds (default: 300)
 * @param options - Debounce options
 * @param options.leading - Invoke on the leading edge (default: false)
 * @param options.trailing - Invoke on the trailing edge (default: true)
 * @param options.maxWait - Maximum time to wait before invoking (default: undefined)
 *
 * @example
 * ```typescript
 * @element('search-input')
 * class SearchInput extends HTMLElement {
 *   @debounce(500)
 *   handleSearch(query: string) {
 *     // Only called 500ms after last keystroke
 *     fetch(`/api/search?q=${query}`);
 *   }
 * }
 * ```
 */
function debounce(wait = 300, options = {}) {
    const { leading = false, trailing = true, maxWait } = options;
    return function (originalMethod, context) {
        const methodName = context.name;
        return function (...args) {
            if (!this[DEBOUNCE_TIMERS]) {
                this[DEBOUNCE_TIMERS] = {};
            }
            const timers = this[DEBOUNCE_TIMERS];
            const timerKey = methodName;
            // Clear existing timer
            if (timers[timerKey]) {
                clearTimeout(timers[timerKey].timeout);
            }
            // Track when debounce started for maxWait
            const now = Date.now();
            const isFirstCall = !timers[timerKey];
            const startTime = isFirstCall ? now : timers[timerKey].startTime;
            // Check if maxWait exceeded
            const shouldInvokeFromMaxWait = maxWait !== undefined && now - startTime >= maxWait;
            // Leading edge invocation
            if (leading && isFirstCall) {
                const result = originalMethod.apply(this, args);
                timers[timerKey] = {
                    timeout: null,
                    startTime,
                    lastArgs: args,
                };
                return result;
            }
            // Set new timer for trailing edge
            const timeout = setTimeout(() => {
                if (trailing || shouldInvokeFromMaxWait) {
                    originalMethod.apply(this, timers[timerKey].lastArgs);
                }
                delete timers[timerKey];
            }, shouldInvokeFromMaxWait ? 0 : wait);
            timers[timerKey] = {
                timeout,
                startTime,
                lastArgs: args,
            };
        };
    };
}
/**
 * @throttle decorator - ensures function is called at most once per specified time period
 *
 * @param wait - Time to wait in milliseconds (default: 300)
 * @param options - Throttle options
 * @param options.leading - Invoke on the leading edge (default: true)
 * @param options.trailing - Invoke on the trailing edge (default: true)
 *
 * @example
 * ```typescript
 * @element('scroll-tracker')
 * class ScrollTracker extends HTMLElement {
 *   @throttle(100)
 *   handleScroll(e: Event) {
 *     // Called at most once every 100ms
 *     this.updateScrollPosition();
 *   }
 * }
 * ```
 */
function throttle(wait = 300, options = {}) {
    const { leading = true, trailing = true } = options;
    return function (originalMethod, context) {
        const methodName = context.name;
        return function (...args) {
            if (!this[THROTTLE_TIMERS]) {
                this[THROTTLE_TIMERS] = {};
            }
            const timers = this[THROTTLE_TIMERS];
            const timerKey = methodName;
            const now = Date.now();
            // First call
            if (!timers[timerKey]) {
                if (leading)
                    originalMethod.apply(this, args);
                timers[timerKey] = { lastInvoke: now, timeout: null, lastArgs: args };
                if (trailing && !leading) {
                    timers[timerKey].timeout = setTimeout(() => {
                        originalMethod.apply(this, timers[timerKey].lastArgs);
                        delete timers[timerKey];
                    }, wait);
                }
                return;
            }
            // Subsequent calls
            const timeSinceLastInvoke = now - timers[timerKey].lastInvoke;
            timers[timerKey].lastArgs = args;
            if (timers[timerKey].timeout) {
                clearTimeout(timers[timerKey].timeout);
            }
            // Enough time passed — invoke immediately
            if (timeSinceLastInvoke >= wait) {
                originalMethod.apply(this, args);
                timers[timerKey].lastInvoke = now;
                return;
            }
            // Set up trailing call
            if (trailing) {
                const remaining = wait - timeSinceLastInvoke;
                timers[timerKey].timeout = setTimeout(() => {
                    originalMethod.apply(this, timers[timerKey].lastArgs);
                    timers[timerKey].lastInvoke = Date.now();
                    timers[timerKey].timeout = null;
                }, remaining);
            }
        };
    };
}
/**
 * @once decorator - ensures function is only called once
 * Subsequent calls return the result of the first call
 *
 * @param perInstance - If true, function can be called once per instance (default: true)
 *                      If false, function can only be called once globally across all instances
 *
 * @example
 * ```typescript
 * @element('data-loader')
 * class DataLoader extends HTMLElement {
 *   @once()
 *   async loadData() {
 *     // Only loads data once, even if called multiple times
 *     const data = await fetch('/api/data');
 *     return data.json();
 *   }
 * }
 * ```
 */
function once(perInstance = true) {
    let globalCalled = false;
    let globalResult;
    return function (originalMethod, context) {
        const methodName = context.name;
        return function (...args) {
            // Per-instance tracking
            if (perInstance) {
                if (!this[ONCE_CALLED])
                    this[ONCE_CALLED] = {};
                if (!this[ONCE_CALLED][methodName]) {
                    this[ONCE_CALLED][methodName] = {
                        called: true,
                        result: originalMethod.apply(this, args),
                    };
                }
                return this[ONCE_CALLED][methodName].result;
            }
            // Global tracking
            if (!globalCalled) {
                globalCalled = true;
                globalResult = originalMethod.apply(this, args);
            }
            return globalResult;
        };
    };
}
/**
 * @memoize decorator - caches function results based on arguments
 * Uses JSON.stringify for argument comparison by default
 *
 * @param options - Memoization options
 * @param options.keyGenerator - Custom function to generate cache key from arguments
 * @param options.maxSize - Maximum cache size (default: 100)
 * @param options.ttl - Time to live in milliseconds (default: undefined - no expiration)
 *
 * @example
 * ```typescript
 * @element('calculator')
 * class Calculator extends HTMLElement {
 *   @memoize({ maxSize: 50 })
 *   fibonacci(n: number): number {
 *     // Results are cached, subsequent calls with same n are instant
 *     if (n <= 1) return n;
 *     return this.fibonacci(n - 1) + this.fibonacci(n - 2);
 *   }
 * }
 * ```
 */
function memoize(options = {}) {
    const { keyGenerator = (...args) => JSON.stringify(args), maxSize = 100, ttl } = options;
    return function (originalMethod, context) {
        const methodName = context.name;
        return function (...args) {
            if (!this[MEMOIZE_CACHE]) {
                this[MEMOIZE_CACHE] = {};
            }
            if (!this[MEMOIZE_CACHE][methodName]) {
                this[MEMOIZE_CACHE][methodName] = new Map();
            }
            const cache = this[MEMOIZE_CACHE][methodName];
            const key = keyGenerator(...args);
            // Check if cached
            if (cache.has(key)) {
                const cached = cache.get(key);
                const expired = ttl !== undefined && (Date.now() - cached.timestamp) > ttl;
                if (expired) {
                    cache.delete(key);
                }
                else {
                    return cached.value;
                }
            }
            // Compute result
            const result = originalMethod.apply(this, args);
            // Store in cache
            cache.set(key, {
                value: result,
                timestamp: Date.now(),
            });
            // Enforce max size (LRU - delete oldest)
            if (cache.size > maxSize) {
                const firstKey = cache.keys().next().value;
                cache.delete(firstKey);
            }
            return result;
        };
    };
}
/**
 * Clear all debounce timers for an instance
 * Useful in cleanup/disconnectedCallback
 */
function clearDebounceTimers(instance) {
    if (instance[DEBOUNCE_TIMERS]) {
        for (const timerKey in instance[DEBOUNCE_TIMERS]) {
            if (instance[DEBOUNCE_TIMERS][timerKey]?.timeout) {
                clearTimeout(instance[DEBOUNCE_TIMERS][timerKey].timeout);
            }
        }
        instance[DEBOUNCE_TIMERS] = {};
    }
}
/**
 * Clear all throttle timers for an instance
 * Useful in cleanup/disconnectedCallback
 */
function clearThrottleTimers(instance) {
    if (instance[THROTTLE_TIMERS]) {
        for (const timerKey in instance[THROTTLE_TIMERS]) {
            if (instance[THROTTLE_TIMERS][timerKey]?.timeout) {
                clearTimeout(instance[THROTTLE_TIMERS][timerKey].timeout);
            }
        }
        instance[THROTTLE_TIMERS] = {};
    }
}
/**
 * Clear memoize cache for an instance
 */
function clearMemoizeCache(instance, methodName) {
    if (instance[MEMOIZE_CACHE]) {
        if (methodName) {
            delete instance[MEMOIZE_CACHE][methodName];
        }
        else {
            instance[MEMOIZE_CACHE] = {};
        }
    }
}
/**
 * Reset once-called state for an instance
 */
function resetOnce(instance, methodName) {
    if (instance[ONCE_CALLED]) {
        if (methodName) {
            delete instance[ONCE_CALLED][methodName];
        }
        else {
            instance[ONCE_CALLED] = {};
        }
    }
}

function isPlainObject(value) {
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function descriptorsEqual(first, second) {
    if (!first || !second)
        return first === second;
    const firstIsData = 'value' in first || 'writable' in first;
    const secondIsData = 'value' in second || 'writable' in second;
    return firstIsData === secondIsData &&
        first.configurable === second.configurable &&
        first.enumerable === second.enumerable &&
        (!firstIsData || (first.writable === second.writable &&
            Object.is(first.value, second.value))) &&
        (firstIsData || (first.get === second.get &&
            first.set === second.set));
}
function invariantValue(target, property) {
    const descriptor = Reflect.getOwnPropertyDescriptor(target, property);
    if (!descriptor || descriptor.configurable)
        return { locked: false, value: undefined };
    if ('value' in descriptor && descriptor.writable === false) {
        return { locked: true, value: descriptor.value };
    }
    if (!('value' in descriptor) && descriptor.get === undefined) {
        return { locked: true, value: undefined };
    }
    return { locked: false, value: undefined };
}
/**
 * Wrap mutable collection/object state so nested writes can invalidate a
 * component without requiring a replacement assignment.
 */
function createDeepReactive(value, changed) {
    if (!value || typeof value !== 'object')
        return value;
    const cache = new WeakMap();
    const rawByProxy = new WeakMap();
    const unwrap = (input) => input && typeof input === 'object'
        ? rawByProxy.get(input) || input
        : input;
    const wrap = (input) => {
        if (!input || typeof input !== 'object')
            return input;
        if (cache.has(input))
            return cache.get(input);
        if (input instanceof Map) {
            let proxy;
            const methods = new Map();
            proxy = new Proxy(input, {
                get(target, property) {
                    const invariant = invariantValue(target, property);
                    if (invariant.locked)
                        return invariant.value;
                    if (methods.has(property))
                        return methods.get(property);
                    let method;
                    if (property === 'set')
                        method = (key, next) => {
                            key = unwrap(key);
                            next = unwrap(next);
                            const existed = target.has(key);
                            const previous = target.get(key);
                            target.set(key, next);
                            if (!existed || !Object.is(previous, next))
                                changed();
                            return proxy;
                        };
                    else if (property === 'delete')
                        method = (key) => {
                            key = unwrap(key);
                            const deleted = target.delete(key);
                            if (deleted)
                                changed();
                            return deleted;
                        };
                    else if (property === 'clear')
                        method = () => {
                            if (target.size === 0)
                                return;
                            target.clear();
                            changed();
                        };
                    else if (property === 'get')
                        method = (key) => wrap(target.get(unwrap(key)));
                    else if (property === 'has')
                        method = (key) => target.has(unwrap(key));
                    else if (property === 'forEach')
                        method = (callback, thisArg) => target.forEach((entryValue, key) => callback.call(thisArg, wrap(entryValue), wrap(key), proxy));
                    else if (property === 'values')
                        method = function* () {
                            for (const entryValue of target.values())
                                yield wrap(entryValue);
                        };
                    else if (property === 'keys')
                        method = function* () {
                            for (const key of target.keys())
                                yield wrap(key);
                        };
                    else if (property === 'entries' || property === Symbol.iterator)
                        method = function* () {
                            for (const [key, entryValue] of target.entries())
                                yield [wrap(key), wrap(entryValue)];
                        };
                    else {
                        const value = Reflect.get(target, property, target);
                        if (typeof value !== 'function')
                            return value;
                        if (property === 'constructor')
                            return value;
                        method = value.bind(target);
                    }
                    methods.set(property, method);
                    return method;
                }
            });
            cache.set(input, proxy);
            rawByProxy.set(proxy, input);
            return proxy;
        }
        if (input instanceof Set) {
            let proxy;
            const methods = new Map();
            proxy = new Proxy(input, {
                get(target, property) {
                    const invariant = invariantValue(target, property);
                    if (invariant.locked)
                        return invariant.value;
                    if (methods.has(property))
                        return methods.get(property);
                    let method;
                    if (property === 'add')
                        method = (entryValue) => {
                            entryValue = unwrap(entryValue);
                            const existed = target.has(entryValue);
                            target.add(entryValue);
                            if (!existed)
                                changed();
                            return proxy;
                        };
                    else if (property === 'delete')
                        method = (entryValue) => {
                            entryValue = unwrap(entryValue);
                            const deleted = target.delete(entryValue);
                            if (deleted)
                                changed();
                            return deleted;
                        };
                    else if (property === 'clear')
                        method = () => {
                            if (target.size === 0)
                                return;
                            target.clear();
                            changed();
                        };
                    else if (property === 'has')
                        method = (entryValue) => target.has(unwrap(entryValue));
                    else if (property === 'forEach')
                        method = (callback, thisArg) => target.forEach(entryValue => {
                            const wrapped = wrap(entryValue);
                            callback.call(thisArg, wrapped, wrapped, proxy);
                        });
                    else if (property === 'values' || property === 'keys' || property === Symbol.iterator)
                        method = function* () {
                            for (const entryValue of target.values())
                                yield wrap(entryValue);
                        };
                    else if (property === 'entries')
                        method = function* () {
                            for (const entryValue of target.values()) {
                                const wrapped = wrap(entryValue);
                                yield [wrapped, wrapped];
                            }
                        };
                    else {
                        const value = Reflect.get(target, property, target);
                        if (typeof value !== 'function')
                            return value;
                        if (property === 'constructor')
                            return value;
                        method = value.bind(target);
                    }
                    methods.set(property, method);
                    return method;
                }
            });
            cache.set(input, proxy);
            rawByProxy.set(proxy, input);
            return proxy;
        }
        if (!Array.isArray(input) && !isPlainObject(input))
            return input;
        const proxy = new Proxy(input, {
            get(target, property, receiver) {
                const invariant = invariantValue(target, property);
                return invariant.locked
                    ? invariant.value
                    : wrap(Reflect.get(target, property, receiver));
            },
            set(target, property, next, receiver) {
                const previous = Reflect.get(target, property, receiver);
                next = unwrap(next);
                // Write directly to the wrapped target. Passing the proxy as the
                // receiver re-enters defineProperty for an ordinary assignment and
                // would report the same mutation twice.
                const result = Reflect.set(target, property, next);
                if (result && !Object.is(previous, next))
                    changed();
                return result;
            },
            deleteProperty(target, property) {
                const existed = Object.prototype.hasOwnProperty.call(target, property);
                const result = Reflect.deleteProperty(target, property);
                if (result && existed)
                    changed();
                return result;
            },
            defineProperty(target, property, descriptor) {
                const previous = Reflect.getOwnPropertyDescriptor(target, property);
                const nextDescriptor = 'value' in descriptor
                    ? { ...descriptor, value: unwrap(descriptor.value) }
                    : descriptor;
                const result = Reflect.defineProperty(target, property, nextDescriptor);
                const current = result ? Reflect.getOwnPropertyDescriptor(target, property) : previous;
                if (result && !descriptorsEqual(previous, current))
                    changed();
                return result;
            }
        });
        cache.set(input, proxy);
        rawByProxy.set(proxy, input);
        return proxy;
    };
    return wrap(value);
}

/**
 * Applies core element functionality to a constructor
 * This is shared between @element and @page decorators
 */
function applyElementFunctionality(constructor) {
    // Mark as element class for channel decorator detection
    constructor.prototype[IS_ELEMENT_CLASS] = true;
    // Add controller property to all elements
    const originalConnectedCallback = constructor.prototype.connectedCallback;
    const originalDisconnectedCallback = constructor.prototype.disconnectedCallback;
    const originalAttributeChangedCallback = constructor.prototype.attributeChangedCallback;
    // Add framework-managed native/controller attributes and all reflected properties
    const observedAttributes = constructor.observedAttributes || [];
    if (!observedAttributes.includes('controller')) {
        observedAttributes.push('controller');
    }
    if (!observedAttributes.includes('autofocus')) {
        observedAttributes.push('autofocus');
    }
    // Add all properties to observed attributes (skip attribute: false)
    const properties = constructor[PROPERTIES];
    if (properties) {
        for (const [propName, propOptions] of properties) {
            if (propOptions.attribute === false)
                continue;
            const attributeName = getAttrName(propOptions, propName);
            if (!observedAttributes.includes(attributeName)) {
                observedAttributes.push(attributeName);
            }
        }
    }
    Object.defineProperty(constructor, 'observedAttributes', {
        get() { return observedAttributes; },
        configurable: true
    });
    // Add ready property - always returns a promise
    Object.defineProperty(constructor.prototype, 'ready', {
        get() {
            if (!this[READY_PROMISE]) {
                // Create a pending promise if not yet initialized
                this[READY_PROMISE] = new Promise((resolve, reject) => {
                    this[READY_RESOLVE] = resolve;
                    this[READY_REJECT] = reject;
                });
                // Preserve a rejected ready promise for explicit awaiters without
                // producing an unhandled rejection when nobody reads `.ready`.
                this[READY_PROMISE].catch(() => { });
            }
            return this[READY_PROMISE];
        },
        enumerable: true,
        configurable: true
    });
    // Add rendered property - resolves when the pending render (batched,
    // debounced, or throttled) has committed to the DOM; resolves
    // immediately when no render is pending.
    Object.defineProperty(constructor.prototype, 'rendered', {
        get() {
            return this[RENDERED_RESOLVE] ? this[RENDERED_PROMISE] : Promise.resolve();
        },
        enumerable: true,
        configurable: true
    });
    // Add controller property
    Object.defineProperty(constructor.prototype, 'controller', {
        get() {
            return this[CONTROLLER];
        },
        set(value) {
            const oldValue = this[CONTROLLER];
            this[CONTROLLER] = value;
            if (value === oldValue)
                return;
            if (value) {
                const label = typeof value === 'string' ? value : (value.name || '(anonymous controller class)');
                attachController(this, value).catch(error => {
                    // Detached before ready — designed teardown, not a failure
                    if (error?.name === 'ControllerAttachAborted') {
                        console.debug(`Controller "${label}" attach aborted (element detached before ready)`);
                        return;
                    }
                    // Registry misses only apply to string names — class references
                    // never go through the registry.
                    if (typeof value === 'string' && error?.message === `Controller "${value}" not found in registry`) {
                        deferControllerAttachment(this, value);
                        return;
                    }
                    console.error(`Failed to attach controller "${label}":`, error);
                });
                return;
            }
            if (oldValue) {
                detachController(this).catch(error => {
                    console.error(`Failed to detach controller:`, error);
                });
            }
        },
        enumerable: true,
        configurable: true
    });
    constructor.prototype.connectedCallback = async function () {
        // If ready promise was already created (controller attached before connected), use existing resolve
        // Otherwise create the ready promise now
        if (!this[READY_PROMISE]) {
            this[READY_PROMISE] = new Promise((resolve, reject) => {
                this[READY_RESOLVE] = resolve;
                this[READY_REJECT] = reject;
            });
            this[READY_PROMISE].catch(() => { });
        }
        // Only run initialization logic once, but re-establish handlers on reconnection
        if (this[INITIALIZED]) {
            try {
                reconnectRenderTree(this);
            }
            catch (error) {
                console.error(`Error reconnecting the render tree for ${this.tagName}:`, error);
            }
            // Re-establish handlers that get cleaned up on disconnect
            setupEventHandlers(this, this);
            setupResponseHandlers(this, this);
            void setupContextHandler(this, 'task');
            // Re-establish observers that get cleaned up on disconnect
            try {
                setupObservers(this, this);
            }
            catch (error) {
                console.error(`Error setting up observers for ${this.tagName} on reconnection:`, error);
            }
            // Call user's connectedCallback
            if (originalConnectedCallback) {
                originalConnectedCallback.call(this);
            }
            // Fire @reconnect handlers — for components that wire long-lived
            // global listeners or subscriptions in @ready and need to re-establish
            // them on re-connection. @ready does NOT re-fire (deliberately, to
            // preserve once-only initialization semantics).
            const reconnectHandlers = constructor[RECONNECT_HANDLERS];
            if (reconnectHandlers) {
                for (const handler of reconnectHandlers) {
                    try {
                        const result = handler.method.call(this);
                        if (result && typeof result.then === 'function') {
                            result.catch(error => {
                                console.error(`Error in @reconnect handler ${handler.methodName}:`, error);
                            });
                        }
                    }
                    catch (error) {
                        console.error(`Error in @reconnect handler ${handler.methodName}:`, error);
                    }
                }
            }
            // Replay a render that was dropped while disconnected. The scheduler
            // sets this flag when it skips a detached element, so a property changed
            // while detached still reaches the DOM on reattach. A plain move (no
            // pending render) leaves the flag unset, so we don't re-render needlessly.
            if (this[RENDER_METHOD] && this[PENDING_RECONNECT_RENDER]) {
                this[PENDING_RECONNECT_RENDER] = false;
                requestRender(this);
            }
            scheduleAutofocus(this);
            return;
        }
        // Mark that properties are being initialized from attributes
        // This allows property setters to work during initialization
        this[PROPERTIES_INITIALIZED] = true;
        // Initialize properties from attributes before rendering
        const properties = constructor[PROPERTIES];
        if (properties) {
            for (const [propName, propOptions] of properties) {
                if (propOptions.attribute === false)
                    continue;
                const attributeName = getAttrName(propOptions, propName);
                if (!this.hasAttribute(attributeName))
                    continue;
                const attrValue = this.getAttribute(attributeName);
                ensureSet(this, EXPLICITLY_SET_PROPERTIES).add(propName);
                if (propOptions.type === Boolean && attrValue === '') {
                    this.setAttribute(attributeName, 'true');
                }
                this[propName] = parseAttributeValue(attrValue, propOptions);
            }
        }
        // Clear pre-init values for properties that have HTML attributes
        if (this[PRE_INIT_PROPERTY_VALUES]) {
            for (const [propName, propValue] of Array.from(this[PRE_INIT_PROPERTY_VALUES].entries())) {
                const propOptions = properties?.get(propName);
                const attributeName = getAttrName(propOptions || {}, propName);
                this[PRE_INIT_PROPERTY_VALUES].delete(propName);
                if (!this.hasAttribute(attributeName)) {
                    this[propName] = propValue;
                }
            }
            delete this[PRE_INIT_PROPERTY_VALUES];
        }
        // Pick up a controller CLASS a template bound before this element was
        // defined. The binding parks the class instead of assigning the
        // property — a pre-upgrade assignment would have shadowed this accessor
        // with an own expando property. Attach directly rather than through the
        // `controller` accessor: some DOM implementations (happy-dom) invoke
        // connectedCallback on define without swapping the prototype, so the
        // accessor may not exist on this instance yet.
        if (this[PENDING_CONTROLLER_BINDING] !== undefined) {
            const pendingControllerClass = this[PENDING_CONTROLLER_BINDING];
            delete this[PENDING_CONTROLLER_BINDING];
            if (!this[CONTROLLER]) {
                this[CONTROLLER] = pendingControllerClass;
                const pendingLabel = pendingControllerClass?.name || '(anonymous controller class)';
                attachController(this, pendingControllerClass).catch((error) => {
                    if (error?.name === 'ControllerAttachAborted') {
                        console.debug(`Controller "${pendingLabel}" attach aborted (element detached before ready)`);
                        return;
                    }
                    console.error(`Failed to attach controller "${pendingLabel}":`, error);
                });
            }
        }
        // Pick up a `controller` attribute set before connection. Real browsers
        // fire attributeChangedCallback on connect for all observed attrs that
        // already existed; happy-dom doesn't always, so we read it proactively.
        if (this.hasAttribute('controller') && !this[CONTROLLER]) {
            const controllerName = this.getAttribute('controller');
            if (controllerName)
                this.controller = controllerName;
        }
        applyStyles(this);
        // Delegated @on handlers must bind inside the render root. Styles used
        // to create that root as a side effect, leaving styleless renderers to
        // bind on the host before their shadow root existed. Create only for
        // declarative renderers here so imperative elements that manage their
        // own shadow root keep their established lifecycle.
        if (this[RENDER_METHOD]) {
            try {
                ensureRenderRoot(this);
            }
            catch {
                // Defer invalid custom-root diagnostics to the normal render path,
                // where render errors are contextualized and reported consistently.
                // There is no usable root to bind delegated handlers to in this
                // invalid configuration, but the initial render below will surface
                // the author error without escaping connectedCallback.
            }
        }
        setupEventHandlers(this, this);
        setupResponseHandlers(this, this);
        void setupContextHandler(this, 'task');
        this[INITIALIZED] = true;
        // One-time (per class) sanity check: a @watch name with no matching
        // @property never fires — surface the typo instead of staying silent.
        validateWatchedProperties(this, constructor);
        // Now that the initial value is settled, give @watch immediate handlers
        // their one init call (before the first render, so derived state is ready).
        invokeImmediateWatchers(this, constructor);
        if (originalConnectedCallback) {
            originalConnectedCallback.call(this);
        }
        // v4.16.1: Render, run @ready handlers, THEN resolve .ready promise
        // This ensures `await el.ready` waits for both initial render AND all async @ready() methods
        //
        // We await a microtask boundary (to defer render for parent property bindings),
        // then run render + @ready handlers sequentially, then resolve .ready.
        const readyHandlers = constructor[READY_HANDLERS];
        // Await one microtask to defer initial render (allows parent property bindings)
        await new Promise(r => queueMicrotask(r));
        // If the element was removed during the microtask gap, don't render,
        // don't run @ready on an orphan. Resolve ready so any awaiters don't
        // hang — the element exists, it just isn't in the DOM.
        if (!this.isConnected) {
            if (this[READY_RESOLVE]) {
                this[READY_RESOLVE]();
                this[READY_RESOLVE] = null;
                this[READY_REJECT] = null;
            }
            return;
        }
        if (this[RENDER_METHOD]) {
            requestRender(this, true);
        }
        // Setup observers after render so shadow DOM content exists
        try {
            setupObservers(this, this);
        }
        catch (error) {
            console.error(`Error setting up observers for ${this.tagName}:`, error);
        }
        // Yield a microtask so child elements' microtask-deferred renders
        // complete before @ready handlers run. Children queued their render
        // microtask when they connected (earlier in the queue), so they
        // drain before this one.
        await new Promise(r => queueMicrotask(r));
        // Element may have been removed during the second microtask gap.
        if (!this.isConnected) {
            if (this[READY_RESOLVE]) {
                this[READY_RESOLVE]();
                this[READY_RESOLVE] = null;
                this[READY_REJECT] = null;
            }
            return;
        }
        // Run @ready handlers serially, awaiting each
        let readyFailed = false;
        let readyFailure;
        if (readyHandlers) {
            this[READY_HANDLERS_RUNNING] = true;
            try {
                for (const handler of readyHandlers) {
                    try {
                        await handler.method.call(this);
                    }
                    catch (error) {
                        console.error(`Error in @ready handler ${handler.methodName}:`, error);
                        if (!readyFailed) {
                            readyFailed = true;
                            readyFailure = error;
                        }
                    }
                }
            }
            finally {
                delete this[READY_HANDLERS_RUNNING];
            }
        }
        // NOW settle — render done AND every @ready handler completed. A failed
        // handler rejects the public signal instead of leaving a half-initialized
        // element indistinguishable from a successful one.
        if (readyFailed && this[READY_REJECT]) {
            this[READY_REJECT](readyFailure);
            this[READY_RESOLVE] = null;
            this[READY_REJECT] = null;
        }
        else if (this[READY_RESOLVE]) {
            this[READY_RESOLVE]();
            this[READY_RESOLVE] = null;
            this[READY_REJECT] = null;
        }
        scheduleAutofocus(this);
    };
    constructor.prototype.disconnectedCallback = async function () {
        // Invalidate @dispatch work queued or started before disconnect synchronously.
        // The platform does not await disconnectedCallback, and @dispose may be
        // async, so waiting until the end permits events from a detached host.
        clearDispatchTimers(this);
        try {
            disconnectRenderTree(this);
        }
        catch (error) {
            console.error(`Error disconnecting the render tree for ${this.tagName}:`, error);
        }
        // Call @dispose handlers
        const disposeHandlers = constructor[DISPOSE_HANDLERS];
        if (disposeHandlers) {
            for (const handler of disposeHandlers) {
                try {
                    await handler.method.call(this);
                }
                catch (error) {
                    console.error(`Error in @dispose handler ${handler.methodName}:`, error);
                }
            }
        }
        // Call original user-defined disconnectedCallback
        if (originalDisconnectedCallback) {
            originalDisconnectedCallback.call(this);
        }
        if (this[CONTROLLER]) {
            detachController(this).catch(error => {
                console.error(`Failed to detach controller:`, error);
            });
        }
        // Cleanup @on event handlers (v2.5.4 compatibility restored!)
        cleanupEventHandlers(this);
        // Cleanup @respond handlers
        cleanupResponseHandlers(this);
        // Cleanup @context handler
        cleanupContextHandler(this);
        // Cleanup @observe observers
        cleanupObservers(this);
        // Cleanup pending @debounce / @throttle timers so they don't fire on a dead element
        clearDebounceTimers(this);
        clearThrottleTimers(this);
        // The render/dispatch debounce-throttle timers live in separate stores.
        // A pending render is replayed on reconnect (same flag the scheduler
        // uses); a pending dispatch is a one-shot signal, so it's dropped.
        if (clearRenderTimers(this)) {
            this[PENDING_RECONNECT_RENDER] = true;
        }
        // @moved / @adopted debounce-throttle timers, likewise dropped so they
        // don't fire on a dead element.
        clearLifecycleTimers(this, MOVED_TIMERS);
        clearLifecycleTimers(this, ADOPTED_TIMERS);
    };
    constructor.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
        originalAttributeChangedCallback?.call(this, name, oldValue, newValue);
        if (name === 'controller') {
            // Parsing a dynamic controller binding briefly creates this marker
            // attribute on the inert template element. It is renderer metadata,
            // not a registry name, so it must never start an attachment.
            if (hasTemplateMarker(newValue))
                return;
            // A class attachment reflects its decorator name for DOM diagnostics,
            // but the class reference remains the only attachment authority.
            if (this[CONTROLLER_ATTRIBUTE_SYNC])
                return;
            if (this[DIRECT_CONTROLLER]) {
                restoreDirectControllerAttribute(this);
                return;
            }
            this.controller = newValue;
            return;
        }
        if (name === 'autofocus') {
            if (newValue === null)
                resetHostAutofocus(this);
            else if (this[INITIALIZED])
                scheduleAutofocus(this);
        }
        const properties = constructor[PROPERTIES];
        if (!properties)
            return;
        for (const [propName, propOptions] of properties) {
            const attributeName = getAttrName(propOptions, propName);
            if (attributeName.toLowerCase() !== name.toLowerCase())
                continue;
            // A property-originated reflection already stored the authoritative
            // JS value. Do not parse the serialized attribute back into a clone.
            if (this[SETTING_FROM_PROPERTY]?.has(name.toLowerCase()))
                break;
            const currentValue = this[PROPERTY_VALUES]?.[propName];
            // A removed attribute reverts the property to its field default. Read it
            // back through the getter (attribute is already gone at this point) so the
            // watcher's newValue and PROPERTY_VALUES match what this[propName] now
            // returns — parseAttributeValue(null) would diverge to null for String/Number.
            const defaultValue = this[PROPERTY_DEFAULTS]?.[propName];
            let parsedValue = newValue === null
                ? (propOptions.type === Boolean ? false : defaultValue)
                : parseAttributeValue(newValue, propOptions, currentValue, defaultValue);
            const wrap = this[PROPERTY_WRAPPERS]?.[propName];
            if (wrap)
                parsedValue = wrap(parsedValue);
            const changed = propOptions?.hasChanged
                ? propOptions.hasChanged(parsedValue, currentValue)
                : notEqual(parsedValue, currentValue);
            if (!changed)
                break;
            ensureSet(this, EXPLICITLY_SET_PROPERTIES).add(propName);
            ensureObj(this, PROPERTY_VALUES)[propName] = parsedValue;
            // Watchers react to changes only. During upgrade the initial value
            // is not a change, so suppress until INITIALIZED; @watch immediate
            // handlers get their one init call from invokeImmediateWatchers.
            if (this[INITIALIZED]) {
                invokeWatchers(this, constructor, propName, currentValue, parsedValue);
            }
            if (this[RENDER_METHOD] && this[INITIALIZED]) {
                requestRender(this);
            }
            break;
        }
    };
    // Add connectedMoveCallback for handling DOM moves
    constructor.prototype.connectedMoveCallback = async function () {
        // Call @moved handlers via the prototype method (the debounce/throttle
        // wrapper), not the raw handler — otherwise @moved({debounce}) options
        // are ignored for real moves and only work when called directly.
        const movedHandlers = constructor[MOVED_HANDLERS];
        if (movedHandlers) {
            for (const handler of movedHandlers) {
                try {
                    await this[handler.methodName]();
                }
                catch (error) {
                    console.error(`Error in @moved handler ${handler.methodName}:`, error);
                }
            }
        }
    };
    // Add adoptedCallback for handling document adoption
    constructor.prototype.adoptedCallback = async function () {
        // Call @adopted handlers via the prototype method (the debounce/throttle
        // wrapper), not the raw handler — same reason as connectedMoveCallback.
        const adoptedHandlers = constructor[ADOPTED_HANDLERS];
        if (adoptedHandlers) {
            for (const handler of adoptedHandlers) {
                try {
                    await this[handler.methodName]();
                }
                catch (error) {
                    console.error(`Error in @adopted handler ${handler.methodName}:`, error);
                }
            }
        }
    };
}
/**
 * Walk the prototype chain and merge parent element metadata into the child.
 * Called once at class definition time — zero per-instance cost.
 * Skips plain HTMLElement (no metadata to merge).
 *
 * Only merges PROPERTIES (stored via context.metadata at decoration time)
 * and formAssociated. Other handler registrations (@watch, @on, @ready, etc.)
 * inherit automatically via TC39 addInitializer — parent initializers run
 * during child instance construction.
 */
function mergeParentMetadata(constructor) {
    let parent = Object.getPrototypeOf(constructor);
    // Collect ancestors bottom-up, then merge top-down so the deepest parent goes first
    const ancestors = [];
    while (parent && parent !== HTMLElement && parent !== Function.prototype) {
        ancestors.push(parent);
        parent = Object.getPrototypeOf(parent);
    }
    ancestors.reverse();
    for (const ancestor of ancestors) {
        // Properties (Map) — parent first, child overrides
        if (ancestor[PROPERTIES]) {
            if (!constructor[PROPERTIES])
                constructor[PROPERTIES] = new Map();
            for (const [key, value] of ancestor[PROPERTIES]) {
                if (!constructor[PROPERTIES].has(key)) {
                    constructor[PROPERTIES].set(key, value);
                }
            }
        }
        // formAssociated — inherit if parent is form-associated
        if (ancestor.formAssociated && !constructor.formAssociated) {
            constructor.formAssociated = true;
        }
    }
}
function defineElement(tagName, constructor, context, options) {
    // Merge metadata from parent @element classes (inheritance support)
    mergeParentMetadata(constructor);
    if (context.metadata && context.metadata[PROPERTIES]) {
        if (!constructor[PROPERTIES])
            constructor[PROPERTIES] = new Map();
        for (const [key, value] of context.metadata[PROPERTIES]) {
            constructor[PROPERTIES].set(key, value);
        }
    }
    else if (_pendingProperties.length > 0) {
        // Symbol.metadata unavailable — drain the pending stack
        if (!constructor[PROPERTIES])
            constructor[PROPERTIES] = new Map();
        const pending = _pendingProperties.pop();
        for (const [key, value] of pending) {
            constructor[PROPERTIES].set(key, value);
        }
    }
    if (options?.formAssociated)
        constructor.formAssociated = true;
    const inheritedOptions = {
        ...(Object.getPrototypeOf(constructor)?.[ELEMENT_OPTIONS] || {})
    };
    const ownOptions = options || {};
    if (ownOptions.renderRoot === 'light' && typeof ownOptions.shadow === 'string' ||
        ownOptions.renderRoot === 'shadow' && ownOptions.shadow === false) {
        throw new TypeError('snice: @element renderRoot and shadow options select conflicting render roots.');
    }
    const mergedOptions = { ...inheritedOptions, ...ownOptions };
    if (Object.prototype.hasOwnProperty.call(ownOptions, 'shadow')) {
        mergedOptions.renderRoot = ownOptions.shadow === false ? 'light' : 'shadow';
    }
    else if (Object.prototype.hasOwnProperty.call(ownOptions, 'renderRoot')) {
        if (ownOptions.renderRoot === 'light')
            mergedOptions.shadow = false;
        else if (mergedOptions.shadow === false)
            mergedOptions.shadow = 'open';
    }
    constructor[ELEMENT_OPTIONS] = mergedOptions;
    if (constructor.prototype[SNICE_ELEMENT_BASE] === true &&
        Object.prototype.hasOwnProperty.call(constructor.prototype, 'render') &&
        !Object.prototype.hasOwnProperty.call(constructor.prototype, RENDER_METHOD) &&
        typeof constructor.prototype.render === 'function') {
        constructor.prototype[RENDER_METHOD] = constructor.prototype.render;
        constructor.prototype[RENDER_OPTIONS] = constructor.renderOptions || {};
    }
    applyElementFunctionality(constructor);
    const existing = customElements.get(tagName);
    if (existing) {
        if (existing === constructor)
            registerRenderHostIdentity(constructor, tagName);
        if (globalThis.SNICE_DEBUG)
            console.warn(`[snice] "${tagName}" is already registered. Skipping.`);
        return constructor;
    }
    customElements.define(tagName, constructor);
    registerRenderHostIdentity(constructor, tagName);
    return constructor;
}
function element(tagName, options) {
    if (options?.renderRoot !== undefined && options.renderRoot !== 'shadow' && options.renderRoot !== 'light') {
        throw new TypeError('snice: @element renderRoot must be "shadow" or "light".');
    }
    if (options?.shadow !== undefined && options.shadow !== false &&
        options.shadow !== 'open' && options.shadow !== 'closed') {
        throw new TypeError('snice: @element shadow must be "open", "closed", or false.');
    }
    return function (constructor, context) {
        return defineElement(tagName, constructor, context, options);
    };
}
function layout(tagName) {
    return function (constructor, context) {
        return defineElement(tagName, constructor, context);
    };
}
// Fallback stack for environments where Symbol.metadata is unavailable.
// @property pushes entries; @element pops them. Works because field decorators
// run synchronously before the class decorator in the same static block.
const _pendingProperties = [];
const nativeOwnPropertyDefaults = new WeakMap();
function isNativeOwnPropertyDefault(instance, propertyKey, value) {
    const ownerDocument = instance?.ownerDocument;
    if (!ownerDocument?.createElement)
        return false;
    let defaults = nativeOwnPropertyDefaults.get(ownerDocument);
    if (!defaults) {
        const nativeElement = ownerDocument.createElement('div');
        defaults = new Map();
        for (const key of Reflect.ownKeys(nativeElement))
            defaults.set(key, nativeElement[key]);
        nativeOwnPropertyDefaults.set(ownerDocument, defaults);
    }
    return defaults.has(propertyKey) && Object.is(defaults.get(propertyKey), value);
}
function property(options) {
    return function (_value, context) {
        const propertyKey = context.name;
        // Use metadata to store property information at decoration time
        if (context.metadata) {
            if (!context.metadata[PROPERTIES]) {
                context.metadata[PROPERTIES] = new Map();
            }
            context.metadata[PROPERTIES].set(propertyKey, options || {});
        }
        else {
            // Symbol.metadata unavailable — use the pending stack instead
            if (_pendingProperties.length === 0)
                _pendingProperties.push(new Map());
            _pendingProperties[_pendingProperties.length - 1].set(propertyKey, options || {});
        }
        return function (initialValue) {
            // A property binding can run while this is still an unupgraded custom
            // element, creating an own data property. Preserve that value across the
            // upgrade, but remove the expando before the decorated field initializer
            // is assigned so the assignment replays through the reactive accessor.
            const markedBindings = this[PRE_UPGRADE_PROPERTY_BINDINGS];
            const wasTemplateBoundBeforeUpgrade = markedBindings?.delete(propertyKey) === true;
            if (markedBindings?.size === 0)
                delete this[PRE_UPGRADE_PROPERTY_BINDINGS];
            const hasOwnValue = Object.prototype.hasOwnProperty.call(this, propertyKey);
            const hadPreUpgradeValue = hasOwnValue && (wasTemplateBoundBeforeUpgrade || !isNativeOwnPropertyDefault(this, propertyKey, this[propertyKey]));
            const preUpgradeValue = hadPreUpgradeValue ? this[propertyKey] : initialValue;
            if (hasOwnValue)
                delete this[propertyKey];
            const constructor = this.constructor;
            if (!constructor[PROPERTIES])
                constructor[PROPERTIES] = new Map();
            const finalOptions = { ...options };
            if (!finalOptions.type && initialValue !== undefined)
                finalOptions.type = detectType(initialValue);
            if (!finalOptions.attributeNaming && constructor.propertyAttributeNaming === 'kebab') {
                finalOptions.attributeNaming = 'kebab';
            }
            constructor[PROPERTIES].set(propertyKey, finalOptions);
            ensureObj(this, PROPERTY_DEFAULTS)[propertyKey] = initialValue;
            const notifyDeepMutation = () => {
                if (!this[PROPERTIES_INITIALIZED])
                    return;
                const current = this[PROPERTY_VALUES]?.[propertyKey];
                if (finalOptions.attribute !== false && finalOptions.reflect !== false) {
                    const attributeName = getAttrName(finalOptions, propertyKey);
                    const attributeValue = valueToAttribute(current, finalOptions, initialValue);
                    const attrKey = attributeName.toLowerCase();
                    ensureSet(this, SETTING_FROM_PROPERTY).add(attrKey);
                    try {
                        if (attributeValue === null)
                            this.removeAttribute?.(attributeName);
                        else
                            this.setAttribute?.(attributeName, attributeValue);
                    }
                    finally {
                        this[SETTING_FROM_PROPERTY].delete(attrKey);
                    }
                }
                if (this[INITIALIZED]) {
                    invokeWatchers(this, this.constructor, propertyKey, current, current);
                    if (this[RENDER_METHOD])
                        requestRender(this);
                }
            };
            const wrap = (value) => {
                if (!finalOptions.deep)
                    return value;
                let wrapped;
                wrapped = createDeepReactive(value, () => {
                    // A caller may retain an old proxied object after replacing the
                    // property. Mutating that detached graph must not invalidate the
                    // component or notify watchers for the current value.
                    if (this[PROPERTY_VALUES]?.[propertyKey] !== wrapped)
                        return;
                    notifyDeepMutation();
                });
                return wrapped;
            };
            ensureObj(this, PROPERTY_WRAPPERS)[propertyKey] = wrap;
            const proto = this.constructor.prototype;
            let definers = proto[PROPERTY_DEFINERS];
            if (!definers || !Object.prototype.hasOwnProperty.call(proto, PROPERTY_DEFINERS)) {
                definers = new Map(definers);
                proto[PROPERTY_DEFINERS] = definers;
            }
            const existingDefiner = definers.get(propertyKey);
            if (!existingDefiner || existingDefiner !== options) {
                definers.set(propertyKey, options);
                Object.defineProperty(this.constructor.prototype, propertyKey, {
                    get() {
                        if (this[PRE_INIT_PROPERTY_VALUES]?.has(propertyKey)) {
                            return this[PRE_INIT_PROPERTY_VALUES].get(propertyKey);
                        }
                        if (this[PROPERTY_VALUES] && propertyKey in this[PROPERTY_VALUES]) {
                            return this[PROPERTY_VALUES][propertyKey];
                        }
                        return initialValue;
                    },
                    set(incomingValue) {
                        const oldValue = this[propertyKey];
                        const changed = finalOptions.hasChanged
                            ? finalOptions.hasChanged(incomingValue, oldValue)
                            : notEqual(incomingValue, oldValue);
                        if (!changed)
                            return;
                        const instanceWrap = this[PROPERTY_WRAPPERS]?.[propertyKey] || ((value) => value);
                        // Property assignments are already typed JavaScript values and
                        // remain the source of truth. `type` and `converter.fromAttribute`
                        // apply only at the string attribute boundary; coercing here would
                        // destroy object identity and turn valid union values (for example
                        // `Date | ''` or `string | string[]`) into the wrong type.
                        const newValue = instanceWrap(incomingValue);
                        if (!this[PROPERTIES_INITIALIZED]) {
                            if (!this[PRE_INIT_PROPERTY_VALUES])
                                this[PRE_INIT_PROPERTY_VALUES] = new Map();
                            this[PRE_INIT_PROPERTY_VALUES].set(propertyKey, newValue);
                            return;
                        }
                        ensureObj(this, PROPERTY_VALUES)[propertyKey] = newValue;
                        if (finalOptions.attribute !== false && finalOptions.reflect !== false) {
                            const attributeName = getAttrName(finalOptions, propertyKey);
                            const attributeValue = valueToAttribute(newValue, finalOptions, this[PROPERTY_DEFAULTS]?.[propertyKey]);
                            ensureSet(this, EXPLICITLY_SET_PROPERTIES).add(propertyKey);
                            const attrKey = attributeName.toLowerCase();
                            ensureSet(this, SETTING_FROM_PROPERTY).add(attrKey);
                            try {
                                if (attributeValue === null)
                                    this.removeAttribute?.(attributeName);
                                else
                                    this.setAttribute?.(attributeName, attributeValue);
                            }
                            finally {
                                this[SETTING_FROM_PROPERTY].delete(attrKey);
                            }
                        }
                        if (this[INITIALIZED]) {
                            invokeWatchers(this, this.constructor, propertyKey, oldValue, newValue);
                            if (this[RENDER_METHOD])
                                requestRender(this);
                        }
                    },
                    configurable: true,
                    enumerable: true
                });
            }
            const wrappedInitialValue = wrap(initialValue);
            ensureObj(this, PROPERTY_VALUES)[propertyKey] = wrappedInitialValue;
            ensureObj(this, PROPERTY_DEFAULTS)[propertyKey] = wrappedInitialValue;
            return hadPreUpgradeValue ? preUpgradeValue : wrappedInitialValue;
        };
    };
}
/** Internal reactive state: never reads from or reflects to an attribute. */
function state(options = {}) {
    return property({ ...options, attribute: false, reflect: false });
}
function getQueryRoot(instance) {
    const isController = instance[IS_CONTROLLER_INSTANCE] === true;
    return isController && instance.element ? instance.element : instance;
}
function query(selector, options = {}) {
    return function (_value, context) {
        const { light = false, shadow = true } = options;
        const propertyKey = context.name;
        return function (initialValue) {
            if (!Object.hasOwn(this.constructor.prototype, propertyKey)) {
                Object.defineProperty(this.constructor.prototype, propertyKey, {
                    get() {
                        const root = getQueryRoot(this);
                        let result = null;
                        const managedRoot = root instanceof HTMLElement ? getRenderRoot(root) : null;
                        if (shadow && managedRoot)
                            result = managedRoot.querySelector(selector);
                        else if (shadow && root.shadowRoot)
                            result = root.shadowRoot.querySelector(selector);
                        if (!result && light)
                            result = root.querySelector(selector);
                        return result || null;
                    },
                    set() { },
                    configurable: true,
                    enumerable: true
                });
            }
            return initialValue;
        };
    };
}
function queryAll(selector, options = {}) {
    return function (_value, context) {
        const { light = false, shadow = true } = options;
        const propertyKey = context.name;
        return function (initialValue) {
            if (!Object.hasOwn(this.constructor.prototype, propertyKey)) {
                Object.defineProperty(this.constructor.prototype, propertyKey, {
                    get() {
                        const root = getQueryRoot(this);
                        const results = new Set();
                        const managedRoot = root instanceof HTMLElement ? getRenderRoot(root) : null;
                        if (shadow && managedRoot) {
                            for (const match of managedRoot.querySelectorAll(selector))
                                results.add(match);
                        }
                        else if (shadow && root.shadowRoot) {
                            for (const match of root.shadowRoot.querySelectorAll(selector))
                                results.add(match);
                        }
                        if (light) {
                            for (const match of root.querySelectorAll(selector))
                                results.add(match);
                        }
                        return [...results];
                    },
                    set() { },
                    configurable: true,
                    enumerable: true
                });
            }
            return initialValue;
        };
    };
}
function watch(...args) {
    // A trailing options object is separated from the watched property names.
    const propertyNames = args.filter((a) => typeof a === 'string');
    const options = args.find((a) => typeof a === 'object' && a !== null);
    // Fire on init by default; opt out of the init call with { immediate: false }.
    const immediate = options?.immediate !== false;
    return function (target, context) {
        const methodName = context.name;
        context.addInitializer(function () {
            const constructor = this.constructor;
            // Dedup by (method reference, propertyName) so that multiple @watch
            // decorators on the same method register for each key independently,
            // while still preventing duplicate registration when child classes
            // inherit/override a method with the same name. Use hasOwnProperty so
            // subclasses get their OWN Map — otherwise child pushes into the
            // parent's PROPERTY_WATCHERS via the prototype chain.
            if (!Object.prototype.hasOwnProperty.call(constructor, WATCH_METHODS)) {
                constructor[WATCH_METHODS] = new Map();
            }
            let registered = constructor[WATCH_METHODS].get(target);
            if (!registered) {
                registered = new Set();
                constructor[WATCH_METHODS].set(target, registered);
            }
            if (!Object.prototype.hasOwnProperty.call(constructor, PROPERTY_WATCHERS)) {
                // Deep-copy the inherited Map so the child starts with the parent's
                // watchers but pushing to a propertyName's array on the child doesn't
                // mutate the parent's array.
                const inherited = constructor[PROPERTY_WATCHERS];
                const copy = new Map();
                if (inherited) {
                    for (const [key, arr] of inherited)
                        copy.set(key, [...arr]);
                }
                constructor[PROPERTY_WATCHERS] = copy;
            }
            for (const propertyName of propertyNames) {
                if (registered.has(propertyName))
                    continue;
                registered.add(propertyName);
                if (!constructor[PROPERTY_WATCHERS].has(propertyName)) {
                    constructor[PROPERTY_WATCHERS].set(propertyName, []);
                }
                constructor[PROPERTY_WATCHERS].get(propertyName).push({
                    methodName,
                    method: target,
                    immediate
                });
            }
        });
    };
}
/**
 * Decorator that injects router context into a property
 * The context is automatically provided to page components by the router
 */
function context() {
    return function (_value, context) {
        const propertyKey = context.name;
        // Return a field initializer function for new decorators
        return function (initialValue) {
            // Set up the property descriptor on first access
            if (!Object.hasOwn(this.constructor.prototype, propertyKey)) {
                const descriptor = {
                    get() {
                        // Cached context
                        if (this[ROUTER_CONTEXT] !== undefined) {
                            return this[ROUTER_CONTEXT];
                        }
                        const resolved = getContext(this);
                        if (resolved === undefined)
                            return undefined;
                        // Explicit providers may be nested or released, so do not cache a
                        // dynamically resolved value. Router-owned context is injected on
                        // the instance and returned by the fast path above.
                        return resolved;
                    },
                    set() {
                        // Context is read-only
                    },
                    configurable: true,
                    enumerable: true
                };
                Object.defineProperty(this.constructor.prototype, propertyKey, descriptor);
            }
            return initialValue;
        };
    };
}
function registerHandler(handlersSymbol, methodsSymbol, target, context, extra) {
    const methodName = context.name;
    context.addInitializer(function () {
        const constructor = this.constructor;
        // hasOwnProperty guards so subclasses get their OWN Set/array instead of
        // mutating the parent's via the prototype chain.
        if (!Object.prototype.hasOwnProperty.call(constructor, methodsSymbol)) {
            constructor[methodsSymbol] = new Set();
        }
        if (constructor[methodsSymbol].has(target))
            return;
        constructor[methodsSymbol].add(target);
        if (!Object.prototype.hasOwnProperty.call(constructor, handlersSymbol)) {
            const inherited = constructor[handlersSymbol];
            constructor[handlersSymbol] = inherited ? [...inherited] : [];
        }
        constructor[handlersSymbol].push({ methodName, method: target, ...extra });
    });
}
function ready() {
    return function (target, context) {
        registerHandler(READY_HANDLERS, READY_METHODS, target, context);
    };
}
function dispose() {
    return function (target, context) {
        registerHandler(DISPOSE_HANDLERS, DISPOSE_METHODS, target, context);
    };
}
/**
 * Fires every time the element is connected AFTER the first connect.
 *
 * `@ready` only fires once (after the initial render). `@dispose` fires on
 * every disconnect. The gap between them is "what should run on a
 * reconnect?" For most components, framework-managed pieces (`@on`,
 * `@observe`, `@respond`, `@context`) are re-established automatically
 * and nothing else is needed.
 *
 * Use `@reconnect` only when the component wires its OWN long-lived global
 * subscription in `@ready` (e.g. a `document.addEventListener` for
 * outside-click detection) and tears it down in `@dispose`. The framework
 * doesn't track those, so they need a hook to re-attach on reconnect.
 *
 * Counterpart to `@dispose`. Symmetric.
 */
function reconnect() {
    return function (target, context) {
        registerHandler(RECONNECT_HANDLERS, RECONNECT_METHODS, target, context);
    };
}
/**
 * Clear any pending @moved / @adopted debounce/throttle timers on an instance
 * (e.g. on disconnect), so they don't fire on a dead element.
 */
function clearLifecycleTimers(instance, timersSymbol) {
    const map = instance[timersSymbol];
    if (!map)
        return;
    for (const t of map.values()) {
        if (t.debounceTimer)
            clearTimeout(t.debounceTimer);
        if (t.throttleTimer)
            clearTimeout(t.throttleTimer);
        t.debounceTimer = null;
        t.throttleTimer = null;
        t.lastThrottleCall = 0;
    }
}
function createLifecycleDecorator(handlersSymbol, timersSymbol, methodsSymbol) {
    return function (options = {}) {
        return function (originalMethod, context) {
            registerHandler(handlersSymbol, methodsSymbol, originalMethod, context, { options });
            const methodName = context.name;
            return function (...args) {
                if (!this[timersSymbol])
                    this[timersSymbol] = new Map();
                if (!this[timersSymbol].has(methodName)) {
                    this[timersSymbol].set(methodName, { throttleTimer: null, debounceTimer: null, lastThrottleCall: 0 });
                }
                const timers = this[timersSymbol].get(methodName);
                const exec = (...a) => originalMethod.apply(this, a);
                // Deferred (debounce/throttle) calls run inside a setTimeout, outside
                // the caller's try/catch — so catch their sync throws and async
                // rejections here and log, rather than surface an uncaught error.
                const execDeferred = (...a) => {
                    try {
                        const result = originalMethod.apply(this, a);
                        if (result && typeof result.then === 'function') {
                            result.catch((error) => console.error(`Error in deferred lifecycle handler ${methodName}:`, error));
                        }
                    }
                    catch (error) {
                        console.error(`Error in deferred lifecycle handler ${methodName}:`, error);
                    }
                };
                if (options.debounce > 0) {
                    clearTimeout(timers.debounceTimer);
                    timers.debounceTimer = setTimeout(() => execDeferred(...args), options.debounce);
                    return undefined;
                }
                if (options.throttle > 0) {
                    const now = Date.now();
                    if (timers.lastThrottleCall === 0 || now - timers.lastThrottleCall >= options.throttle) {
                        timers.lastThrottleCall = now;
                        return exec(...args);
                    }
                    if (!timers.throttleTimer) {
                        const remaining = options.throttle - (now - timers.lastThrottleCall);
                        timers.throttleTimer = setTimeout(() => {
                            timers.throttleTimer = null;
                            timers.lastThrottleCall = Date.now();
                            execDeferred(...args);
                        }, remaining);
                    }
                    return undefined;
                }
                return exec(...args);
            };
        };
    };
}
const moved = createLifecycleDecorator(MOVED_HANDLERS, MOVED_TIMERS, MOVED_METHODS);
const adopted = createLifecycleDecorator(ADOPTED_HANDLERS, ADOPTED_TIMERS, ADOPTED_METHODS);

/**
 * Optional authoring base for convention-driven Snice components.
 *
 * Decorators remain fully supported on plain HTMLElement subclasses. Extending
 * this class adds typed lifecycle promises, imperative invalidation, modern
 * kebab-case implicit attributes, and `render()` / static `styles` conventions.
 */
class SniceElement extends HTMLElement {
    static { this.propertyAttributeNaming = 'kebab'; }
    createRenderRoot() {
        const options = this.constructor[ELEMENT_OPTIONS];
        const mode = typeof options?.shadow === 'string' ? options.shadow : 'open';
        return this.attachShadow({ mode, delegatesFocus: options?.delegatesFocus });
    }
    invalidate() {
        requestRender(this);
        return this.rendered;
    }
    renderNow() {
        renderElementNow(this);
        return this.rendered;
    }
}
// `@element` uses this inherited marker to enable render() conventions only
// for the opt-in base class. Plain HTMLElement components may legitimately
// have imperative helper methods named render().
Object.defineProperty(SniceElement.prototype, SNICE_ELEMENT_BASE, { value: true });

/*!
 * pica-route v1.1.2
 * A pure URL pattern matching library compatible with route-parser API
 * (c) 2024
 * Released under the MIT License.
 */
class Route {
    constructor(pattern) {
        this.parsed = this.parsePattern(pattern);
    }
    get spec() {
        return this.parsed.template;
    }
    parsePattern(pattern) {
        // Handle optional query syntax (?query=:param)
        let pathPattern = pattern;
        let queryPattern = '';
        let queryOptional = false;
        const queryIndex = pattern.indexOf('?');
        if (queryIndex !== -1) {
            pathPattern = pattern.slice(0, queryIndex);
            queryPattern = pattern.slice(queryIndex + 1);
            // Check if query is wrapped in optional syntax (?...)
            if (pathPattern.endsWith('(') && queryPattern.endsWith(')')) {
                queryOptional = true;
                pathPattern = pathPattern.slice(0, -1);
                queryPattern = queryPattern.slice(0, -1);
            }
        }
        return {
            pathSegments: this.parseSegments(pathPattern),
            querySegments: queryPattern ? this.parseQuerySegments(queryPattern) : [],
            queryOptional,
            template: pattern
        };
    }
    parseSegments(pattern) {
        const segments = [];
        let i = 0;
        while (i < pattern.length) {
            const char = pattern[i];
            switch (char) {
                case '(':
                    // Parse optional group
                    const { segments: optionalSegments, endIndex } = this.parseOptionalGroup(pattern, i);
                    segments.push({ type: 'optional', segments: optionalSegments });
                    i = endIndex + 1;
                    break;
                case ':':
                    // Parse parameter
                    const { name, endIndex: paramEndIndex } = this.parseIdentifier(pattern, i + 1);
                    segments.push({ type: 'param', name });
                    i = paramEndIndex;
                    break;
                case '*':
                    // Parse splat
                    const { name: splatName, endIndex: splatEndIndex } = this.parseIdentifier(pattern, i + 1);
                    segments.push({ type: 'splat', name: splatName });
                    i = splatEndIndex;
                    break;
                default:
                    // Parse literal text
                    const { value, endIndex: literalEndIndex } = this.parseLiteral(pattern, i);
                    if (value) {
                        segments.push({ type: 'literal', value });
                    }
                    i = literalEndIndex;
                    break;
            }
        }
        return segments;
    }
    parseOptionalGroup(pattern, startIndex) {
        let depth = 1;
        let i = startIndex + 1;
        while (i < pattern.length && depth > 0) {
            if (pattern[i] === '(')
                depth++;
            if (pattern[i] === ')')
                depth--;
            i++;
        }
        const content = pattern.slice(startIndex + 1, i - 1);
        return {
            segments: this.parseSegments(content),
            endIndex: i - 1
        };
    }
    parseIdentifier(pattern, startIndex) {
        let i = startIndex;
        while (i < pattern.length && /[\w-]/.test(pattern[i])) {
            i++;
        }
        return {
            name: pattern.slice(startIndex, i),
            endIndex: i
        };
    }
    parseLiteral(pattern, startIndex) {
        let i = startIndex;
        while (i < pattern.length && !'(:*('.includes(pattern[i])) {
            i++;
        }
        return {
            value: pattern.slice(startIndex, i),
            endIndex: i
        };
    }
    parseQuerySegments(queryPattern) {
        // Remove optional group syntax wrapping the query
        let cleanPattern = queryPattern;
        if (cleanPattern.startsWith('(') && cleanPattern.endsWith(')')) {
            cleanPattern = cleanPattern.slice(1, -1);
        }
        const segments = [];
        let i = 0;
        while (i < cleanPattern.length) {
            switch (cleanPattern[i]) {
                case ':':
                    const { name, endIndex } = this.parseIdentifier(cleanPattern, i + 1);
                    segments.push({ type: 'param', name });
                    i = endIndex;
                    break;
                default:
                    const { value, endIndex: literalEndIndex } = this.parseQueryLiteral(cleanPattern, i);
                    if (value) {
                        segments.push({ type: 'literal', value });
                    }
                    i = literalEndIndex;
                    break;
            }
        }
        return segments;
    }
    parseQueryLiteral(pattern, startIndex) {
        let i = startIndex;
        while (i < pattern.length && pattern[i] !== ':') {
            i++;
        }
        return {
            value: pattern.slice(startIndex, i),
            endIndex: i
        };
    }
    getAllParamNames() {
        const paramNames = new Set();
        const collectParams = (segments) => {
            for (const segment of segments) {
                switch (segment.type) {
                    case 'param':
                    case 'splat':
                        paramNames.add(segment.name);
                        break;
                    case 'optional':
                        collectParams(segment.segments);
                        break;
                }
            }
        };
        collectParams(this.parsed.pathSegments);
        collectParams(this.parsed.querySegments);
        return Array.from(paramNames);
    }
    canMatchRemainingSegments(path, startIndex, segments) {
        // Quick check: try to match the remaining segments
        // This is a simplified check - we just look for literal segments that must exist
        let pathIndex = startIndex;
        for (const segment of segments) {
            if (segment.type === 'literal') {
                const literalIndex = path.indexOf(segment.value, pathIndex);
                if (literalIndex === -1) {
                    return false; // Required literal not found
                }
                pathIndex = literalIndex + segment.value.length;
            }
            // For optional, param, and splat segments, we're more permissive in this quick check
        }
        return true;
    }
    match(url) {
        const [pathPart, queryPart] = url.split('?');
        const pathParams = this.matchPath(pathPart, this.parsed.pathSegments);
        if (pathParams === false) {
            return false;
        }
        const queryParams = this.matchQuery(queryPart || '', this.parsed.querySegments);
        if (queryParams === false) {
            return false;
        }
        // Initialize result with all optional parameters set to undefined
        const allParams = this.getAllParamNames();
        const result = {};
        // Set all parameters to undefined initially
        for (const paramName of allParams) {
            result[paramName] = undefined;
        }
        // Override with actual matched values
        Object.assign(result, pathParams, queryParams);
        return result;
    }
    matchPath(path, segments) {
        const result = {};
        let pathIndex = 0;
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            const remainingSegments = segments.slice(i + 1);
            const matchResult = this.matchSegment(path, pathIndex, segment, remainingSegments);
            if (matchResult === false) {
                return false;
            }
            Object.assign(result, matchResult.params);
            pathIndex = matchResult.nextIndex;
        }
        // Check if we've consumed the entire path
        if (pathIndex < path.length) {
            return false;
        }
        return result;
    }
    matchSegment(path, startIndex, segment, remainingSegments = []) {
        switch (segment.type) {
            case 'literal':
                if (path.slice(startIndex, startIndex + segment.value.length) === segment.value) {
                    return { params: {}, nextIndex: startIndex + segment.value.length };
                }
                return false;
            case 'param': {
                let endIndex = startIndex;
                // Check if the next segment is a literal and find where it starts
                const nextSegment = remainingSegments[0];
                let stopChars = ['/', '?'];
                if (nextSegment && nextSegment.type === 'literal') {
                    // For parameters followed by literals, we need to find where the literal starts
                    const literalValue = nextSegment.value;
                    const literalStart = path.indexOf(literalValue, startIndex);
                    if (literalStart !== -1) {
                        endIndex = literalStart;
                    }
                    else {
                        // Literal not found, this parameter can't match
                        return false;
                    }
                }
                else {
                    // Default behavior - stop at / or ?
                    while (endIndex < path.length && !stopChars.includes(path[endIndex])) {
                        endIndex++;
                    }
                }
                if (endIndex === startIndex)
                    return false;
                let value;
                try {
                    value = decodeURIComponent(path.slice(startIndex, endIndex));
                }
                catch (e) {
                    // If decoding fails, use the original value
                    value = path.slice(startIndex, endIndex);
                }
                // If we hit a query string, we should reject this match for path parameters
                if (endIndex < path.length && path[endIndex] === '?' && remainingSegments.length > 0) {
                    return false;
                }
                // Keep values as strings to match route-parser behavior
                return {
                    params: { [segment.name]: value },
                    nextIndex: endIndex
                };
            }
            case 'splat': {
                // For splats, we need to find the optimal stopping point by looking ahead
                let endIndex = path.length;
                const nextSegment = remainingSegments[0];
                if (nextSegment) {
                    switch (nextSegment.type) {
                        case 'literal':
                            // For literal dots after splats, use first occurrence (for file extensions)
                            // For other literals, use last occurrence
                            if (nextSegment.value === '.') {
                                const firstDotIndex = path.indexOf('.', startIndex);
                                if (firstDotIndex !== -1) {
                                    endIndex = firstDotIndex;
                                }
                            }
                            else {
                                // Find the last occurrence of the next literal
                                const literalIndex = path.lastIndexOf(nextSegment.value);
                                if (literalIndex !== -1 && literalIndex >= startIndex) {
                                    endIndex = literalIndex;
                                }
                            }
                            break;
                        case 'param':
                            // For parameter after splat, find the last slash
                            let lastSlashIndex = -1;
                            for (let i = path.length - 1; i >= startIndex; i--) {
                                if (path[i] === '/') {
                                    lastSlashIndex = i;
                                    break;
                                }
                            }
                            if (lastSlashIndex !== -1) {
                                endIndex = lastSlashIndex;
                            }
                            break;
                        case 'splat':
                            // For consecutive splats, give this splat everything up to the last slash
                            // The next splat gets the last component
                            let lastSlash = -1;
                            for (let i = path.length - 1; i > startIndex; i--) {
                                if (path[i] === '/') {
                                    lastSlash = i + 1; // Include the slash
                                    break;
                                }
                            }
                            if (lastSlash !== -1) {
                                endIndex = lastSlash;
                            }
                            else {
                                // No slash found, this splat gets everything except the last component
                                const pathSegments = path.slice(startIndex).split('/').filter(s => s);
                                if (pathSegments.length > 1) {
                                    endIndex = startIndex + pathSegments.slice(0, -1).join('/').length + 1;
                                }
                                else {
                                    endIndex = startIndex; // Give nothing to this splat
                                }
                            }
                            break;
                        case 'optional':
                            // Look for literals inside optional groups
                            const findFirstLiteral = (segs) => {
                                for (const seg of segs) {
                                    if (seg.type === 'literal')
                                        return seg.value;
                                    if (seg.type === 'optional') {
                                        const nested = findFirstLiteral(seg.segments);
                                        if (nested)
                                            return nested;
                                    }
                                }
                                return null;
                            };
                            const firstLiteral = findFirstLiteral(nextSegment.segments);
                            if (firstLiteral) {
                                const literalIndex = path.lastIndexOf(firstLiteral);
                                if (literalIndex !== -1 && literalIndex >= startIndex) {
                                    endIndex = literalIndex;
                                }
                            }
                            break;
                    }
                }
                // Don't go past query parameters
                const queryIndex = path.indexOf('?', startIndex);
                if (queryIndex !== -1 && queryIndex < endIndex) {
                    endIndex = queryIndex;
                }
                let value;
                try {
                    value = decodeURIComponent(path.slice(startIndex, endIndex));
                }
                catch (e) {
                    // If decoding fails, use the original value
                    value = path.slice(startIndex, endIndex);
                }
                return {
                    params: { [segment.name]: value },
                    nextIndex: endIndex
                };
            }
            case 'optional': {
                // Try to match the optional segment first, with backtracking if needed
                let tempIndex = startIndex;
                let params = {};
                let allMatched = true;
                for (let i = 0; i < segment.segments.length; i++) {
                    const innerSegment = segment.segments[i];
                    const remainingInner = segment.segments.slice(i + 1);
                    const innerResult = this.matchSegment(path, tempIndex, innerSegment, remainingInner);
                    if (innerResult === false) {
                        allMatched = false;
                        break;
                    }
                    Object.assign(params, innerResult.params);
                    tempIndex = innerResult.nextIndex;
                }
                if (allMatched) {
                    // Check if the rest of the segments can still be matched
                    const canMatchRemaining = this.canMatchRemainingSegments(path, tempIndex, remainingSegments);
                    if (canMatchRemaining) {
                        return {
                            params,
                            nextIndex: tempIndex
                        };
                    }
                }
                // Optional segment either didn't match or would prevent remaining segments from matching
                // So skip it and return to original position
                return { params: {}, nextIndex: startIndex };
            }
            default:
                return false;
        }
    }
    findNextLiteral(segments) {
        for (const segment of segments) {
            if (segment.type === 'literal') {
                return segment;
            }
            else if (segment.type === 'optional') {
                const nested = this.findNextLiteral(segment.segments);
                if (nested)
                    return nested;
            }
        }
        return null;
    }
    matchQuery(query, segments) {
        if (segments.length === 0) {
            return {};
        }
        const result = {};
        let queryIndex = 0;
        for (const segment of segments) {
            switch (segment.type) {
                case 'literal':
                    if (query.slice(queryIndex, queryIndex + segment.value.length) === segment.value) {
                        queryIndex += segment.value.length;
                    }
                    else {
                        return false;
                    }
                    break;
                case 'param': {
                    let endIndex = queryIndex;
                    while (endIndex < query.length && query[endIndex] !== '&') {
                        endIndex++;
                    }
                    if (endIndex === queryIndex)
                        return false;
                    let value;
                    try {
                        value = decodeURIComponent(query.slice(queryIndex, endIndex));
                    }
                    catch (e) {
                        // If decoding fails, use the original value
                        value = query.slice(queryIndex, endIndex);
                    }
                    result[segment.name] = value;
                    queryIndex = endIndex;
                    break;
                }
            }
        }
        return result;
    }
    reverse(params = {}) {
        const pathResult = this.reverseSegments(this.parsed.pathSegments, params);
        if (pathResult === false) {
            return false;
        }
        let result = pathResult;
        if (this.parsed.querySegments.length > 0) {
            const queryResult = this.reverseSegments(this.parsed.querySegments, params);
            if (!this.parsed.queryOptional) {
                // Query is required - if it fails, the whole reverse fails
                if (queryResult === false) {
                    return false;
                }
            }
            // Add query string if we have a result
            if (queryResult && queryResult !== '') {
                result += '?' + queryResult;
            }
        }
        return result;
    }
    reverseSegments(segments, params, inActivatedStructure = false) {
        let result = '';
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            const remainingSegments = segments.slice(i + 1);
            switch (segment.type) {
                case 'literal':
                    result += segment.value;
                    break;
                case 'param':
                case 'splat':
                    if (segment.name in params) {
                        result += String(params[segment.name]);
                    }
                    else {
                        // Missing required parameter - but for query segments, this might be optional
                        return false;
                    }
                    break;
                case 'optional':
                    // Check if this optional group should be included
                    // considering dependencies from later optional groups
                    const shouldInclude = this.hasAllParams(segment.segments, params, inActivatedStructure) ||
                        this.hasLaterOptionalGroupNeedingParams(remainingSegments, params, segment.segments);
                    if (shouldInclude) {
                        const optionalResult = this.reverseSegments(segment.segments, params, true);
                        if (optionalResult === false) {
                            return false;
                        }
                        result += optionalResult;
                    }
                    break;
            }
        }
        return result;
    }
    // Check if any later optional groups need parameters and would require this group to be included
    hasLaterOptionalGroupNeedingParams(laterSegments, params, currentOptionalSegments) {
        // Check if any later optional group has parameters that are provided
        for (const segment of laterSegments) {
            if (segment.type === 'optional') {
                if (this.hasAnyParamInSegments(segment.segments, params)) {
                    // Check if the current optional group only has literals/no required params
                    // If so, it should be included as a bridge to the later group
                    return this.hasOnlyLiteralsAndOptionals(currentOptionalSegments);
                }
            }
        }
        return false;
    }
    hasOnlyLiteralsAndOptionals(segments) {
        for (const segment of segments) {
            if (segment.type === 'param' || segment.type === 'splat') {
                return false;
            }
        }
        return true;
    }
    hasAllParams(segments, params, inActivatedStructure = false) {
        // Check if we can include this optional group
        let hasDirectParams = false;
        let hasNestedParams = false;
        let missingDirectParam = false;
        let hasOnlyLiteralsAndOptionals = true;
        // First pass: check direct params and literals
        for (const segment of segments) {
            switch (segment.type) {
                case 'param':
                case 'splat':
                    hasOnlyLiteralsAndOptionals = false;
                    if (segment.name in params && params[segment.name] !== undefined) {
                        hasDirectParams = true;
                    }
                    else {
                        missingDirectParam = true;
                    }
                    break;
            }
        }
        // Determine if this level will be activated and propagate to nested
        const willBeActivated = (hasDirectParams && !missingDirectParam);
        const passToNested = inActivatedStructure || willBeActivated;
        // Second pass: check nested optional groups with correct context
        for (const segment of segments) {
            if (segment.type === 'optional') {
                const shouldIncludeNested = this.hasAllParams(segment.segments, params, passToNested);
                if (shouldIncludeNested) {
                    hasNestedParams = true;
                }
            }
        }
        // Include this optional group if:
        // 1. We have all direct parameters needed, OR
        // 2. We have nested parameters and no missing direct parameters, OR
        // 3. We have only literals/optionals and parent was activated
        if (hasDirectParams && !missingDirectParam) {
            return true; // We have all required params for this level
        }
        if (hasNestedParams && !missingDirectParam) {
            return true; // We need this group to reach nested parameters
        }
        // Include literal-only groups in nested structures
        if (hasOnlyLiteralsAndOptionals && inActivatedStructure) {
            return true; // Include literal paths in activated structures
        }
        return false;
    }
    hasOnlyOptionalMissingParams(segments, params) {
        // Check if all missing parameters are in nested optional groups
        for (const segment of segments) {
            switch (segment.type) {
                case 'param':
                case 'splat':
                    if (!(segment.name in params)) {
                        return false; // Has required missing param
                    }
                    break;
            }
        }
        return true;
    }
    hasAnyParamInSegments(segments, params) {
        for (const segment of segments) {
            switch (segment.type) {
                case 'param':
                case 'splat':
                    if (segment.name in params) {
                        return true;
                    }
                    break;
                case 'optional':
                    if (this.hasAnyParamInSegments(segment.segments, params)) {
                        return true;
                    }
                    break;
            }
        }
        return false;
    }
}

/**
 * Score a route pattern by specificity so that more specific routes are tried
 * first. Scoring is per-segment by ROLE, not by string length: a static segment
 * outweighs a dynamic (`:param`) segment, which outweighs a wildcard (`*` /
 * `*name`). So `/post/new` beats `/post/:id`, and a splat always ranks last —
 * regardless of how long the pattern string happens to be. This mirrors the
 * segment-scoring model mainstream routers (React Router, Remix) use.
 *
 * Sort callers descending by this score (highest = most specific = first).
 *
 * NOTE: mirrored in `src/react/route-specificity.ts` for the isolated React
 * bundle — keep the two in sync.
 */
const STATIC_SEGMENT = 10;
const DYNAMIC_SEGMENT = 3;
const WILDCARD_SEGMENT = -2;
function routeSpecificity(spec) {
    const segments = spec.split('/').filter(Boolean);
    // Base on segment count so a deeper path outranks a shallower one on ties.
    let score = segments.length;
    for (const segment of segments) {
        if (segment.startsWith('*')) {
            score += WILDCARD_SEGMENT;
        }
        else if (segment.startsWith(':')) {
            score += DYNAMIC_SEGMENT;
        }
        else {
            score += STATIC_SEGMENT;
        }
    }
    return score;
}

/**
 * Generic transition system for animating between elements
 */
/**
 * Parse CSS property string into an object
 */
function parseStyles(styleString) {
    const styles = {};
    styleString.split(';').forEach(rule => {
        const idx = rule.indexOf(':');
        if (idx === -1)
            return;
        const prop = rule.slice(0, idx).trim();
        const value = rule.slice(idx + 1).trim();
        if (prop && value) {
            styles[prop] = value;
        }
    });
    return styles;
}
/**
 * Perform a transition between two elements
 */
function applyBaseTransitionStyles(el, isSlotted, duration, prop) {
    el.style.position = 'absolute';
    el.style.width = '100%';
    if (isSlotted) {
        el.style.height = '100%';
        el.style.transition = `opacity ${duration}ms ease-in-out`;
        return;
    }
    el.style.top = '0';
    el.style.left = '0';
    el.style.transition = `${prop} ${duration}ms ease-in-out`;
}
function resetTransitionStyles(el, isSlotted) {
    el.style.position = '';
    el.style.width = '';
    el.style.transition = '';
    if (isSlotted) {
        el.style.height = '';
        return;
    }
    el.style.top = '';
    el.style.left = '';
}
async function performTransition(container, oldElement, newElement, transition = {}, 
/**
 * Called at each async boundary — if it returns true, this transition has
 * been superseded by a newer navigation. We abort cleanup so we don't tear
 * down elements now owned by the newer transition.
 */
isStale = () => false) {
    const outDuration = transition.outDuration ?? 300;
    const inDuration = transition.inDuration ?? 300;
    const mode = (transition.mode || 'sequential');
    const outStyles = transition.out ? parseStyles(transition.out) : { opacity: '0' };
    const inStartStyles = { opacity: '0' };
    const inEndStyles = transition.in ? parseStyles(transition.in) : { opacity: '1' };
    const containerStyle = container.style;
    const isLayoutElement = container.tagName.includes('-') && container.shadowRoot;
    // Track the position we were ORIGINALLY given, not what a concurrent
    // transition may have already written. The first transition to touch this
    // container wins; subsequent overlapping transitions reuse the same value
    // and the last one to finish restores it.
    const POS_KEY = Symbol.for('snice:transition-original-position');
    const POS_COUNT = Symbol.for('snice:transition-count');
    const c = container;
    if (!isLayoutElement) {
        if (!(POS_KEY in c)) {
            c[POS_KEY] = containerStyle.position;
            c[POS_COUNT] = 0;
        }
        c[POS_COUNT]++;
        containerStyle.position = 'relative';
    }
    const restorePosition = () => {
        if (isLayoutElement)
            return;
        c[POS_COUNT]--;
        if (c[POS_COUNT] <= 0) {
            containerStyle.position = c[POS_KEY];
            delete c[POS_KEY];
            delete c[POS_COUNT];
        }
    };
    const isSlotted = oldElement.hasAttribute('slot') || newElement.hasAttribute('slot');
    applyBaseTransitionStyles(oldElement, isSlotted, outDuration, 'all');
    applyBaseTransitionStyles(newElement, isSlotted, inDuration, 'all');
    Object.assign(newElement.style, inStartStyles);
    container.appendChild(newElement);
    void newElement.offsetHeight;
    // Animate
    switch (mode) {
        case TransitionMode.SIMULTANEOUS:
            Object.assign(oldElement.style, outStyles);
            Object.assign(newElement.style, inEndStyles);
            await new Promise(resolve => setTimeout(resolve, Math.max(outDuration, inDuration)));
            break;
        case TransitionMode.SEQUENTIAL:
            Object.assign(oldElement.style, outStyles);
            await new Promise(resolve => setTimeout(resolve, outDuration));
            if (isStale()) {
                if (newElement.parentNode === container)
                    newElement.remove();
                restorePosition();
                return;
            }
            Object.assign(newElement.style, inEndStyles);
            await new Promise(resolve => setTimeout(resolve, inDuration));
            break;
    }
    if (isStale()) {
        // A newer navigation took over while we were animating. Don't touch
        // oldElement (the newer transition owns it now) and remove OUR newElement
        // since the newer transition appended its own.
        if (newElement.parentNode === container)
            newElement.remove();
        restorePosition();
        return;
    }
    // Cleanup
    oldElement.remove();
    resetTransitionStyles(newElement, isSlotted);
    Object.keys({ ...inStartStyles, ...inEndStyles }).forEach(prop => {
        newElement.style[prop] = '';
    });
    restorePosition();
}

/**
 * Marks an ordinary class as a Snice daemon.
 *
 * The decorator never constructs or globally registers the class. Application
 * code owns every instance and exposes selected instances through an explicit
 * app context.
 */
function daemon(constructor, _context) {
    Object.defineProperty(constructor.prototype, IS_DAEMON_CLASS, {
        value: true,
        configurable: true,
    });
}
/** @internal Activates framework-managed communication for a provided daemon. */
function bindDaemon(instance) {
    if (!isDaemonInstance(instance)) {
        throw new TypeError('Application contexts may provide only instances of @daemon classes.');
    }
    const state = daemonState(instance);
    if (state.bindings === 0) {
        state.active = true;
        try {
            setupEventHandlers(instance, state.target);
            setupResponseHandlers(instance, state.target);
        }
        catch (error) {
            cleanupResponseHandlers(instance);
            cleanupEventHandlers(instance);
            state.active = false;
            throw error;
        }
    }
    state.bindings += 1;
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        state.bindings = Math.max(0, state.bindings - 1);
        if (state.bindings !== 0)
            return;
        cleanupResponseHandlers(instance);
        cleanupEventHandlers(instance);
        clearDispatchTimers(instance);
        state.active = false;
        // Consumers may still hold cleanup records for the old target until their
        // elements disconnect. Rotate the target so released contexts are inert
        // immediately and a later provision starts from a clean event surface.
        state.target = new EventTarget();
    };
}

function bindContextDaemons(context) {
    const daemonMap = context.daemons;
    if (daemonMap === undefined)
        return () => { };
    if (!daemonMap || typeof daemonMap !== 'object' || Array.isArray(daemonMap)) {
        throw new TypeError('AppContext.daemons must be a readonly record of named @daemon instances.');
    }
    const releases = [];
    const bound = new Set();
    try {
        for (const [name, instance] of Object.entries(daemonMap)) {
            if (!name.trim()) {
                throw new TypeError('AppContext.daemons keys must be non-empty strings.');
            }
            if (!instance || typeof instance !== 'object') {
                throw new TypeError(`AppContext.daemons["${name}"] must be an instance of an @daemon class.`);
            }
            if (bound.has(instance))
                continue;
            bound.add(instance);
            releases.push(bindDaemon(instance));
        }
    }
    catch (error) {
        for (const release of releases.reverse())
            release();
        throw error;
    }
    return () => {
        for (const release of releases.reverse())
            release();
    };
}
/**
 * Makes one explicit application context visible beneath a root.
 *
 * Daemon instances must already exist in context.daemons. The returned cleanup
 * removes the provider and deactivates framework communication for daemons no
 * longer provided by any context. An optional fetch function is exposed on the
 * same boundary through getContextFetch() without modifying the context object.
 */
function provideContextBoundary(root, context, options, navigation) {
    const releaseProvider = installContextProvider(root, context, options.fetch, navigation);
    let releaseDaemons;
    try {
        releaseDaemons = bindContextDaemons(context);
    }
    catch (error) {
        releaseProvider();
        throw error;
    }
    let released = false;
    return () => {
        if (released)
            return;
        released = true;
        releaseProvider();
        releaseDaemons?.();
    };
}
function provideContext(root, context, options = {}) {
    return provideContextBoundary(root, context, options);
}
/** @internal Router-owned provider including live navigation state. */
function provideRouterContext(root, navigation) {
    return provideContextBoundary(root, navigation.application, {
        fetch: navigation.fetch,
    }, navigation);
}

const EMPTY_QUERY_MARKER = '__snice_empty_query_value__';
const QUERY_PARAM_PATTERN = /=:[\w-]+\)?$/;
function containsDecodedMarker(url, marker) {
    if (url.includes(marker))
        return true;
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1)
        return false;
    return url.slice(queryIndex + 1).split('&').some(part => {
        try {
            return decodeURIComponent(part).includes(marker);
        }
        catch {
            return false;
        }
    });
}
function emptyQueryMarker(route, url) {
    let marker = EMPTY_QUERY_MARKER;
    while (route.spec.includes(marker) || containsDecodedMarker(url, marker)) {
        marker += '_';
    }
    return marker;
}
function fillEmptyQueryParams(route, url, marker) {
    const urlQueryIndex = url.indexOf('?');
    const routeQueryIndex = route.spec.indexOf('?');
    if (urlQueryIndex === -1 || routeQueryIndex === -1)
        return null;
    const urlParts = url.slice(urlQueryIndex + 1).split('&');
    const routeParts = route.spec.slice(routeQueryIndex + 1).split('&');
    let changed = false;
    const normalizedParts = urlParts.map((part, index) => {
        if (part.endsWith('=') && QUERY_PARAM_PATTERN.test(routeParts[index] ?? '')) {
            changed = true;
            return `${part}${marker}`;
        }
        return part;
    });
    if (!changed)
        return null;
    return `${url.slice(0, urlQueryIndex + 1)}${normalizedParts.join('&')}`;
}
/**
 * Match through pica-route while preserving legal empty query parameter values.
 * pica-route 1.1.2 rejects an empty param and therefore discards the whole route.
 * Keep this compatibility shim in sync with packages/react/src/route-match.ts.
 */
function matchRoute(route, url) {
    const directMatch = route.match(url);
    if (directMatch !== false)
        return directMatch;
    const marker = emptyQueryMarker(route, url);
    const normalizedUrl = fillEmptyQueryParams(route, url, marker);
    if (normalizedUrl === null)
        return false;
    const params = route.match(normalizedUrl);
    if (params === false)
        return false;
    for (const name of Object.keys(params)) {
        if (params[name] === marker)
            params[name] = '';
    }
    return params;
}

// Router historically allows a later Router instance to take over the same
// target. Track only Router-owned provider releases so that handoff remains
// explicit without weakening duplicate protection for public provideContext().
const routerContextProviders = new WeakMap();
/**
 * Possible outcomes from route resolution
 */
var RouteResult;
(function (RouteResult) {
    RouteResult["SUCCESS"] = "success";
    RouteResult["GUARDS_FAILED"] = "guards-failed";
    RouteResult["NOT_FOUND"] = "not-found";
})(RouteResult || (RouteResult = {}));
/**
 * Creates a new router instance.
 * @param {RouterOptions} options - The router configuration options.
 * @returns An object containing the router's API methods.
 */
function Router(options) {
    const win = options.window ?? window;
    const doc = options.document ?? document;
    const routes = [];
    let nextRegistrationOrder = 0;
    let is_sorted = false;
    let placards = []; // Store collected placards
    let _404; // the 404 page
    let _403; // the 403 forbidden page
    let home; // the home page
    let currentLayoutName = null; // Track current layout name
    let currentLayoutTimestamp = null; // Track current layout timestamp
    const context = options.context || {}; // Store context for guards
    let contextProviderRoot = null;
    let releaseContextProvider = null;
    // Create Context instance for managing router state
    const navigationContext = new Context(context, [], '', {}, options.fetcher);
    function ensureContextProvider(target) {
        if (contextProviderRoot === target
            && releaseContextProvider
            && routerContextProviders.get(target) === releaseContextProvider)
            return;
        releaseContextProvider?.();
        routerContextProviders.get(target)?.();
        const releaseProvidedContext = provideRouterContext(target, navigationContext);
        let released = false;
        const release = () => {
            if (released)
                return;
            released = true;
            releaseProvidedContext();
            if (routerContextProviders.get(target) === release) {
                routerContextProviders.delete(target);
            }
        };
        releaseContextProvider = release;
        contextProviderRoot = target;
        routerContextProviders.set(target, release);
    }
    function getCurrentLayoutElement(target) {
        const noCurrentLayout = !currentLayoutName || !currentLayoutTimestamp;
        if (noCurrentLayout) {
            return null;
        }
        const layoutElements = target.querySelectorAll(currentLayoutName);
        for (const element of layoutElements) {
            const hasTimestamp = element[CREATED_AT] === currentLayoutTimestamp;
            if (hasTimestamp) {
                return element;
            }
        }
        return null;
    }
    /**
     * Decorator function for defining a page with associated routes.
     * @param {PageOptions} pageOptions - The page configuration options.
     * @returns A decorator function to apply to a custom element class.
     */
    function page(pageOptions) {
        return function (constructor, context) {
            // Transfer metadata from context to constructor (for new decorators)
            if (context?.metadata && context.metadata[PROPERTIES]) {
                if (!constructor[PROPERTIES]) {
                    constructor[PROPERTIES] = new Map();
                }
                for (const [key, value] of context.metadata[PROPERTIES]) {
                    constructor[PROPERTIES].set(key, value);
                }
            }
            // Apply all element functionality (properties, queries, watchers, controllers, etc.)
            applyElementFunctionality(constructor);
            // Store transition config on constructor for later use
            constructor[PAGE_TRANSITION] = pageOptions.transition;
            // Extend the connectedCallback to add router-specific functionality
            const elementConnectedCallback = constructor.prototype.connectedCallback;
            constructor.prototype.connectedCallback = function () {
                // Store the Context instance for @context decorated methods to access.
                // Only Router-created elements (marked with ROUTER_CONTEXT during route
                // resolution, home/404/403 creation) receive the application context —
                // a bare document.createElement('my-page') in a unit test must stay
                // inert so the test's own injected context wins.
                if (this[ROUTER_CONTEXT] !== undefined) {
                    this[CONTEXT_HANDLER] = navigationContext;
                }
                // Call the element's connectedCallback first
                elementConnectedCallback?.call(this);
            };
            // Extend the disconnectedCallback to clean up router-specific stuff
            const elementDisconnectedCallback = constructor.prototype.disconnectedCallback;
            constructor.prototype.disconnectedCallback = function () {
                // Call element's disconnectedCallback first
                elementDisconnectedCallback?.call(this);
                // Clean up context references
                delete this[ROUTER_CONTEXT];
                delete this[CONTEXT_HANDLER];
            };
            // Define the custom element (skip if already registered)
            const existing = win.customElements.get(pageOptions.tag);
            if (!existing) {
                win.customElements.define(pageOptions.tag, constructor);
                registerRenderHostIdentity(constructor, pageOptions.tag);
            }
            else if (existing === constructor) {
                registerRenderHostIdentity(constructor, pageOptions.tag);
            }
            // Register the routes with guards, layout, and placard
            pageOptions.routes.forEach(route => {
                const path = typeof route === 'string' ? route : route.path;
                const order = typeof route === 'string' ? undefined : route.order;
                register(path, pageOptions.tag, pageOptions.transition, pageOptions.guards, pageOptions.layout, pageOptions.placard, order);
            });
            return constructor;
        };
    }
    /**
     * Registers a new route with the router.
     * @param {string} route - The route path.
     * @param {string} tag - The custom element tag associated with the route.
     * @param {number} order - Optional lower-first tie-break after specificity.
     * @example
     * register('/custom-route', 'custom-element');
     */
    function register(route, tag, transition, guards, layout, placard, order = 0) {
        if (!Number.isFinite(order)) {
            throw new TypeError(`Route order for "${route}" must be a finite number.`);
        }
        routes.push({
            route: new Route(route),
            tag,
            transition,
            guards,
            layout,
            placard,
            order,
            registrationOrder: nextRegistrationOrder++
        });
        is_sorted = false;
        switch (route) {
            case '/404':
                _404 = tag;
                break;
            case '/403':
                _403 = tag;
                break;
            case '/':
                home = tag;
                break;
        }
    }
    function setupEventListeners() {
        const handler = () => {
            const target = doc.querySelector(options.target);
            if (!target)
                return;
            // A later Router can explicitly take over the same live root. The old
            // history listener remains installed for backward compatibility, but it
            // must no longer navigate or replace the new owner's app context.
            if (contextProviderRoot === target
                && releaseContextProvider
                && routerContextProviders.get(target) !== releaseContextProvider)
                return;
            // Router has no public disposal API and historically leaves its history
            // listener installed. Tests and app shells may replace a target with a
            // new element using the same selector, then initialize a new Router. In
            // that case the old listener must yield to the provider already owning
            // the replacement root instead of stealing it on the next hashchange.
            if (contextProviderRoot !== target && hasContextProvider(target)) {
                releaseContextProvider?.();
                releaseContextProvider = null;
                contextProviderRoot = null;
                return;
            }
            renderPath(getPath());
        };
        switch (options.type) {
            case 'hash':
                win.addEventListener('hashchange', handler);
                break;
            case 'pushstate':
                win.addEventListener('popstate', handler);
                break;
        }
    }
    /**
     * Initializes the router and sets up navigation event listeners.
     * @example
     * initialize();
     */
    function initialize() {
        const target = doc.querySelector(options.target);
        if (!target) {
            throw new Error(`Target element not found: ${options.target}`);
        }
        ensureContextProvider(target);
        const needsSorting = !is_sorted;
        if (needsSorting) {
            routes.sort((a, b) => routeSpecificity(b.route.spec) - routeSpecificity(a.route.spec)
                || a.order - b.order
                || a.registrationOrder - b.registrationOrder);
            is_sorted = true;
        }
        // Collect placards from registered routes
        collectPlacards();
        setupEventListeners();
        const path = getPath();
        renderPath(path);
    }
    function collectPlacards() {
        const seen = new Set();
        placards = routes
            .filter(route => route.placard)
            .map(route => {
            const placard = route.placard;
            return typeof placard === 'function'
                ? placard(context)
                : placard;
        })
            .filter(p => {
            if (seen.has(p.name))
                return false;
            seen.add(p.name);
            return true;
        });
    }
    function emitContextUpdate(target, currentPath, routeParams) {
        // Update the navigation context and notify all registered elements
        navigationContext[CONTEXT_UPDATE](context, placards, currentPath, routeParams);
    }
    function updateLayout(layoutElement, currentPath, routeParams) {
        // Check if layout implements the update method
        if (typeof layoutElement.update === 'function') {
            layoutElement.update(context, placards, currentPath, routeParams);
        }
    }
    function getPath() {
        switch (options.type) {
            case 'hash':
                return win.location.hash.slice(1);
            case 'pushstate':
                return `${win.location.pathname}${win.location.search}`;
        }
    }
    function normalizePath(path) {
        const withoutHash = path.trim().replace(/^#/, '');
        if (!withoutHash)
            return '/';
        return withoutHash.startsWith('/') ? withoutHash : `/${withoutHash}`;
    }
    function recordNavigation(path) {
        switch (options.type) {
            case 'hash': {
                const hash = `#${path}`;
                if (win.location.hash !== hash)
                    win.history.pushState(null, '', hash);
                break;
            }
            case 'pushstate': {
                const current = `${win.location.pathname}${win.location.search}`;
                if (current !== path)
                    win.history.pushState(null, '', path);
                break;
            }
        }
    }
    function renderForbiddenPage(target) {
        let newPageElement;
        if (_403) {
            newPageElement = doc.createElement(_403);
            newPageElement[ROUTER_CONTEXT] = context;
        }
        else {
            const div = doc.createElement('div');
            div.className = 'default-403';
            div.innerHTML = /*html*/ `<h1>403</h1><p>Unauthorized</p>`;
            newPageElement = div;
        }
        target.innerHTML = '';
        target.appendChild(newPageElement);
        currentLayoutName = null;
        currentLayoutTimestamp = null;
    }
    function createLoadingSpinner() {
        const container = doc.createElement('div');
        container.setAttribute('data-snice-loading', '');
        container.innerHTML = /*html*/ `
      <style>@keyframes snice-spin{to{transform:rotate(360deg)}}</style>
      <div style="display:flex;align-items:center;justify-content:center;height:100%;min-height:200px">
        <div style="width:32px;height:32px;border:3px solid rgba(128,128,128,0.3);border-top-color:rgba(128,128,128,0.8);border-radius:50%;animation:snice-spin 0.6s linear infinite"></div>
      </div>`;
        return container;
    }
    async function checkGuards(guards, params, target, stale) {
        const hasGuards = !!guards;
        if (!hasGuards) {
            return true;
        }
        const guardsArray = Array.isArray(guards) ? guards : [guards];
        let spinner = null;
        let spinnerTimer = null;
        // Show spinner after a short delay (avoids flash for fast sync guards).
        // Skip if a newer navigation has already superseded this one.
        spinnerTimer = setTimeout(() => {
            if (stale && stale())
                return;
            spinner = createLoadingSpinner();
            target.appendChild(spinner);
        }, 50);
        try {
            for (const guard of guardsArray) {
                const allowed = await guard(context, params);
                if (!allowed) {
                    // Don't stomp the current page with a 403 if a newer navigation
                    // already took over while this guard was resolving.
                    if (!stale || !stale()) {
                        renderForbiddenPage(target);
                    }
                    return false;
                }
            }
            return true;
        }
        finally {
            if (spinnerTimer)
                clearTimeout(spinnerTimer);
            // @ts-ignore — TS narrows spinner to never because it can't track setTimeout mutations
            if (spinner)
                spinner.remove();
        }
    }
    function createHomeElement() {
        const newPageElement = doc.createElement(home);
        newPageElement[ROUTER_CONTEXT] = context;
        const constructor = win.customElements.get(home);
        const transition = constructor?.[PAGE_TRANSITION];
        const homeRoute = routes.find(r => r.route.match('/'));
        return { element: newPageElement, transition, layout: homeRoute?.layout };
    }
    function create404Element() {
        const has404Page = !!_404;
        if (has404Page) {
            const newPageElement = doc.createElement(_404);
            newPageElement[ROUTER_CONTEXT] = context;
            const constructor = win.customElements.get(_404);
            const transition = constructor?.[PAGE_TRANSITION];
            return { element: newPageElement, transition, layout: undefined };
        }
        const div = doc.createElement('div');
        div.className = 'default-404';
        div.innerHTML = /*html*/ `<h1>404</h1><p>Page not found</p>`;
        return { element: div, transition: undefined, layout: undefined };
    }
    async function resolveRoute(path, target, stale) {
        for (const route of routes) {
            const params = matchRoute(route.route, path);
            const isMatch = params !== false;
            if (!isMatch) {
                continue;
            }
            const guardsAllowed = await checkGuards(route.guards, params, target, stale);
            if (!guardsAllowed) {
                return { result: RouteResult.GUARDS_FAILED };
            }
            const newPageElement = doc.createElement(route.tag);
            newPageElement[ROUTER_CONTEXT] = context;
            newPageElement[CONTEXT_HANDLER] = navigationContext;
            const routeParams = params;
            Object.keys(routeParams).forEach(key => newPageElement.setAttribute(key, routeParams[key]));
            return { result: RouteResult.SUCCESS, element: newPageElement, transition: route.transition, layout: route.layout, routeParams };
        }
        return { result: RouteResult.NOT_FOUND };
    }
    function determineLayout(pageLayout) {
        const isExplicitlyNoLayout = pageLayout === false;
        if (isExplicitlyNoLayout) {
            return null;
        }
        const hasPageLayout = !!pageLayout;
        if (hasPageLayout) {
            return pageLayout;
        }
        const hasRouterLayout = !!options.layout;
        if (hasRouterLayout) {
            return options.layout;
        }
        return null;
    }
    function setupLayout(layoutToUse) {
        const needsNewLayout = layoutToUse !== currentLayoutName;
        if (!needsNewLayout) {
            return { element: null, needsNewLayout: false };
        }
        currentLayoutName = layoutToUse;
        const shouldCreateLayout = !!layoutToUse;
        if (shouldCreateLayout) {
            const timestamp = Date.now();
            currentLayoutTimestamp = timestamp;
            const layoutElement = doc.createElement(layoutToUse);
            layoutElement[ROUTER_CONTEXT] = context;
            layoutElement[CREATED_AT] = timestamp;
            return { element: layoutElement, needsNewLayout: true };
        }
        currentLayoutTimestamp = null;
        return { element: null, needsNewLayout: true };
    }
    async function renderWithLayout(target, pageElement, transition, layoutElement, needsNewLayout, currentPath, routeParams, stale) {
        const currentLayout = layoutElement || getCurrentLayoutElement(target);
        if (!currentLayout) {
            return;
        }
        // Update layout with current context and placards
        updateLayout(currentLayout, currentPath, routeParams);
        const oldPageInLayout = currentLayout.querySelector('[slot="page"]');
        const shouldTransition = !!(transition && oldPageInLayout);
        if (shouldTransition) {
            pageElement.setAttribute('slot', 'page');
            await performTransition$1(currentLayout, oldPageInLayout, pageElement, transition);
            if (stale && stale())
                return;
            if (needsNewLayout) {
                target.innerHTML = '';
                target.appendChild(currentLayout);
            }
            return;
        }
        // Guard before committing DOM changes.
        if (stale && stale())
            return;
        const existingPages = currentLayout.querySelectorAll('[slot="page"]');
        existingPages.forEach(page => page.remove());
        pageElement.setAttribute('slot', 'page');
        currentLayout.appendChild(pageElement);
        if (needsNewLayout) {
            target.innerHTML = '';
            target.appendChild(currentLayout);
        }
    }
    async function renderDirect(target, pageElement, transition, stale) {
        const currentElementInTarget = target.children[0];
        const shouldTransition = !!(transition && currentElementInTarget);
        if (shouldTransition) {
            await performTransition$1(target, currentElementInTarget, pageElement, transition);
            return;
        }
        // Guard against superseded navigation before committing DOM.
        if (stale && stale())
            return;
        target.innerHTML = '';
        target.appendChild(pageElement);
    }
    async function renderPage(target, pageElement, transition, layout, path, routeParams, stale) {
        const layoutToUse = determineLayout(layout);
        const { element: layoutElement, needsNewLayout } = setupLayout(layoutToUse);
        const finalTransition = transition || options.transition;
        const hasLayout = layoutElement !== null || getCurrentLayoutElement(target) !== null;
        if (hasLayout) {
            await renderWithLayout(target, pageElement, finalTransition, layoutElement, needsNewLayout, path, routeParams, stale);
            if (stale && stale())
                return;
            emitContextUpdate(target, path, routeParams);
            return;
        }
        await renderDirect(target, pageElement, finalTransition, stale);
        if (stale && stale())
            return;
        emitContextUpdate(target, path, routeParams);
    }
    /**
     * Navigates to the specified path.
     * @param {string} path - The path to navigate to.
     * @example
     * navigate('/login');
     */
    let navGeneration = 0;
    async function renderPath(path) {
        const target = doc.querySelector(options.target);
        if (!target) {
            throw new Error(`Target element not found: ${options.target}`);
        }
        ensureContextProvider(target);
        const myGen = ++navGeneration;
        const stale = () => myGen !== navGeneration;
        collectPlacards();
        win.scrollTo(0, 0);
        // Home path
        const isHomePath = (path?.trim() === '' || path === '/') && !!home;
        if (isHomePath) {
            const homeRoute = routes.find(r => r.route.match('/'));
            if (!(await checkGuards(homeRoute?.guards, {}, target, stale)))
                return;
            if (stale())
                return;
            const { element, transition, layout } = createHomeElement();
            await renderPage(target, element, transition, layout, path, {}, stale);
            return;
        }
        // No path
        if (!path)
            return;
        // Resolve route
        const routeResult = await resolveRoute(path, target, stale);
        if (stale())
            return;
        // Guards failed (403 already rendered by checkGuards)
        if (routeResult.result === RouteResult.GUARDS_FAILED)
            return;
        // Route matched
        if (routeResult.result === RouteResult.SUCCESS) {
            const { element, transition, layout, routeParams = {} } = routeResult;
            await renderPage(target, element, transition, layout, path, routeParams, stale);
            return;
        }
        // 404 fallthrough
        const { element, transition, layout } = create404Element();
        await renderPage(target, element, transition, layout, path, {}, stale);
    }
    async function navigate(path) {
        const normalizedPath = normalizePath(path);
        recordNavigation(normalizedPath);
        await renderPath(normalizedPath);
    }
    async function performTransition$1(container, oldElement, newElement, transition, atGen = navGeneration) {
        // `isStale` returns true when a newer navigate() has been kicked off.
        // The transition util checks this at each async boundary and bails so
        // we don't remove an element the newer transition now owns.
        const isStale = () => atGen !== navGeneration;
        return performTransition(container, oldElement, newElement, transition, isStale);
    }
    return {
        page,
        initialize,
        navigate,
        register
    };
}

/**
 * createRequestHandler — vanilla JS utility for handling @request channels
 * outside of the Snice element/controller decorator system.
 *
 * Listens for @request CustomEvents on a target element (or document),
 * performs the discovery/data handshake, and dispatches to your route map.
 *
 * @example
 * ```js
 * import { createRequestHandler } from 'snice';
 *
 * const cleanup = createRequestHandler(document, {
 *   'fetch-user': async (payload) => {
 *     const res = await fetch(`/api/users/${payload.id}`);
 *     return res.json();
 *   },
 *   'save-settings': async (payload) => {
 *     await fetch('/api/settings', { method: 'POST', body: JSON.stringify(payload) });
 *     return { ok: true };
 *   }
 * });
 *
 * // Later, to remove all listeners:
 * cleanup();
 * ```
 */
/**
 * Attach request handlers to a DOM target.
 *
 * @param target  The element or document to listen on. Events bubble, so an
 *                ancestor element catches requests from any descendant.
 * @param routes  Map of channel names to handler functions.
 * @param options Optional configuration.
 * @returns A cleanup function that removes all listeners.
 */
function createRequestHandler(target, routes, options) {
    const cleanups = [];
    for (const [channelName, handler] of Object.entries(routes)) {
        const eventName = `@request/${channelName}`;
        const listener = (event) => {
            const ce = event;
            const { discovery, data, payload } = ce.detail;
            if (!options?.passive) {
                ce.preventDefault();
                ce.stopImmediatePropagation();
                ce.stopPropagation();
            }
            // Signal that we found the handler — clears discovery timeout
            discovery.resolve();
            // Execute the handler and wire up the data promise
            Promise.resolve()
                .then(() => handler(payload))
                .then((result) => data.resolve(result))
                .catch((error) => {
                data.reject(error);
                console.error(`createRequestHandler: error in "${channelName}" handler:`, error);
            });
        };
        target.addEventListener(eventName, listener);
        cleanups.push(() => target.removeEventListener(eventName, listener));
    }
    return () => {
        for (const fn of cleanups)
            fn();
        cleanups.length = 0;
    };
}

/**
 * Template binding helpers for class and style attributes.
 *
 * @example
 * ```typescript
 * html`<div class="${classMap({ box: true, 'box--active': this.active })}"></div>`
 * html`<div style="${styleMap({ color: this.color, fontWeight: 'bold' })}"></div>`
 * ```
 */
/**
 * Build a class string from an object: keys with truthy values are included.
 */
function classMap(classes) {
    let out = '';
    for (const name of Object.keys(classes)) {
        if (classes[name])
            out += (out ? ' ' : '') + name;
    }
    return out;
}
/**
 * Build a style string from an object. camelCase keys convert to kebab-case;
 * CSS custom properties (`--token`) pass through unchanged. Entries whose
 * value is null/undefined/false are dropped.
 */
function styleMap(styles) {
    const decls = [];
    for (const key of Object.keys(styles)) {
        const value = styles[key];
        if (value === null || value === undefined || value === false)
            continue;
        const prop = key.startsWith('--')
            ? key
            : key.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());
        decls.push(`${prop}: ${value}`);
    }
    return decls.join('; ');
}

/**
 * Custom element readiness utilities
 * Handles waiting for custom elements to be defined with timeout warnings
 */
/**
 * Global flag to disable custom element readiness timeout warnings
 * Set this to true in environments where slow element registration is expected
 */
let DISABLE_ELEMENT_READY_WARNINGS = false;
/**
 * Set whether to disable custom element readiness timeout warnings
 */
function setDisableElementReadyWarnings(value) {
    DISABLE_ELEMENT_READY_WARNINGS = value;
}
/**
 * Default timeout for custom element registration warning (500ms)
 */
const DEFAULT_WARNING_TIMEOUT = 500;
/**
 * Wait for a custom element to be defined
 * Logs a warning if it takes longer than the warning timeout
 *
 * @param tagName - The custom element tag name
 * @param warningTimeout - Time in ms before warning (default 500ms)
 * @returns Promise that resolves when element is defined
 */
async function waitForElementDefined(tagName, warningTimeout = DEFAULT_WARNING_TIMEOUT) {
    // If already defined, return immediately
    if (customElements.get(tagName)) {
        return;
    }
    // Set up warning timer if not disabled
    let warningTimer = null;
    if (!DISABLE_ELEMENT_READY_WARNINGS) {
        warningTimer = setTimeout(() => {
            console.warn(`Custom element <${tagName}> is taking longer than ${warningTimeout}ms to register. ` +
                `This may indicate a missing import or circular dependency. ` +
                `Set DISABLE_ELEMENT_READY_WARNINGS=true to disable this warning.`);
        }, warningTimeout);
    }
    try {
        // Wait for element to be defined
        await customElements.whenDefined(tagName);
    }
    finally {
        // Clear warning timer
        if (warningTimer) {
            clearTimeout(warningTimer);
        }
    }
}
/**
 * Wait for a custom element to be defined and ready
 * First waits for the element to be registered, then waits for its ready promise
 *
 * @param element - The custom element instance
 * @param warningTimeout - Time in ms before warning about registration (default 500ms)
 * @returns Promise that resolves when element is defined and ready
 */
async function waitForElementReady(element, warningTimeout = DEFAULT_WARNING_TIMEOUT) {
    const tagName = element.tagName.toLowerCase();
    // Wait for element to be defined
    await waitForElementDefined(tagName, warningTimeout);
    // Wait for element's ready promise if it exists
    if ('ready' in element && typeof element.ready?.then === 'function') {
        await element.ready;
    }
}
/**
 * Process all custom elements in a node tree and wait for them to be ready
 * This is useful after inserting a template with custom elements
 *
 * @param node - The root node to scan for custom elements
 * @param warningTimeout - Time in ms before warning about registration (default 500ms)
 * @returns Promise that resolves when all custom elements are ready
 */
async function waitForAllCustomElements(node, warningTimeout = DEFAULT_WARNING_TIMEOUT) {
    const customElements = [];
    // Find all custom elements (tag names with hyphens)
    if (node instanceof Element) {
        if (node.tagName.includes('-')) {
            customElements.push(node);
        }
        // Also check in shadow DOM
        if (node.shadowRoot) {
            const shadowCustomElements = node.shadowRoot.querySelectorAll('*');
            shadowCustomElements.forEach(el => {
                if (el.tagName.includes('-')) {
                    customElements.push(el);
                }
            });
        }
    }
    // If it's a DocumentFragment, check all children
    if (node instanceof DocumentFragment) {
        const elements = node.querySelectorAll('*');
        elements.forEach(el => {
            if (el.tagName.includes('-')) {
                customElements.push(el);
            }
        });
    }
    // Wait for all custom elements to be ready
    await Promise.all(customElements.map(el => waitForElementReady(el, warningTimeout)));
}

/**
 * Render debugging utilities for Snice v3.0.0
 * For testing and debugging only - not recommended for production use
 */
/**
 * Track renders of an element using an async generator
 * Each call to tracker.next() waits for the next render to complete
 *
 * @example
 * ```typescript
 * const tracker = trackRenders(element);
 *
 * element.someProp = 'new value';
 * await tracker.next(); // Waits for render
 * // DOM is now updated
 *
 * element.someProp = 'another value';
 * await tracker.next(); // Waits for next render
 * // DOM is updated again
 * ```
 *
 * WARNING: For testing/debugging only!
 * - Do not use in production code
 * - The generator yields indefinitely - use it only in controlled test environments
 * - Each yield waits for the next render event
 */
async function* trackRenders(element) {
    while (true) {
        await new Promise(resolve => {
            if (!element[RENDER_CALLBACKS]) {
                element[RENDER_CALLBACKS] = [];
            }
            element[RENDER_CALLBACKS].push(resolve);
        });
        yield;
    }
}

/**
 * Shared body scroll-lock with refcounting.
 *
 * Multiple overlay components (drawer, modal, etc.) can each lock body
 * scroll independently. Without coordination, closing one overlay would
 * unlock scroll while another is still open. This utility increments
 * a module-scope counter on lock, decrements on unlock, and only mutates
 * `document.body.style.overflow` on the 0 ↔ 1 transitions. The original
 * overflow value is captured at the first lock and restored at the last
 * unlock — important because some pages set `overflow: scroll` for layout
 * and a naive `= ''` would clobber it.
 */
let count = 0;
let savedOverflow = null;
// Test hook — `afterEach` in test suites can call `.clear()` to reset the
// counter when document.body.innerHTML is wiped. The counter is module-scope,
// so without this it survives across tests and the second test's first lock
// would only increment instead of flipping `overflow: hidden`.
const TEST_HOOK = Symbol.for('snice:body-scroll-lock');
globalThis[TEST_HOOK] = {
    clear() {
        count = 0;
        savedOverflow = null;
    },
};
function lockBodyScroll() {
    if (count === 0) {
        savedOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    }
    count++;
}
function unlockBodyScroll() {
    if (count === 0)
        return;
    count--;
    if (count === 0) {
        document.body.style.overflow = savedOverflow ?? '';
        savedOverflow = null;
    }
}
/**
 * Test/debug only — peek at the current refcount.
 */
function getBodyScrollLockCount() {
    return count;
}

/**
 * Context-aware fetch implementation with middleware support
 *
 * @example
 * ```typescript
 * const fetcher = new ContextAwareFetcher();
 *
 * fetcher.use('request', function(request, next) {
 *   const token = this.application.user?.token;
 *   if (token) {
 *     request.headers.set('Authorization', `Bearer ${token}`);
 *   }
 *   return next();
 * });
 *
 * fetcher.use('response', async function(response, next) {
 *   if (!response.ok) {
 *     throw new Error(`HTTP ${response.status}`);
 *   }
 *   return next();
 * });
 *
 * const router = Router({
 *   target: '#app',
 *   context: { user: null },
 *   fetcher
 * });
 * ```
 */
class ContextAwareFetcher {
    constructor() {
        this.requestMiddlewares = [];
        this.responseMiddlewares = [];
    }
    use(type, middleware) {
        if (type === 'request') {
            this.requestMiddlewares.push(middleware);
            return;
        }
        this.responseMiddlewares.push(middleware);
    }
    /**
     * Create a fetch function bound to the given Context instance
     * @param ctx - The Context instance to bind to
     * @returns A fetch function with middleware applied
     */
    create(ctx) {
        const requestMiddlewares = this.requestMiddlewares;
        const responseMiddlewares = this.responseMiddlewares;
        const fetchFn = async function (input, init) {
            // Relative URLs are the normal case for a browser app, but constructing
            // a Request from one throws when there is no document base (Node/undici
            // test runtimes). Resolve against the location base when one exists.
            if (typeof input === 'string' && typeof location !== 'undefined' && location.href) {
                try {
                    new URL(input);
                }
                catch {
                    input = new URL(input, location.href).href;
                }
            }
            const request = new Request(input, init);
            // Request middleware chain
            let reqIndex = 0;
            const nextRequest = async () => {
                if (reqIndex < requestMiddlewares.length) {
                    const middleware = requestMiddlewares[reqIndex++];
                    return middleware.call(this, request, nextRequest);
                }
                // Execute actual fetch
                const response = await fetch(request);
                // Response middleware chain
                let resIndex = 0;
                const nextResponse = async () => {
                    if (resIndex < responseMiddlewares.length) {
                        const middleware = responseMiddlewares[resIndex++];
                        return middleware.call(this, response, nextResponse);
                    }
                    return response;
                };
                return nextResponse();
            };
            return nextRequest();
        };
        return fetchFn.bind(ctx);
    }
}

// v3.0.0 exports
// Auto-enable native element controllers in browser environments
if (typeof document !== 'undefined') {
    useNativeElementControllers();
}
// NOTE: @on decorator works in both elements AND controllers.
// Supports event delegation, keyboard modifiers, debounce/throttle - see docs/events.md
// Template event syntax (@click=${handler}) is also available as an alternative.

export { Context, ContextAwareFetcher, IS_CONTROLLER_INSTANCE, Router, SimpleArray, SniceElement, TransitionMode, adopted, applyElementFunctionality, attachController, classMap, clearDebounceTimers, clearMemoizeCache, clearThrottleTimers, context$1 as context, context as contextProperty, controller, createRequestHandler, css, daemon, debounce, detachController, dispatch, dispose, element, escapeAttr, escapeHtml, getBodyScrollLockCount, getContext, getContextFetch, getController, getSymbol, html, isSafeUrl, layout, live, lockBodyScroll, memoize, moved, noChange, nothing, observe, on, once, parseDuration, property, provideContext, query, queryAll, ready, reconnect, render, repeat, request, resetOnce, respond, setDisableElementReadyWarnings, setStrictRenderErrors, state, styleMap, styles, svg, throttle, trackRenders, unlockBodyScroll, unsafeHTML, useNativeElementControllers, waitForAllCustomElements, waitForElementDefined, waitForElementReady, watch };
//# sourceMappingURL=index.esm.js.map
